import React, { useRef, useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { API_WEB_URLS } from "../../constants/constAPI";
import ExcelJS from "exceljs";
import useSession from "../utils/useSession";

// Native browser download helper (no file-saver dependency needed)
const saveAs = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  setTimeout(() => {
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, 300);
};

// All import-able flat fields (for template + import validation)
const IMPORT_FLAT_FIELDS = [
  { key: "EmployeeCode", label: "Employee Code" },
  { key: "Name", label: "Employee Name", required: true },
  { key: "FatherName", label: "Father Name" },
  { key: "SerialNo", label: "Serial No" },
  { key: "Sex", label: "Gender (M/F)" },
  { key: "DateOfBirth", label: "Date of Birth (DD/MM/YYYY)" },
  { key: "DateOfRetire", label: "Date of Retirement (DD/MM/YYYY)" },
  { key: "EmployeeType", label: "Employee Type" },
  { key: "Status", label: "Working Status" },
  { key: "Married", label: "Marital Status" },
  { key: "EducationQualification", label: "Education Qualification" },
  { key: "WeeklyHoliday", label: "Weekly Holiday" },
  { key: "MobileNo", label: "Mobile No" },
  { key: "Email", label: "Email" },
  { key: "PermanentAddress", label: "Permanent Address" },
  { key: "PresentAddress", label: "Present Address" },
  { key: "Remark", label: "Remark" },

  // Department & Shift 
  { key: "BranchName", label: "Branch Name" },
  { key: "DepartmentName", label: "Department Name" },
  { key: "DesignationName", label: "Designation Name" },
  { key: "Dept_EffectiveDate", label: "Dept Effective Date (DD/MM/YYYY)" },
  { key: "ShiftName", label: "Shift Name" },
  { key: "Shift_EffectiveFrom", label: "Shift Effective From (DD/MM/YYYY)" },
  // Join & Salary
  { key: "JoinDate", label: "Join Date (DD/MM/YYYY)" },
  { key: "JoinTypeName", label: "Join Type Name" },
  { key: "LeftDate", label: "Date of Leaving (DD/MM/YYYY)" },
  { key: "ReasonOfLeft", label: "Reason to Left" },
  { key: "Sal_BasicSalary", label: "Basic Salary" },
  { key: "Sal_GradePay", label: "Grade Pay" },
  { key: "Sal_BasicTypeName", label: "Basic Type (M/D/H/P)" },
  { key: "Sal_EffectiveDate", label: "Salary Effective Date (DD/MM/YYYY)" },
  // ESI
  { key: "ESIApplicable", label: "ESI Applicable (YES/NO)" },
  { key: "ESINo", label: "ESI Number" },
  { key: "ESIJoiningDate", label: "ESI Joining Date (DD/MM/YYYY)" },
  { key: "ESIOldEmployee", label: "ESI Old Employee (YES/NO)" },
  { key: "TICs", label: "TICs (YES/NO)" },
  { key: "PICs", label: "PICs (YES/NO)" },
  { key: "Accident", label: "Accident (YES/NO)" },
  // PF
  { key: "PFApplicable", label: "PF Applicable (YES/NO)" },
  { key: "PFNo", label: "PF Number" },
  { key: "UAN", label: "UAN" },
  { key: "PFJoinDate", label: "PF Join Date (DD/MM/YYYY)" },
  { key: "PensionApplicable", label: "Pension Applicable (YES/NO)" },
  { key: "ExtraPF", label: "Extra PF" },
  { key: "PFCalculationType", label: "PF Calculation Type (P/F/M)" },
  { key: "EmplrPFOverLimit", label: "Emplr PF Over Limit (YES/NO)" },
  { key: "EmpleeDedOverLimit", label: "Emplee Ded Over Limit (YES/NO)" },
  { key: "PensionOverLimit", label: "Pension Over Limit (YES/NO)" },
  { key: "HigherPension", label: "Higher Pension (YES/NO)" },
  { key: "FixedPFSalary", label: "Fixed PF Salary" },
  { key: "OldMemberPF", label: "Old Member PF (YES/NO)" },
  { key: "OldMemberPension", label: "Old Member Pension (YES/NO)" },
  { key: "PrevPFNo", label: "Previous PF No" },
  { key: "OldUAN", label: "Old UAN" },
  { key: "DateOfExitPr", label: "Date Of Exit Prev (DD/MM/YYYY)" },
  { key: "PMRPY", label: "PMRPY (YES/NO)" },
  { key: "PMGKY", label: "PMGKY (YES/NO)" },
  { key: "ABRY", label: "ABRY (YES/NO)" },
  { key: "IsDeferredPension", label: "Is Deferred Pension (YES/NO)" },
  { key: "PensionAge", label: "Pension Age" },
  // Bank & KYC
  { key: "BankName", label: "Bank Name" },
  { key: "BankBranch", label: "Bank Branch" },
  { key: "IFSCCode", label: "IFSC Code" },
  { key: "BankAccountNo", label: "Bank Account No" },
  { key: "NameAsBank", label: "Name As Per Bank" },
  { key: "BankPayment", label: "Bank Payment Mode (YES/NO)" },
  { key: "PanCardNo", label: "PAN Card No" },
  { key: "NameAsPerPan", label: "Name As Per PAN" },
  { key: "AadharNumber", label: "Aadhar Number" },
  { key: "NameInAadhar", label: "Name In Aadhar" },
  { key: "DrivingLicenceNo", label: "Driving Licence No" },
  { key: "DLExpiryDate", label: "DL Expiry Date (DD/MM/YYYY)" },
  { key: "NameAsPerDL", label: "Name As Per DL" },
  { key: "PassportNo", label: "Passport No" },
  { key: "PassportFrom", label: "Passport Valid From (DD/MM/YYYY)" },
  { key: "PassportTill", label: "Passport Valid Till (DD/MM/YYYY)" },
  { key: "PassportName", label: "Name As Per Passport" },
  { key: "IsInternationalEmployee", label: "Is International Employee (YES/NO)" },
  { key: "CountryOrigin", label: "Country Origin" },
  { key: "Nationality", label: "Nationality" },
  { key: "CountryName", label: "Country Name" },
  { key: "VoterIDNo", label: "Voter ID No" },
  { key: "RationCardNo", label: "Ration Card No" },
  { key: "IsHandicap", label: "Is Handicap (YES/NO)" },
  { key: "HandicapCategory", label: "Handicap Category" },
  { key: "RefundFileDate", label: "Refund File Date (DD/MM/YYYY)" },
  { key: "Refunded", label: "Refunded (YES/NO)" },
  { key: "GratuityPaidDate", label: "Gratuity Paid Date (DD/MM/YYYY)" },
];

// Reference guidelines & format definitions for the helper modal
const FORMAT_GUIDELINES = [
  {
    category: "🔑 Core Identification & Matching",
    fields: [
      {
        field: "Employee Code (EmployeeCode)",
        format: "Text / Number",
        allowed: "Any unique code (e.g. 0001, EMP-101, SN01)",
        rule: "PRIMARY MATCHING KEY. If found in active firm → UPDATES existing employee. If blank or not found → INSERTS new employee with auto-generated Serial No.",
        example: "0001 or EMP-101",
      },
      {
        field: "Employee Name (Name)",
        format: "Text (Required)",
        allowed: "Alphabetical Full Name",
        rule: "Mandatory for all employees. Rows without an Employee Name will be skipped with a validation error.",
        example: "Rajesh Sharma",
      },
      {
        field: "Employee Type (EmployeeType)",
        format: "Text / Dropdown",
        allowed: "Ordinary, Permanent, CONTRACTUAL (and active types from Employee Type Master)",
        rule: "Employment category for the employee. Options dynamically generated from Employee Type Master. If left blank, automatically defaults to 'Ordinary'. Case-insensitive.",
        example: "Ordinary or Permanent",
      },
      {
        field: "Gender / Sex (Sex)",
        format: "Standard Code",
        allowed: "M, F, O (or Male, Female, Other)",
        rule: "Standard code M = Male, F = Female, O = Other.",
        example: "M",
      },
      {
        field: "Serial No (SerialNo)",
        format: "Numeric",
        allowed: "1, 2, 3...",
        rule: "Sequential register number. If left blank during insert, the system automatically assigns the next highest number for this firm.",
        example: "15",
      },
      {
        field: "Status",
        format: "Text",
        allowed: "Active, Inactive, Left, Suspended",
        rule: "Current operational status of the employee in the company.",
        example: "Active",
      },
    ],
  },
  {
    category: "⏰ Shift Master & Shift History",
    fields: [
      {
        field: "Shift Name (ShiftName / Shift / Latest Shift)",
        format: "Dropdown / Text",
        allowed: "Shift Name configured in Firm Shift Master",
        rule: "Specifies employee work shift from Firm Shift Master. Automatically maps to firm shift settings, updates Employee Master, and adds a record in Shift History (EmployeeShiftL). If left blank for new employees, automatically assigns the firm's default active shift.",
        example: "Firm Shift Name",
      },
      {
        field: "Shift Effective From (Shift_EffectiveFrom / Shift Effective Date)",
        format: "DD/MM/YYYY",
        allowed: "DD/MM/YYYY (e.g. 01/04/2024)",
        rule: "Effective date from which the shift assignment becomes active. For new employees, if left blank, it automatically defaults to Date of Joining (DOJ).",
        example: "01/04/2024",
      },
    ],
  },
  {
    category: "📅 Join & Left History (Including Reason to Left)",
    fields: [
      {
        field: "Date of Joining (JoinDate / DOJ)",
        format: "DD/MM/YYYY",
        allowed: "DD/MM/YYYY (e.g. 25/01/2024)",
        rule: "Date the employee joined the company. For new employees, this date serves as the master default for all effective dates (Department, Shift, Salary, PF/ESI).",
        example: "2023-01-15",
      },
      {
        field: "Join Type (JoinTypeName)",
        format: "Text / Selection",
        allowed: "Join, Rejoin, Transfer, Promotion (from JoinTypeMaster). Auto-defaults to 'Join'.",
        rule: "Categorization of how the employee joined the organization. Defaults automatically to 'Join' if left blank.",
        example: "Join",
      },
      {
        field: "Date of Leaving (LeftDate / DOL / Exit Date)",
        format: "DD/MM/YYYY",
        allowed: "DD/MM/YYYY (e.g. 25/01/2024)",
        rule: "Date the employee left or separated from the company. Required when recording resignation or cessation.",
        example: "2026-03-31",
      },
      {
        field: "Reason to Left (ReasonOfLeft / Reason to Left Master)",
        format: "Text / Selection",
        allowed: "Resignation, Retirement, Termination, Better Prospects, Health / Medical Issues, Personal / Family Reasons, Contract Expired, Absenteeism / Desertion, Death, Permanent Incapacity, Own Accord",
        rule: "Official reason for employee leaving, linked to the Reason To Left Master. Required for PF / ESI exit compliance filings (Form 10/19).",
        example: "Resignation or Better Prospects",
      },
    ],
  },
  {
    category: "🏢 Department & Designation",
    fields: [
      {
        field: "Department Name (DepartmentName)",
        format: "Text / ID",
        allowed: "Department Name or ID",
        rule: "Resolves to the firm's Department Master. Creates/updates Department History.",
        example: "Production",
      },
      {
        field: "Designation Name (DesignationName)",
        format: "Text / ID",
        allowed: "Designation Name or ID",
        rule: "Resolves to the firm's Designation Master. Creates/updates Department History.",
        example: "Supervisor",
      },
      {
        field: "Department Effective Date (Dept_EffectiveDate)",
        format: "DD/MM/YYYY",
        allowed: "DD/MM/YYYY (e.g. 25/01/2024)",
        rule: "Effective date of department assignment. Defaults to Date of Joining for new employees.",
        example: "2025-04-01",
      },
      {
        field: "Branch Name (BranchName)",
        format: "Text / ID",
        allowed: "Branch Name or ID",
        rule: "Allocates the employee to specific firm branch location.",
        example: "Main Branch",
      },
    ],
  },
  {
    category: "💰 Salary & Wage Calculation",
    fields: [
      {
        field: "Basic Salary (Sal_BasicSalary)",
        format: "Numeric (Decimal)",
        allowed: "Positive number (e.g. 18000.00)",
        rule: "Core base salary rate of the employee before allowances and deductions.",
        example: "21500.00",
      },
      {
        field: "Basic Calculation Type (Sal_BasicTypeName)",
        format: "Code / Selection",
        allowed: "M, D, H, P (or Monthly, Daily, Hourly, PieceRate)",
        rule: "Specifies base salary calculation type: M = Monthly, D = Daily / Per Day, H = Hourly, P = Piece Rate. Defaults to M.",
        example: "M",
      },
      {
        field: "Salary Effective Date (Sal_EffectiveDate)",
        format: "DD/MM/YYYY",
        allowed: "DD/MM/YYYY (e.g. 25/01/2024)",
        rule: "Date from which this salary structure becomes effective. For new employees, defaults to Join Date.",
        example: "2025-04-01",
      },
      {
        field: "Grade Pay (Sal_GradePay)",
        format: "Numeric",
        allowed: "Decimal amount",
        rule: "Additional grade pay component if applicable under firm wage agreement.",
        example: "2400.00",
      },
    ],
  },
  {
    category: "🏦 PF (Provident Fund) Compliance",
    fields: [
      {
        field: "PF Applicable (PFApplicable)",
        format: "YES / NO",
        allowed: "YES, NO",
        rule: "Determines if EPF/EPS deductions apply. If NO, PF fields are ignored.",
        example: "YES",
      },
      {
        field: "PF Number (PFNo)",
        format: "Alphanumeric",
        allowed: "Regional PF Code / Member ID",
        rule: "EPFO establishment member ID (e.g. DL/CPM/0000000/000/0000001).",
        example: "MH/PUN/0012345/000/0000123",
      },
      {
        field: "Universal Account Number (UAN)",
        format: "12 Digits",
        allowed: "12-digit numeric UAN",
        rule: "Mandatory for PF compliance. Must be valid 12-digit EPFO allotted number.",
        example: "100123456789",
      },
      {
        field: "PF Joining Date (PFJoinDate)",
        format: "DD/MM/YYYY",
        allowed: "DD/MM/YYYY (e.g. 25/01/2024)",
        rule: "Date of admission into EPF scheme. Only set when PF Applicable is YES; defaults to Join Date.",
        example: "2023-01-15",
      },
      {
        field: "Pension Applicable (PensionApplicable)",
        format: "YES / NO",
        allowed: "YES, NO",
        rule: "Whether employee is eligible for Employee Pension Scheme (EPS 1995). Defaults to YES for PF members.",
        example: "YES",
      },
      {
        field: "PF Calculation Type (PFCalculationType)",
        format: "Code (P/F/M)",
        allowed: "P (Percentage / 12%), F (Fixed), M (Manual)",
        rule: "Formula for PF contribution. P calculates standard 12% statutory deduction.",
        example: "P",
      },
      {
        field: "Extra PF / VPF (ExtraPF)",
        format: "Numeric",
        allowed: "Amount or %",
        rule: "Voluntary Provident Fund (VPF) additional contribution percentage or fixed amount.",
        example: "0",
      },
      {
        field: "PF Over Limit Flags (EmplrPFOverLimit / EmpleeDedOverLimit / PensionOverLimit / HigherPension)",
        format: "YES / NO",
        allowed: "YES, NO",
        rule: "Controls contribution on actual wage exceeding the statutory ceiling of ₹15,000.",
        example: "NO",
      },
      {
        field: "Fixed PF Salary (FixedPFSalary)",
        format: "Numeric",
        allowed: "Capped salary amount",
        rule: "Fixed wage ceiling limit for calculating PF if restricted by agreement.",
        example: "15000.00",
      },
      {
        field: "Prior Member & Scheme Flags (OldMemberPF / OldMemberPension / PMRPY / PMGKY / ABRY)",
        format: "YES / NO",
        allowed: "YES, NO",
        rule: "Government benefit schemes and prior PF/EPS membership declarations under Form 11.",
        example: "NO",
      },
    ],
  },
  {
    category: "🏥 ESI (Employee State Insurance) Compliance",
    fields: [
      {
        field: "ESI Applicable (ESIApplicable)",
        format: "YES / NO",
        allowed: "YES, NO",
        rule: "Specifies if employee is covered under ESI Act (wages <= ₹21,000 / month). If NO, ESI fields are ignored.",
        example: "YES",
      },
      {
        field: "ESI Insurance Number (ESINo)",
        format: "17 Digits",
        allowed: "17-digit numeric ESIC insurance IP number",
        rule: "Permanent Insurance Number issued by ESIC.",
        example: "31123456780010001",
      },
      {
        field: "ESI Joining Date (ESIJoiningDate)",
        format: "DD/MM/YYYY",
        allowed: "DD/MM/YYYY (e.g. 25/01/2024)",
        rule: "Date registered under ESI scheme. Defaults to Join Date if ESI Applicable is YES.",
        example: "2023-01-15",
      },
      {
        field: "ESI Identity & Status (ESIOldEmployee / TICs / PICs / Accident)",
        format: "YES / NO",
        allowed: "YES, NO",
        rule: "Identity card issuance status (Temporary / Permanent Identity Card) and prior registration flag.",
        example: "YES",
      },
    ],
  },
  {
    category: "💳 Bank & Payment Details",
    fields: [
      {
        field: "Bank Name (BankName)",
        format: "Text",
        allowed: "Full Name of Commercial / Nationalized Bank",
        rule: "Name of the financial institution where employee holds salary account.",
        example: "State Bank of India or HDFC Bank",
      },
      {
        field: "Bank Branch (BankBranch)",
        format: "Text",
        allowed: "Branch name or city",
        rule: "Bank branch location for salary routing.",
        example: "Main Branch, Jaipur",
      },
      {
        field: "IFSC Code (IFSCCode)",
        format: "11 Alphanumeric",
        allowed: "Standard 11-digit IFSC (e.g. SBIN0001234)",
        rule: "Required for electronic NEFT / RTGS / NACH salary transfers.",
        example: "SBIN0001234",
      },
      {
        field: "Bank Account No (BankAccountNo)",
        format: "Numeric / Text",
        allowed: "9 to 18 digits account number",
        rule: "Employee's primary savings / salary account number.",
        example: "123456789012",
      },
      {
        field: "Name As Per Bank (NameAsBank)",
        format: "Text",
        allowed: "Exact name printed on passbook / cheque",
        rule: "Must match bank account records to prevent salary transfer rejections.",
        example: "Rajesh Sharma",
      },
      {
        field: "Bank Payment Mode (BankPayment)",
        format: "YES / NO",
        allowed: "YES, NO",
        rule: "YES disburses salary via Bank Transfer; NO disburses salary via Cash / Cheque.",
        example: "YES",
      },
      {
        field: "Weekly Holiday (WeeklyHoliday)",
        format: "Day Name",
        allowed: "Sunday, Monday, Tuesday, Wednesday, Thursday, Friday, Saturday",
        rule: "Scheduled weekly day off for attendance and overtime calculations.",
        example: "Sunday",
      },
    ],
  },
  {
    category: "🪪 Identity Documents & Personal KYC",
    fields: [
      {
        field: "PAN Card No (PanCardNo) & Name",
        format: "10 Characters",
        allowed: "Standard PAN format (e.g. ABCDE1234F) & NameAsPerPan",
        rule: "Mandatory for Income Tax, Form 16, and non-higher TDS rate compliance.",
        example: "ABCDE1234F",
      },
      {
        field: "Aadhaar Number (AadharNumber) & Name",
        format: "12 Digits",
        allowed: "12-digit Aadhaar & NameInAadhar",
        rule: "Required for EPFO UAN Aadhaar linking and KYC verification.",
        example: "1234 5678 9012",
      },
      {
        field: "Date of Birth (DateOfBirth / DOB)",
        format: "DD/MM/YYYY",
        allowed: "DD/MM/YYYY (e.g. 25/01/2024)",
        rule: "Used for age verification, pension eligibility (58 yrs), and retirement calculation.",
        example: "1994-06-15",
      },
      {
        field: "Date of Retirement (DateOfRetire)",
        format: "DD/MM/YYYY",
        allowed: "DD/MM/YYYY (e.g. 25/01/2024)",
        rule: "Projected date of superannuation / retirement based on firm parameter age.",
        example: "2052-06-30",
      },
      {
        field: "Father Name (FatherName)",
        format: "Text",
        allowed: "Father's Full Name",
        rule: "Required for statutory PF, ESI, and Gratuity nomination records.",
        example: "Ramesh Sharma",
      },
      {
        field: "Marital Status (Married / MaritalStatus)",
        format: "Text",
        allowed: "Married, Unmarried, Single, Divorced, Widow",
        rule: "Personal family status for nominee allocations.",
        example: "Married",
      },
      {
        field: "Education Qualification (EducationQualification)",
        format: "Text",
        allowed: "10th, 12th, Graduate, Post Graduate, Diploma, ITI, B.Tech, etc.",
        rule: "Highest educational degree or certification.",
        example: "Graduate",
      },
      {
        field: "Contact & Address (MobileNo / Email / PresentAddress / PermanentAddress)",
        format: "Text",
        allowed: "Valid 10-digit mobile, email, and full postal addresses",
        rule: "Communication details for SMS slip notifications and statutory compliance.",
        example: "9876543210, user@example.com",
      },
      {
        field: "Other Documents (DrivingLicenceNo / PassportNo / VoterIDNo / RationCardNo)",
        format: "Text & Dates",
        allowed: "Respective document numbers and validity expiry dates",
        rule: "Supplemental KYC verification proof documents.",
        example: "DL-123456789, Z1234567",
      },
      {
        field: "Handicap & Category (IsHandicap / HandicapCategory)",
        format: "YES / NO & Category",
        allowed: "YES, NO (Locomotor, Visual, Hearing, etc.)",
        rule: "Declaration for statutory professional tax exemptions and special PF provisions.",
        example: "NO",
      },
    ],
  },
  {
    category: "🎁 Dynamic Allowances & Deductions",
    fields: [
      {
        field: "Allow_<Id>_Amount / Ded_<Id>_Amount",
        format: "Numeric",
        allowed: "Decimal amount (e.g. 500.00)",
        rule: "Specified amount or percentage rate for the firm's dynamic allowance/deduction.",
        example: "1200.00",
      },
      {
        field: "Allow_<Id>_Calculation / Ded_<Id>_Calculation",
        format: "Code (M/P/D/F)",
        allowed: "M = Monthly/Manual, P = Percentage, D = Daily, F = Fixed",
        rule: "Defines how the dynamic allowance/deduction will be computed during payroll.",
        example: "M",
      },
      {
        field: "Allow_<Id>_IsApplicable, IsOnGross, IsPFApply, IsESIApply...",
        format: "YES / NO",
        allowed: "YES or NO",
        rule: "Controls if the allowance is active, included in Gross salary, subject to PF/ESI deduction, or linked to working days.",
        example: "YES",
      },
    ],
  },
];

// Helper: format boolean for display / export
const fmtBool = (v) => {
  if (v === true || v === 1 || v === "1" || String(v).toUpperCase() === "TRUE" || String(v).toUpperCase() === "YES" || String(v).toUpperCase() === "Y") {
    return "YES";
  }
  if (v === false || v === 0 || v === "0" || String(v).toUpperCase() === "FALSE" || String(v).toUpperCase() === "NO" || String(v).toUpperCase() === "N") {
    return "NO";
  }
  return v ?? "";
};

// Helper: parse bool from Excel / input
const parseBool = (v) => {
  if (v === true || v === 1 || v === "1") return true;
  const s = String(v).trim().toUpperCase();
  if (s === "TRUE" || s === "YES" || s === "Y") return true;
  return false;
};

// Helper: safe date string for UI display (DD/MM/YYYY)
const safeDate = (v) => {
  if (!v) return "";
  if (typeof v === "number" && v > 20000 && v < 80000) {
    const d = new Date(Math.round((v - 25569) * 86400 * 1000));
    if (!isNaN(d.getTime())) v = d;
  }
  if (v instanceof Date) {
    const yyyy = v.getFullYear();
    const mm = String(v.getMonth() + 1).padStart(2, "0");
    const dd = String(v.getDate()).padStart(2, "0");
    return `${dd}/${mm}/${yyyy}`;
  }
  const s = String(v).trim();
  if (!s || s === "null" || s === "undefined") return "";
  // If already DD/MM/YYYY or DD-MM-YYYY
  if (/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})/.test(s)) {
    const match = s.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})/);
    return `${match[1].padStart(2, "0")}/${match[2].padStart(2, "0")}/${match[3]}`;
  }
  // If ISO YYYY-MM-DD or YYYY-MM-DDTHH:mm:ss
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) {
    const parts = s.substring(0, 10).split("-");
    return `${parts[2]}/${parts[1]}/${parts[0]}`;
  }
  return s.substring(0, 10);
};

// Helper: safe ISO date string for API & DB storage (YYYY-MM-DD)
const toIsoDate = (v) => {
  if (!v) return "";
  if (typeof v === "number" && v > 20000 && v < 80000) {
    const d = new Date(Math.round((v - 25569) * 86400 * 1000));
    if (!isNaN(d.getTime())) v = d;
  }
  if (v instanceof Date) {
    const yyyy = v.getFullYear();
    const mm = String(v.getMonth() + 1).padStart(2, "0");
    const dd = String(v.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  }
  const s = String(v).trim();
  if (!s || s === "null" || s === "undefined") return "";
  // If ISO YYYY-MM-DD or YYYY-MM-DDTHH:mm:ss
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) {
    return s.substring(0, 10);
  }
  // If DD/MM/YYYY or DD-MM-YYYY
  if (/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})/.test(s)) {
    const match = s.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})/);
    return `${match[3]}-${match[2].padStart(2, "0")}-${match[1].padStart(2, "0")}`;
  }
  return s.substring(0, 10);
};

// Generic / Type descriptors from Guidelines sheet that should NOT be treated as field keys
const INVALID_GUIDELINE_KEYS = new Set([
  "text", "numeric", "date", "dropdown", "currency",
  "text / alphanumeric", "text (mandatory)", "dropdown / text",
  "date (dd/mm/yyyy)", "numeric (10 digits)", "numeric (12 digits)",
  "numeric (17 digits)", "alphanumeric (11 chars)", "alphanumeric (10 chars)",
  "currency / number", "dropdown / code", "dropdown (yes / no)",
  "text / value", "valid text or number", "text (master)",
  "positive numeric value", "unique identifier (e.g. 0001, emp-101)",
  "full name (alphabets)", "10-digit phone number", "12-digit uan number",
  "17-digit esi ip insurance number", "11-character ifsc code",
  "m, f, other", "yes, no", "m, p, d, f"
]);

// Helper: normalize calculation code (M, P, D, F)
const normalizeCalcCode = (v) => {
  if (!v) return "M";
  const s = String(v).trim().toUpperCase();
  const first = s.charAt(0);
  if (first === "P" || s === "%") return "P";
  if (first === "D") return "D";
  if (first === "F") return "F";
  if (first === "M") return "M";
  return "M";
};

// Helper: normalize gender (M, F, O)
const normalizeGender = (v) => {
  if (!v) return "M";
  const s = String(v).trim().toUpperCase();
  if (s === "M" || s === "MALE" || s === "1") return "M";
  if (s === "F" || s === "FEMALE" || s === "2") return "F";
  if (s === "O" || s === "OTHER" || s === "OTHERS") return "O";
  return s.charAt(0);
};

// Helper: normalize basic type code (M, D, H, P)
const normalizeBasicTypeCode = (v) => {
  if (v === null || v === undefined || v === "") return "";
  const s = String(v).trim().toUpperCase();
  if (s === "1" || s === "M" || s.startsWith("MONTH")) return "M";
  if (s === "2" || s === "D" || s.startsWith("DAY") || s.startsWith("DAILY") || s.includes("PER DAY") || s.includes("PERDAY")) return "D";
  if (s === "3" || s === "H" || s.startsWith("HOUR")) return "H";
  if (s === "4" || s === "P" || s.startsWith("PIECE")) return "P";
  const first = s.charAt(0);
  if (["M", "D", "H", "P"].includes(first)) return first;
  return s;
};

// Helper: Check if an employee Excel record differs from the database record
const isEmployeeModified = (excelRow, dbRow) => {
  if (!dbRow) return { modified: false };

  const compareField = (excelVal, dbVal, type = "string") => {
    if (type === "boolean") {
      return parseBool(excelVal) !== parseBool(dbVal);
    }
    if (type === "date") {
      const d1 = safeDate(excelVal);
      const d2 = safeDate(dbVal);
      const isD1Empty = !d1 || d1 === "1900-01-01";
      const isD2Empty = !d2 || d2 === "1900-01-01";
      if (isD1Empty && isD2Empty) return false;
      return d1 !== d2;
    }
    if (type === "number") {
      const n1 = (excelVal === "" || excelVal === null || excelVal === undefined) ? 0 : Number(excelVal);
      const n2 = (dbVal === "" || dbVal === null || dbVal === undefined) ? 0 : Number(dbVal);
      if (isNaN(n1) && isNaN(n2)) return false;
      return Math.abs(n1 - n2) > 0.0001;
    }
    if (type === "gender") {
      return normalizeGender(excelVal) !== normalizeGender(dbVal);
    }
    if (type === "calc") {
      const c1 = excelVal ? normalizeCalcCode(excelVal) : "";
      const c2 = dbVal ? normalizeCalcCode(dbVal) : "";
      return c1 !== c2;
    }
    if (type === "basicType") {
      const b1 = excelVal ? normalizeBasicTypeCode(excelVal) : "";
      const b2 = dbVal ? normalizeBasicTypeCode(dbVal) : "";
      return b1 !== b2;
    }

    const s1 = String(excelVal ?? "").trim().toLowerCase();
    const s2 = String(dbVal ?? "").trim().toLowerCase();
    return s1 !== s2;
  };

  const fieldDefs = [
    { key: "Name", type: "string" },
    { key: "FatherName", type: "string" },
    { key: "SerialNo", type: "number" },
    { key: "EmployeeType", type: "string" },
    { key: "Sex", type: "gender" },
    { key: "DateOfBirth", type: "date" },
    { key: "DateOfRetire", type: "date" },
    { key: "MobileNo", type: "string" },
    { key: "Email", type: "string" },
    { key: "PermanentAddress", type: "string" },
    { key: "PresentAddress", type: "string" },
    { key: "Remark", type: "string" },
    { key: "Status", type: "string" },
    { key: "Married", type: "string" },
    { key: "EducationQualification", altKey: "EdQualification", type: "string" },
    { key: "WeeklyHoliday", type: "string" },
    { key: "BranchName", type: "string" },
    { key: "DepartmentName", dbKey: "Dept_DepartmentName", type: "string" },
    { key: "DesignationName", dbKey: "Dept_DesignationName", type: "string" },
    { key: "Dept_EffectiveDate", type: "date" },
    { key: "ShiftName", dbKey: "Shift_ShiftName", altKey: "Shift", type: "string" },
    { key: "Shift_EffectiveFrom", type: "date" },
    { key: "JoinDate", dbKey: "Join_JoinDate", type: "date" },
    { key: "JoinTypeName", dbKey: "Join_JoinTypeName", altKey: "JoinType", type: "string" },
    { key: "LeftDate", dbKey: "Join_LeftDate", type: "date" },
    { key: "ReasonOfLeft", dbKey: "Join_ReasonOfLeft", altKey: "Reason", type: "string" },
    { key: "Sal_BasicSalary", dbKey: "Sal_BasicSalary", altKey: "BasicSalary", type: "number" },
    { key: "Sal_BasicTypeName", dbKey: "Sal_BasicTypeName", altKey: "BasicTypeName", type: "basicType" },
    { key: "Sal_BasicType", dbKey: "Sal_BasicType", altKey: "BasicType", type: "basicType" },
    { key: "F_BasicTypeMaster", dbKey: "Sal_F_BasicTypeMaster", altKey: "F_BasicTypeMaster", type: "number" },
    { key: "Sal_GradePay", dbKey: "Sal_GradePay", altKey: "GradePay", type: "number" },
    { key: "Sal_EffectiveDate", type: "date" },
    { key: "ESIApplicable", type: "boolean" },
    { key: "ESINo", type: "string" },
    { key: "ESIJoiningDate", type: "date" },
    { key: "ESIOldEmployee", type: "boolean" },
    { key: "TICs", type: "boolean" },
    { key: "PICs", type: "boolean" },
    { key: "Accident", type: "boolean" },
    { key: "PFApplicable", type: "boolean" },
    { key: "PFNo", type: "string" },
    { key: "UAN", dbKey: "UANNo", altKey: "UANNo", type: "string" },
    { key: "PFJoinDate", type: "date" },
    { key: "PensionApplicable", type: "boolean" },
    { key: "ExtraPF", type: "number" },
    { key: "PFCalculationType", type: "calc" },
    { key: "EmplrPFOverLimit", type: "boolean" },
    { key: "EmpleeDedOverLimit", type: "boolean" },
    { key: "PensionOverLimit", type: "boolean" },
    { key: "HigherPension", type: "boolean" },
    { key: "FixedPFSalary", type: "number" },
    { key: "OldMemberPF", type: "boolean" },
    { key: "OldMemberPension", type: "boolean" },
    { key: "PrevPFNo", type: "string" },
    { key: "OldUAN", type: "string" },
    { key: "DateOfExitPr", type: "date" },
    { key: "PMRPY", type: "boolean" },
    { key: "PMGKY", type: "boolean" },
    { key: "ABRY", type: "boolean" },
    { key: "IsDeferredPension", type: "boolean" },
    { key: "PensionAge", type: "number" },
    { key: "BankName", type: "string" },
    { key: "BankBranch", type: "string" },
    { key: "IFSCCode", altKey: "BankIFSC", dbKey: "BankIFSC", type: "string" },
    { key: "BankAccountNo", altKey: "BankAccNo", dbKey: "BankAccNo", type: "string" },
    { key: "NameAsBank", type: "string" },
    { key: "BankPayment", type: "boolean" },
    { key: "PanCardNo", type: "string" },
    { key: "NameAsPerPan", type: "string" },
    { key: "AadharNumber", altKey: "AadharCardNo", dbKey: "AadharCardNo", type: "string" },
    { key: "NameInAadhar", type: "string" },
    { key: "DrivingLicenceNo", type: "string" },
    { key: "DLExpiryDate", type: "date" },
    { key: "NameAsPerDL", type: "string" },
    { key: "PassportNo", type: "string" },
    { key: "PassportFrom", type: "date" },
    { key: "PassportTill", type: "date" },
    { key: "PassportName", type: "string" },
    { key: "IsInternationalEmployee", type: "boolean" },
    { key: "CountryOrigin", type: "string" },
    { key: "Nationality", type: "string" },
    { key: "CountryName", type: "string" },
    { key: "VoterIDNo", type: "string" },
    { key: "RationCardNo", type: "string" },
    { key: "IsHandicap", type: "boolean" },
    { key: "HandicapCategory", type: "string" },
    { key: "RefundFileDate", type: "date" },
    { key: "Refunded", type: "boolean" },
    { key: "GratuityPaidDate", type: "date" },
  ];

  for (const def of fieldDefs) {
    let excelVal = excelRow[def.key];
    if (excelVal === undefined && def.altKey) {
      excelVal = excelRow[def.altKey];
    }
    let dbVal = dbRow[def.dbKey || def.key];
    if (dbVal === undefined && def.altKey) {
      dbVal = dbRow[def.altKey];
    }

    if (excelVal !== undefined && compareField(excelVal, dbVal, def.type)) {
      return { modified: true, field: def.key, excelVal, dbVal };
    }
  }

  // Compare Dynamic Allowances
  if (Array.isArray(excelRow.Allowances) && excelRow.Allowances.length > 0) {
    for (const allow of excelRow.Allowances) {
      const id = allow.F_AllowanceMaster;
      if (!id) continue;
      if (allow.Amount !== undefined && allow.Amount !== "") {
        const dbAmt = dbRow[`Allow_${id}_Amount`];
        if (compareField(allow.Amount, dbAmt, "number")) {
          return { modified: true, field: `Allowance_${id}_Amount` };
        }
      }
      if (allow.Calculation !== undefined && allow.Calculation !== "") {
        const dbCalc = dbRow[`Allow_${id}_Calculation`];
        if (compareField(allow.Calculation, dbCalc, "calc")) {
          return { modified: true, field: `Allowance_${id}_Calculation` };
        }
      }
    }
  }

  // Compare Dynamic Deductions
  if (Array.isArray(excelRow.Deductions) && excelRow.Deductions.length > 0) {
    for (const ded of excelRow.Deductions) {
      const id = ded.F_DeductionMaster;
      if (!id) continue;
      if (ded.Amount !== undefined && ded.Amount !== "") {
        const dbAmt = dbRow[`Ded_${id}_Amount`];
        if (compareField(ded.Amount, dbAmt, "number")) {
          return { modified: true, field: `Deduction_${id}_Amount` };
        }
      }
      if (ded.Calculation !== undefined && ded.Calculation !== "") {
        const dbCalc = dbRow[`Ded_${id}_Calculation`];
        if (compareField(ded.Calculation, dbCalc, "calc")) {
          return { modified: true, field: `Deduction_${id}_Calculation` };
        }
      }
    }
  }

  return { modified: false };
};

// ─────────────────────────────────────────────
// Robust Multi-Pattern Matchers for Dynamic Allowances & Deductions
// ─────────────────────────────────────────────
const buildAllowanceMatchers = (allowances) => {
  const matchers = [];
  for (const a of (allowances || [])) {
    const aId = Number(a.Id || a.id);
    const rawName = String(a.Name || a.name || "").trim();
    if (!aId || !rawName) continue;

    const lowerName = rawName.toLowerCase();
    const cleanAlpha = lowerName.replace(/[^a-z0-9]/g, "");
    const withoutParens = lowerName.replace(/\s*\([^)]*\)/g, "").trim();
    const withoutParensAlpha = withoutParens.replace(/[^a-z0-9]/g, "");

    const parenMatches = rawName.match(/\(([^)]+)\)/g) || [];
    const acronyms = parenMatches.map((p) => p.replace(/[()]/g, "").trim().toLowerCase().replace(/[^a-z0-9]/g, ""));

    const nameWithoutAllowWord = lowerName.replace(/\s*\b(allowance|allowances|allow|all\.?)\b/gi, "").replace(/[^a-z0-9]/g, "");
    const withoutParensNoAllow = withoutParens.replace(/\s*\b(allowance|allowances|allow|all\.?)\b/gi, "").replace(/[^a-z0-9]/g, "");

    const words = withoutParens.split(/\s+/).filter(Boolean);
    const autoAcronym = words.length > 1 ? words.map((w) => w[0]).join("") : "";

    const patterns = new Set([
      lowerName,
      cleanAlpha,
      withoutParens,
      withoutParensAlpha,
      nameWithoutAllowWord,
      withoutParensNoAllow,
      ...acronyms,
      autoAcronym,
    ].filter(Boolean));

    // Common Indian payroll aliases
    if (["hra", "houserent", "houserentallowance"].some((k) => patterns.has(k))) {
      ["hra", "houserent", "houserentallowance"].forEach((k) => patterns.add(k));
    }
    if (["da", "dearness", "dearnessallowance"].some((k) => patterns.has(k))) {
      ["da", "dearness", "dearnessallowance"].forEach((k) => patterns.add(k));
    }
    if (["conv", "conall", "conveyance", "conveyanceallowance"].some((k) => patterns.has(k))) {
      ["conv", "conall", "conveyance", "conveyanceallowance"].forEach((k) => patterns.add(k));
    }
    if (["washall", "washing", "washingallowance"].some((k) => patterns.has(k))) {
      ["washall", "washing", "washingallowance"].forEach((k) => patterns.add(k));
    }
    if (["lta", "leavetravel", "leavetravelallowance"].some((k) => patterns.has(k))) {
      ["lta", "leavetravel", "leavetravelallowance"].forEach((k) => patterns.add(k));
    }
    if (["medical", "medicalallowance"].some((k) => patterns.has(k))) {
      ["medical", "medicalallowance"].forEach((k) => patterns.add(k));
    }

    matchers.push({ aId, aName: rawName, patterns, withoutParensAlpha, withoutParensNoAllow });
  }
  return matchers;
};

const buildDeductionMatchers = (deductions) => {
  const matchers = [];
  for (const d of (deductions || [])) {
    const dId = Number(d.Id || d.id);
    const rawName = String(d.Name || d.name || "").trim();
    if (!dId || !rawName) continue;

    const lowerName = rawName.toLowerCase();
    const cleanAlpha = lowerName.replace(/[^a-z0-9]/g, "");
    const withoutParens = lowerName.replace(/\s*\([^)]*\)/g, "").trim();
    const withoutParensAlpha = withoutParens.replace(/[^a-z0-9]/g, "");

    const parenMatches = rawName.match(/\(([^)]+)\)/g) || [];
    const acronyms = parenMatches.map((p) => p.replace(/[()]/g, "").trim().toLowerCase().replace(/[^a-z0-9]/g, ""));

    const nameWithoutDedWord = lowerName.replace(/\s*\b(deduction|deductions|ded|deduct)\b/gi, "").replace(/[^a-z0-9]/g, "");
    const withoutParensNoDed = withoutParens.replace(/\s*\b(deduction|deductions|ded|deduct)\b/gi, "").replace(/[^a-z0-9]/g, "");

    const words = withoutParens.split(/\s+/).filter(Boolean);
    const autoAcronym = words.length > 1 ? words.map((w) => w[0]).join("") : "";

    const patterns = new Set([
      lowerName,
      cleanAlpha,
      withoutParens,
      withoutParensAlpha,
      nameWithoutDedWord,
      withoutParensNoDed,
      ...acronyms,
      autoAcronym,
    ].filter(Boolean));

    const DED_EXCLUDE_WORDS = new Set(["with", "from", "and", "salary", "basic", "amount", "rate", "date", "type", "name", "total", "gross", "code", "mode", "card", "join", "left"]);
    words.filter((w) => w.length > 3 && !DED_EXCLUDE_WORDS.has(w)).forEach((w) => patterns.add(w));

    // Common Indian payroll deduction aliases
    if (["tds", "incometax", "tax"].some((k) => patterns.has(k))) {
      ["tds", "incometax", "itdeduction", "taxdeduction"].forEach((k) => patterns.add(k));
    }
    if (["pt", "profettax", "professionaltax", "proftax"].some((k) => patterns.has(k))) {
      ["pt", "profettax", "professionaltax", "proftax"].forEach((k) => patterns.add(k));
    }
    if (["canteen", "food", "mess"].some((k) => patterns.has(k))) {
      ["canteen", "food", "mess", "canteencharge", "canteencharges", "canteendeduction"].forEach((k) => patterns.add(k));
    }
    if (["uniform", "dress"].some((k) => patterns.has(k))) {
      ["uniform", "dress", "uniformdeduction", "uniformcharges"].forEach((k) => patterns.add(k));
    }
    if (["transport", "transportation", "bus", "travel", "cab"].some((k) => patterns.has(k))) {
      ["transport", "transportation", "bus", "travel", "cab", "transportdeduction"].forEach((k) => patterns.add(k));
    }
    if (["loan", "advance"].some((k) => patterns.has(k))) {
      ["loan", "advance", "salaryadvance", "loandeduction", "advancededuction"].forEach((k) => patterns.add(k));
    }
    if (["lic", "insurance"].some((k) => patterns.has(k))) {
      ["lic", "insurance", "groupinsurance", "insurancededuction"].forEach((k) => patterns.add(k));
    }
    if (["security", "deposit"].some((k) => patterns.has(k))) {
      ["security", "deposit", "securitydeposit"].forEach((k) => patterns.add(k));
    }

    matchers.push({ dId, dName: rawName, patterns, withoutParensAlpha, withoutParensNoDed });
  }
  return matchers;
};

const resolveDynamicAllowanceHeader = (headerStr, matchers) => {
  if (!headerStr || !matchers || matchers.length === 0) return null;
  const raw = String(headerStr).trim();
  const lower = raw.toLowerCase();

  let base = lower
    .replace(/\s*\b(allowance|allowances|allow|all\.?|amount|amt|value|val|calculation|calc|round\s*off|working\s*days?|work\s*days?|applicable|is\s*active|active|pf\s*apply|pf|esi\s*apply|esi|on\s*gross|ongross|gross|effective\s*from|from\s*date|effective)\b/gi, " ")
    .replace(/[_\s]+/g, " ")
    .trim();
  const baseAlpha = base.replace(/[^a-z0-9]/g, "");
  const rawAlpha = lower.replace(/[^a-z0-9]/g, "");
  const baseWithoutParens = base.replace(/\s*\([^)]*\)/g, "").replace(/[^a-z0-9]/g, "");

  const headerParen = (raw.match(/\(([^)]+)\)/) || [])[1];
  const headerAcronym = headerParen ? headerParen.toLowerCase().replace(/[^a-z0-9]/g, "") : "";

  for (const m of matchers) {
    if (
      m.patterns.has(rawAlpha) ||
      m.patterns.has(base) ||
      m.patterns.has(baseAlpha) ||
      m.patterns.has(baseWithoutParens) ||
      (headerAcronym && m.patterns.has(headerAcronym)) ||
      (baseAlpha && m.withoutParensNoAllow && (baseAlpha === m.withoutParensNoAllow || (m.withoutParensNoAllow.length >= 4 && baseAlpha.startsWith(m.withoutParensNoAllow))))
    ) {
      if (lower.includes("round off") || lower.includes("roundoff")) return `Allow_${m.aId}_IsRoundOffApply`;
      if (lower.includes("working day") || lower.includes("work day")) return `Allow_${m.aId}_IsWorkingDaysApply`;
      if (lower.includes("applicable") || lower.includes("is active")) return `Allow_${m.aId}_IsApplicable`;
      if (lower.includes("calculation") || lower.includes("calc")) return `Allow_${m.aId}_Calculation`;
      if (lower.includes("pf apply") || lower.includes("is pf")) return `Allow_${m.aId}_IsPFApply`;
      if (lower.includes("esi apply") || lower.includes("is esi")) return `Allow_${m.aId}_IsESIApply`;
      if (lower.includes("on gross") || lower.includes("ongross")) return `Allow_${m.aId}_IsOnGross`;
      if (lower.includes("effective") || lower.includes("from date")) return `Allow_${m.aId}_EffectiveFrom`;
      return `Allow_${m.aId}_Amount`;
    }
  }
  return null;
};

const resolveDynamicDeductionHeader = (headerStr, matchers) => {
  if (!headerStr || !matchers || matchers.length === 0) return null;
  const raw = String(headerStr).trim();
  const lower = raw.toLowerCase();

  let base = lower
    .replace(/\s*\b(deduction|deductions|ded|amount|amt|value|val|calculation|calc|round\s*off|working\s*days?|work\s*days?|applicable|is\s*active|active|pf\s*apply|pf|esi\s*apply|esi|on\s*gross|ongross|gross|effective\s*from|from\s*date|effective)\b/gi, " ")
    .replace(/[_\s]+/g, " ")
    .trim();
  const baseAlpha = base.replace(/[^a-z0-9]/g, "");
  const rawAlpha = lower.replace(/[^a-z0-9]/g, "");
  const baseWithoutParens = base.replace(/\s*\([^)]*\)/g, "").replace(/[^a-z0-9]/g, "");

  const headerParen = (raw.match(/\(([^)]+)\)/) || [])[1];
  const headerAcronym = headerParen ? headerParen.toLowerCase().replace(/[^a-z0-9]/g, "") : "";

  for (const m of matchers) {
    if (
      m.patterns.has(rawAlpha) ||
      m.patterns.has(base) ||
      m.patterns.has(baseAlpha) ||
      m.patterns.has(baseWithoutParens) ||
      (headerAcronym && m.patterns.has(headerAcronym)) ||
      (baseAlpha && m.withoutParensNoDed && (baseAlpha === m.withoutParensNoDed || (m.withoutParensNoDed.length >= 4 && baseAlpha.startsWith(m.withoutParensNoDed))))
    ) {
      if (lower.includes("round off") || lower.includes("roundoff")) return `Ded_${m.dId}_IsRoundOffApply`;
      if (lower.includes("working day") || lower.includes("work day")) return `Ded_${m.dId}_IsWorkingDaysApply`;
      if (lower.includes("applicable") || lower.includes("is active")) return `Ded_${m.dId}_IsApplicable`;
      if (lower.includes("calculation") || lower.includes("calc")) return `Ded_${m.dId}_Calculation`;
      if (lower.includes("pf apply") || lower.includes("is pf")) return `Ded_${m.dId}_IsPFApply`;
      if (lower.includes("esi apply") || lower.includes("is esi")) return `Ded_${m.dId}_IsESIApply`;
      if (lower.includes("on gross") || lower.includes("ongross")) return `Ded_${m.dId}_IsOnGross`;
      if (lower.includes("effective") || lower.includes("from date")) return `Ded_${m.dId}_EffectiveFrom`;
      return `Ded_${m.dId}_Amount`;
    }
  }
  return null;
};

// ─────────────────────────────────────────────
// Main Component
// ─────────────────────────────────────────────
const EmployeeImportExport = () => {
  const navigate = useNavigate();

  // Export state
  const [exporting, setExporting] = useState(false);
  const [exportColumns, setExportColumns] = useState([]);
  const [existingEmployees, setExistingEmployees] = useState([]);
  const [downloadingTemplate, setDownloadingTemplate] = useState(false);
  const [exportSummary, setExportSummary] = useState({
    totalEmployees: 0,
    totalColumns: 0,
    allowancesCount: 0,
    deductionsCount: 0,
    loading: false,
  });

  // Column Selection & Preferences state (Firm-wise persistent)
  const [selectedColumnKeys, setSelectedColumnKeys] = useState([]);
  const [showColumnModal, setShowColumnModal] = useState(false);
  const [columnSearch, setColumnSearch] = useState("");
  const [savingPreferences, setSavingPreferences] = useState(false);

  // Import state
  const [importFile, setImportFile] = useState(null);
  const [previewData, setPreviewData] = useState([]); // { rowIndex, data, errors[], status, type, isNew, isModified }
  const [importProgress, setImportProgress] = useState(0);
  const [importing, setImporting] = useState(false);
  const [showResultModal, setShowResultModal] = useState(false);
  const [importResults, setImportResults] = useState({
    total: 0,
    success: 0,
    inserted: 0,
    updated: 0,
    unchanged: 0,
    failed: 0,
    errors: [],
    details: [],
    createdDepartments: [],
    createdDesignations: [],
  });
  const [isParsing, setIsParsing] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [importedColumns, setImportedColumns] = useState([]);
  const [previewSearch, setPreviewSearch] = useState("");

  // Import pagination state
  const [importPage, setImportPage] = useState(1);
  const [importPageSize, setImportPageSize] = useState(25);

  // Master Lists for ID Resolution and dynamic matching
  const [employeeTypesList, setEmployeeTypesList] = useState(["Ordinary", "Permanent", "CONTRACTUAL"]);
  const [shiftsList, setShiftsList] = useState([]);
  const [firmShiftsList, setFirmShiftsList] = useState([]);
  const [departmentsList, setDepartmentsList] = useState([]);
  const [designationsList, setDesignationsList] = useState([]);
  const [branchesList, setBranchesList] = useState([]);
  const [joinTypesList, setJoinTypesList] = useState([]);
  const [firmAllowancesList, setFirmAllowancesList] = useState([]);
  const [firmDeductionsList, setFirmDeductionsList] = useState([]);
  const [workerCategoriesList, setWorkerCategoriesList] = useState([]);

  // Export preview & pagination state
  const [showExportPreview, setShowExportPreview] = useState(false);
  const [exportSearch, setExportSearch] = useState("");
  const [exportPage, setExportPage] = useState(1);
  const [exportPageSize, setExportPageSize] = useState(25);

  // Guidelines modal state
  const [showGuidelinesModal, setShowGuidelinesModal] = useState(false);
  const [guidelineSearch, setGuidelineSearch] = useState("");

  // Firm and Branch from Session
  const { firm: selectedFirm, firmId: sessionFirmId, branchId: sessionBranchId } = useSession();
  const firmId = sessionFirmId ? String(sessionFirmId) : "0";
  const branchId = sessionBranchId ? String(sessionBranchId) : "0";

  const fileInputRef = useRef(null);

  // Auth token
  const authUser = (() => {
    try {
      return JSON.parse(localStorage.getItem("userDetails") || "{}");
    } catch {
      return {};
    }
  })();
  const authToken = authUser?.token || API_WEB_URLS.Token;
  const userId = authUser?.Id ? String(authUser.Id) : "1";

  // ──────────────────────────────────────────
  // Reusable Master Fetchers (Departments & Designations)
  // ──────────────────────────────────────────
  const fetchDepartments = useCallback(async (targetFirmId = firmId) => {
    if (!targetFirmId || targetFirmId === "0") return [];
    try {
      const deptRes = await fetch(
        `${API_WEB_URLS.BASE}${API_WEB_URLS.MASTER}/0/token/DepartmentMaster/TBL.F_FirmMaster/${targetFirmId}`,
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      if (deptRes.ok) {
        const deptJson = await deptRes.json();
        const dList = deptJson?.data?.dataList || deptJson?.dataList || deptJson?.data || [];
        if (Array.isArray(dList)) {
          const active = dList.filter((d) => !d.IsNotInUse);
          setDepartmentsList(active);
          return active;
        }
      }
    } catch (deptErr) {
      console.warn("Could not fetch DepartmentMaster:", deptErr);
    }
    return [];
  }, [authToken, firmId]);

  const fetchDesignations = useCallback(async (targetFirmId = firmId) => {
    if (!targetFirmId || targetFirmId === "0") return [];
    try {
      const desigRes = await fetch(
        `${API_WEB_URLS.BASE}${API_WEB_URLS.MASTER}/0/token/DesignationMaster/TBL.F_FirmMaster/${targetFirmId}`,
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      if (desigRes.ok) {
        const desigJson = await desigRes.json();
        const dgList = desigJson?.data?.dataList || desigJson?.dataList || desigJson?.data || [];
        if (Array.isArray(dgList)) {
          const active = dgList.filter((d) => !d.IsNotInUse);
          setDesignationsList(active);
          return active;
        }
      }
    } catch (desigErr) {
      console.warn("Could not fetch DesignationMaster:", desigErr);
    }
    return [];
  }, [authToken, firmId]);

  // ──────────────────────────────────────────
  // Fetch Export Stats & Column Preferences on Mount / Firm Change
  // ──────────────────────────────────────────
  const loadExportSummary = async () => {
    if (!firmId || firmId === "0") return;
    setExportSummary((prev) => ({ ...prev, loading: true }));
    try {
      const res = await fetch(`${API_WEB_URLS.BASE}EmployeeExport/0/token/${firmId}`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      if (res.ok) {
        const json = await res.json();
        const exportData = json?.data;
        if (exportData) {
          setExistingEmployees(exportData.rows || []);
          const allCols = exportData.columns || [];
          setExportColumns(allCols);
          setFirmAllowancesList(Array.isArray(exportData.allowances) ? exportData.allowances : []);
          setFirmDeductionsList(Array.isArray(exportData.deductions) ? exportData.deductions : []);
          setExportSummary({
            totalEmployees: exportData.totalCount || exportData.rows?.length || 0,
            totalColumns: allCols.length || 0,
            allowancesCount: exportData.allowances?.length || 0,
            deductionsCount: exportData.deductions?.length || 0,
            loading: false,
          });

          // ── Load Firm-Wise Column Preferences ──
          let activeKeys = null;
          if (exportData.savedPreferences) {
            try {
              const parsed = typeof exportData.savedPreferences === "string"
                ? JSON.parse(exportData.savedPreferences)
                : exportData.savedPreferences;
              if (Array.isArray(parsed) && parsed.length > 0) {
                activeKeys = parsed;
              }
            } catch (e) {
              console.warn("Could not parse savedPreferences from API:", e);
            }
          }

          // Fallback to localStorage if not found on API
          if (!activeKeys) {
            const local = localStorage.getItem(`export_cols_firm_${firmId}`);
            if (local) {
              try {
                const parsedLocal = JSON.parse(local);
                if (Array.isArray(parsedLocal) && parsedLocal.length > 0) {
                  activeKeys = parsedLocal;
                }
              } catch {}
            }
          }

          // Default to all columns if no preference saved yet
          if (!activeKeys && allCols.length > 0) {
            activeKeys = allCols.map((c) => c.key);
          }

          if (activeKeys) {
            setSelectedColumnKeys(activeKeys);
          }
        }
      }

      // Fetch active Departments for firm
      await fetchDepartments(firmId);

      // Fetch active Designations for firm
      await fetchDesignations(firmId);

      // Fetch active Worker Categories (for designation fallback)
      try {
        const wcRes = await fetch(`${API_WEB_URLS.BASE}${API_WEB_URLS.MASTER}/0/token/WorkerCategory/Id/0`, {
          headers: { Authorization: `Bearer ${authToken}` },
        });
        if (wcRes.ok) {
          const wcJson = await wcRes.json();
          const wcList = wcJson?.data?.dataList || wcJson?.dataList || wcJson?.data || [];
          if (Array.isArray(wcList)) setWorkerCategoriesList(wcList.filter((w) => !w.IsNotInUse));
        }
      } catch (wcErr) {
        console.warn("Could not fetch WorkerCategory:", wcErr);
      }

      // Fetch active Branches for firm
      try {
        const brRes = await fetch(`${API_WEB_URLS.BASE}${API_WEB_URLS.MASTER}/0/token/GetBranchByFirmId/Id/${firmId}`, {
          headers: { Authorization: `Bearer ${authToken}` },
        });
        if (brRes.ok) {
          const brJson = await brRes.json();
          const bList = brJson?.data?.dataList || brJson?.dataList || brJson?.data || [];
          if (Array.isArray(bList)) setBranchesList(bList);
        }
      } catch (brErr) {
        console.warn("Could not fetch BranchMaster:", brErr);
      }

      // Fetch active Join Types
      try {
        const jtRes = await fetch(`${API_WEB_URLS.BASE}${API_WEB_URLS.MASTER}/0/token/JoinTypeMaster/Id/0`, {
          headers: { Authorization: `Bearer ${authToken}` },
        });
        if (jtRes.ok) {
          const jtJson = await jtRes.json();
          const jtList = jtJson?.data?.dataList || jtJson?.dataList || jtJson?.data || [];
          if (Array.isArray(jtList)) setJoinTypesList(jtList.filter(j => !j.IsNotInUse));
        }
      } catch (jtErr) {
        console.warn("Could not fetch JoinTypeMaster:", jtErr);
      }

      // Fetch active Allowances for firm
      try {
        const allowRes = await fetch(`${API_WEB_URLS.BASE}${API_WEB_URLS.MASTER}/0/token/AllowanceMasterForEmployeeEntry/TBL.F_FirmMaster/${firmId}`, {
          headers: { Authorization: `Bearer ${authToken}` },
        });
        if (allowRes.ok) {
          const allowJson = await allowRes.json();
          const aList = allowJson?.data?.dataList || allowJson?.dataList || allowJson?.data || [];
          if (Array.isArray(aList)) setFirmAllowancesList(aList);
        }
      } catch (allowErr) {
        console.warn("Could not fetch AllowanceMaster:", allowErr);
      }

      // Fetch active Deductions for firm
      try {
        const dedRes = await fetch(`${API_WEB_URLS.BASE}${API_WEB_URLS.MASTER}/0/token/DeductionMaster/TBL.F_FirmMaster/${firmId}`, {
          headers: { Authorization: `Bearer ${authToken}` },
        });
        if (dedRes.ok) {
          const dedJson = await dedRes.json();
          const ddList = dedJson?.data?.dataList || dedJson?.dataList || dedJson?.data || [];
          if (Array.isArray(ddList)) setFirmDeductionsList(ddList);
        }
      } catch (dedErr) {
        console.warn("Could not fetch DeductionMaster:", dedErr);
      }

      // Fetch active Employee Types from EmployeeTypeMaster for Excel dropdowns
      try {
        const typeRes = await fetch(`${API_WEB_URLS.BASE}${API_WEB_URLS.MASTER}/0/token/EmployeeTypeMaster`, {
          headers: { Authorization: `Bearer ${authToken}` },
        });
        if (typeRes.ok) {
          const typeJson = await typeRes.json();
          const rawList = typeJson?.data?.dataList || typeJson?.dataList || typeJson?.data || [];
          if (Array.isArray(rawList) && rawList.length > 0) {
            const activeTypes = rawList
              .filter((item) => !item.IsNotInUse)
              .map((item) => String(item.Name || item.EmployeeType || "").trim())
              .filter(Boolean);
            if (activeTypes.length > 0) {
              setEmployeeTypesList(Array.from(new Set([...activeTypes, "Ordinary"])));
            }
          }
        }
      } catch (typeErr) {
        console.warn("Could not fetch EmployeeTypeMaster for dropdowns:", typeErr);
      }

      // Fetch active Shifts exclusively from FirmShiftTimeData for the selected firm
      try {
        let loadedShifts = [];
        let loadedShiftObjects = [];
        const shiftRes = await fetch(`${API_WEB_URLS.BASE}${API_WEB_URLS.MASTER}/0/token/FirmShiftTimeData/Id/0`, {
          headers: { Authorization: `Bearer ${authToken}` },
        });
        if (shiftRes.ok) {
          const shiftJson = await shiftRes.json();
          const rawShiftData = shiftJson?.data?.dataList || shiftJson?.dataList || shiftJson?.data || [];
          if (Array.isArray(rawShiftData) && rawShiftData.length > 0) {
            const jsonStr = rawShiftData[0]?.FirmShiftTimeJson;
            if (jsonStr) {
              try {
                const parsed = typeof jsonStr === "string" ? JSON.parse(jsonStr) : jsonStr;
                if (Array.isArray(parsed)) {
                  const firmShifts = parsed.filter((m) => String(m.F_FirmMaster) === String(firmId));
                  loadedShiftObjects = firmShifts;
                  loadedShifts = firmShifts.map((m) => String(m.Name || "").trim()).filter(Boolean);
                }
              } catch {}
            }
          }
        }

        // Fallback to ShiftMaster only if no firm-specific shifts are configured
        if (loadedShifts.length === 0) {
          try {
            const smRes = await fetch(`${API_WEB_URLS.BASE}${API_WEB_URLS.MASTER}/0/token/ShiftMaster/Id/0`, {
              headers: { Authorization: `Bearer ${authToken}` },
            });
            if (smRes.ok) {
              const smJson = await smRes.json();
              const smList = smJson?.data?.dataList || smJson?.dataList || smJson?.data || [];
              if (Array.isArray(smList) && smList.length > 0) {
                const activeSM = smList.filter((s) => !s.IsNotInUse);
                loadedShiftObjects = activeSM;
                loadedShifts = activeSM.map((s) => String(s.Name || "").trim()).filter(Boolean);
              }
            }
          } catch {}
        }

        setShiftsList(Array.from(new Set(loadedShifts)));
        setFirmShiftsList(loadedShiftObjects);
      } catch (shiftErr) {
        console.warn("Could not fetch Shifts for dropdowns:", shiftErr);
      }
    } catch (err) {
      console.error("Failed to load export summary:", err);
    } finally {
      setExportSummary((prev) => ({ ...prev, loading: false }));
    }
  };

  useEffect(() => {
    loadExportSummary();
  }, [firmId]);

  // ──────────────────────────────────────────
  // Column Selection Handlers & Grouping
  // ──────────────────────────────────────────
  const groupedColumns = React.useMemo(() => {
    const groups = {};
    (exportColumns || []).forEach((col) => {
      let cat = col.category || "General Information";
      if (cat.startsWith("Allowance:")) cat = "🎁 Dynamic Allowances";
      else if (cat.startsWith("Deduction:")) cat = "✂️ Dynamic Deductions";
      else if (cat.includes("Basic Information")) cat = "👤 Basic Information";
      else if (cat.includes("ESI Details")) cat = "🏥 ESI Details";
      else if (cat.includes("PF Details")) cat = "🏦 PF Details";
      else if (cat.includes("Bank & Holiday")) cat = "💳 Bank & Holiday Details";
      else if (cat.includes("Identity Documents")) cat = "🪪 Identity Documents";
      else if (cat.includes("Department History")) cat = "🏢 Department History";
      else if (cat.includes("Shift History")) cat = "⏰ Shift History";
      else if (cat.includes("Join History")) cat = "📅 Join History";
      else if (cat.includes("Salary History")) cat = "💰 Salary History";

      if (!groups[cat]) groups[cat] = [];
      groups[cat].push(col);
    });
    return groups;
  }, [exportColumns]);

  const filteredGroupedColumns = React.useMemo(() => {
    if (!columnSearch.trim()) return groupedColumns;
    const q = columnSearch.toLowerCase().trim();
    const res = {};
    Object.entries(groupedColumns).forEach(([cat, cols]) => {
      const matching = cols.filter(
        (c) =>
          c.label.toLowerCase().includes(q) ||
          c.key.toLowerCase().includes(q) ||
          cat.toLowerCase().includes(q)
      );
      if (matching.length > 0) {
        res[cat] = matching;
      }
    });
    return res;
  }, [groupedColumns, columnSearch]);

  const handleSelectAllColumns = () => {
    setSelectedColumnKeys(exportColumns.map((c) => c.key));
  };

  const handleDeselectAllColumns = () => {
    setSelectedColumnKeys(["Name", "EmployeeCode"]);
  };

  const handleSelectCoreColumns = () => {
    const coreKeys = [
      "SerialNo", "EmployeeCode", "Name", "FatherName", "EmployeeType", "Sex",
      "DateOfBirth", "MobileNo", "BranchName", "DepartmentName", "DesignationName",
      "JoinDate", "Join_JoinDate", "Sal_BasicSalary", "Sal_EffectiveDate"
    ];
    const available = exportColumns.filter((c) => coreKeys.includes(c.key)).map((c) => c.key);
    setSelectedColumnKeys(available.length > 0 ? available : ["Name", "EmployeeCode"]);
  };

  const handleToggleCategory = (colsInCategory) => {
    const catKeys = colsInCategory.map((c) => c.key);
    const allSelected = catKeys.every((k) => selectedColumnKeys.includes(k));
    if (allSelected) {
      setSelectedColumnKeys((prev) => prev.filter((k) => k === "Name" || !catKeys.includes(k)));
    } else {
      setSelectedColumnKeys((prev) => Array.from(new Set([...prev, ...catKeys])));
    }
  };

  const handleToggleColumn = (key) => {
    if (key === "Name") {
      toast.info("Employee Name is required.");
      return;
    }
    setSelectedColumnKeys((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };

  const handleSavePreferences = async () => {
    if (!firmId || firmId === "0") {
      toast.warning("Please select a firm first.");
      return;
    }
    setSavingPreferences(true);
    try {
      localStorage.setItem(`export_cols_firm_${firmId}`, JSON.stringify(selectedColumnKeys));

      const res = await fetch(
        `${API_WEB_URLS.BASE}EmployeeExport/Preferences/${userId}/token/${firmId}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${authToken}`,
          },
          body: JSON.stringify(selectedColumnKeys),
        }
      );

      if (res.ok) {
        toast.success("✅ Column preferences saved as default for this firm!");
        setShowColumnModal(false);
      } else {
        toast.info("Column preferences saved locally for this firm.");
        setShowColumnModal(false);
      }
    } catch (err) {
      console.error("Save preference error:", err);
      toast.info("Column preferences saved locally for this firm.");
      setShowColumnModal(false);
    } finally {
      setSavingPreferences(false);
    }
  };


  // ──────────────────────────────────────────
  // Excel Column Guidelines & In-Workbook Help
  // ──────────────────────────────────────────
  const EXCEL_COLUMN_GUIDELINES = {
    Name: {
      format: "Text (Mandatory)",
      allowed: "Full Name (Alphabets)",
      rule: "Mandatory employee full name.",
      example: "Rahul Sharma",
    },
    EmployeeCode: {
      format: "Text / Alphanumeric",
      allowed: "Unique identifier (e.g. 0001, EMP-101)",
      rule: "Unique employee identification code. Used to match and update existing employee records.",
      example: "0001",
    },
    FatherName: { format: "Text", allowed: "Father's Name", rule: "Father's full name.", example: "Suresh Sharma" },
    MotherName: { format: "Text", allowed: "Mother's Name", rule: "Mother's full name.", example: "Sunita Sharma" },
    SpouseName: { format: "Text", allowed: "Spouse's Name", rule: "Spouse's name if married.", example: "Pooja Sharma" },
    Sex: { format: "Dropdown / Text", allowed: "M, F, Other", rule: "Gender of employee: M (Male), F (Female), or Other.", example: "M" },
    DOB: { format: "Date (DD/MM/YYYY)", allowed: "Valid date in past", rule: "Date of Birth. Format: YYYY-MM-DD or DD/MM/YYYY.", example: "15/05/1995" },
    DOJ: { format: "Date (DD/MM/YYYY)", allowed: "Valid date", rule: "Date of Joining. Crucial for salary & statutory effective dates.", example: "01/01/2024" },
    DOC: { format: "Date (DD/MM/YYYY)", allowed: "Valid date", rule: "Date of Confirmation.", example: "2024-04-01" },
    Married: { format: "Dropdown / Text", allowed: "YES, NO, Married, Unmarried", rule: "Marital status: YES (Married) or NO (Unmarried).", example: "YES" },
    BloodGroup: { format: "Text", allowed: "A+, A-, B+, B-, AB+, AB-, O+, O-", rule: "Employee blood group.", example: "O+" },
    MobileNo: { format: "Numeric (10 digits)", allowed: "10-digit phone number", rule: "Primary contact phone number.", example: "9876543210" },
    Email: { format: "Email Address", allowed: "Valid email ID", rule: "Employee official or personal email address.", example: "rahul@example.com" },
    PanNo: { format: "Alphanumeric (10 chars)", allowed: "e.g. ABCDE1234F", rule: "Income Tax PAN card number.", example: "ABCDE1234F" },
    AdharNo: { format: "Numeric (12 digits)", allowed: "12-digit Aadhaar number", rule: "Aadhaar / UID number.", example: "123456789012" },

    // Master Links
    DepartmentName: { format: "Text (Master)", allowed: "Existing Department name", rule: "Department name. Auto-created if new.", example: "Production" },
    DesignationName: { format: "Text (Master)", allowed: "Existing Designation name", rule: "Designation name. Auto-created if new.", example: "Supervisor" },
    CategoryName: { format: "Text (Master)", allowed: "Existing Category name", rule: "Employee category. Auto-created if new.", example: "Skilled" },
    BranchName: { format: "Text (Master)", allowed: "Existing Branch name", rule: "Branch location name. Auto-created if new.", example: "Head Office" },
    ShiftName: { format: "Text (Master)", allowed: shiftsList.length > 0 ? `Firm Shifts (${shiftsList.join(", ")})` : "Firm Shift Name", rule: "Shift name configured in Firm Shift Master. Auto-mapped to EmployeeShiftL.", example: shiftsList[0] || "Firm Shift Name" },
    EmployeeType: { format: "Dropdown / Text", allowed: "Ordinary, Permanent, CONTRACTUAL", rule: "Employment type classification from Employee Type Master.", example: "Ordinary" },
    Status: { format: "Dropdown / Text", allowed: "Working, Left, Suspended, Retired", rule: "Employment working status: Working (Active in payroll), Left, Suspended, or Retired. Defaults to 'Working' if blank.", example: "Working" },
    WorkingStatus: { format: "Dropdown / Text", allowed: "Working, Left, Suspended, Retired", rule: "Employment working status: Working (Active in payroll), Left, Suspended, or Retired. Defaults to 'Working' if blank.", example: "Working" },
    ReasonToLeft: { format: "Text (Master)", allowed: "Resignation, Retirement, Better Opportunity, Medical, etc.", rule: "Reason for leaving master name if employee is Left/Inactive.", example: "Resignation" },
    LeftDate: { format: "Date (DD/MM/YYYY)", allowed: "Valid date", rule: "Date when employee left service.", example: "31/12/2024" },
    ResignDate: { format: "Date (DD/MM/YYYY)", allowed: "Valid date", rule: "Date when resignation was submitted.", example: "2024-11-30" },

    // Addresses
    PresentAddress: { format: "Text", allowed: "Current residential address", rule: "Present address of employee.", example: "Flat 101, Galaxy Apts, Delhi" },
    PresentCity: { format: "Text", allowed: "City name", rule: "Current residential city.", example: "New Delhi" },
    PresentState: { format: "Text", allowed: "State name", rule: "Current residential state.", example: "Delhi" },
    PresentPinCode: { format: "Numeric (6 digits)", allowed: "6-digit postal PIN code", rule: "Present address PIN code.", example: "110001" },
    PermanentAddress: { format: "Text", allowed: "Permanent home address", rule: "Permanent home address.", example: "Vill. Rampur, Dist. Patna" },
    PermanentCity: { format: "Text", allowed: "City name", rule: "Permanent address city.", example: "Patna" },
    PermanentState: { format: "Text", allowed: "State name", rule: "Permanent address state.", example: "Bihar" },
    PermanentPinCode: { format: "Numeric (6 digits)", allowed: "6-digit postal PIN code", rule: "Permanent address PIN code.", example: "800001" },

    // Bank & Payment
    BankName: { format: "Text", allowed: "Bank name", rule: "Salary disbursement bank name.", example: "State Bank of India" },
    Branch: { format: "Text", allowed: "Bank branch name", rule: "Bank branch location.", example: "Connaught Place" },
    AccountNo: { format: "Alphanumeric / Text", allowed: "Bank account number", rule: "Employee salary bank account number.", example: "30123456789" },
    IFSCCode: { format: "Alphanumeric (11 chars)", allowed: "11-character IFSC code", rule: "Bank IFSC code.", example: "SBIN0001234" },
    PaymentMode: { format: "Dropdown / Text", allowed: "Bank, Cash, Cheque, NEFT, RTGS", rule: "Salary payment mode.", example: "Bank" },
    BankPayment: { format: "Dropdown / Text", allowed: "YES, NO", rule: "Is salary paid via bank transfer: YES or NO.", example: "YES" },

    // Statutory PF / ESI
    PFApplicable: { format: "Dropdown / Text", allowed: "YES, NO", rule: "Whether Provident Fund (PF) is applicable: YES or NO.", example: "YES" },
    PFNo: { format: "Text", allowed: "PF Member ID (e.g. DL/CPM/0012345/000/0000001)", rule: "Provident Fund member ID.", example: "DLCPM00123450000001" },
    UAN: { format: "Numeric (12 digits)", allowed: "12-digit UAN number", rule: "Universal Account Number issued by EPFO.", example: "100123456789" },
    PFEffectiveDate: { format: "Date (DD/MM/YYYY)", allowed: "Valid date", rule: "PF coverage effective date. Blank defaults to DOJ.", example: "01/01/2024" },
    PensionApplicable: { format: "Dropdown / Text", allowed: "YES, NO", rule: "Whether EPS Pension scheme is applicable: YES or NO.", example: "YES" },
    ESIApplicable: { format: "Dropdown / Text", allowed: "YES, NO", rule: "Whether ESIC is applicable: YES or NO.", example: "YES" },
    ESINo: { format: "Numeric (17 digits)", allowed: "17-digit ESI IP insurance number", rule: "Employee ESI Insurance number.", example: "11123456789012345" },
    ESIEffectiveDate: { format: "Date (DD/MM/YYYY)", allowed: "Valid date", rule: "ESI coverage effective date. Blank defaults to DOJ.", example: "01/01/2024" },
    ESIDispensary: { format: "Text", allowed: "Dispensary name / location", rule: "Assigned ESI dispensary name.", example: "Mayapuri Dispensary" },

    // Salary Master
    Sal_BasicSalary: { format: "Currency / Number", allowed: "Positive numeric value", rule: "Basic wage / salary amount.", example: "15000" },
    Sal_BasicType: { format: "Dropdown (M / D / H / P)", allowed: "M, D, H, P", rule: "Basic pay rate basis: M = Monthly, D = Daily / Per Day, H = Hourly, P = Piece Rate.", example: "M" },
    Sal_BasicTypeName: { format: "Dropdown (M / D / H / P)", allowed: "M, D, H, P", rule: "Basic pay rate basis: M = Monthly, D = Daily / Per Day, H = Hourly, P = Piece Rate.", example: "M" },
    Sal_Calculation: { format: "Dropdown / Text", allowed: "M, P, D, F", rule: "M = Monthly, P = Percentage, D = Daily, F = Fixed.", example: "M" },
    Sal_Period: { format: "Dropdown / Text", allowed: "Monthly, Daily, Weekly, Fortnightly", rule: "Wage calculation period.", example: "Monthly" },
    Sal_EffectiveFrom: { format: "Date (DD/MM/YYYY)", allowed: "Valid date", rule: "Effective date for salary rate. Blank defaults to DOJ.", example: "01/01/2024" },
    Sal_RoundOff: { format: "Dropdown / Text", allowed: "YES, NO", rule: "Round off basic salary: YES or NO.", example: "YES" },
    Sal_WorkingDays: { format: "Dropdown / Text", allowed: "YES, NO", rule: "Whether basic salary depends on attendance working days.", example: "YES" },
    WeeklyHoliday: { format: "Dropdown / Text", allowed: "Sunday, Monday, Tuesday, Wednesday, Thursday, Friday, Saturday", rule: "Scheduled weekly off day.", example: "Sunday" },
  };

  const getExcelColumnGuideline = (key, label) => {
    if (key === "EmployeeType") {
      const types = employeeTypesList && employeeTypesList.length > 0 ? employeeTypesList : ["Ordinary", "Permanent", "CONTRACTUAL"];
      return {
        name: label || "Employee Type",
        format: "Dropdown / Text",
        allowed: types.join(", "),
        rule: `Employment category. Select from master (${types.join(", ")}). Defaults to 'Ordinary' if blank.`,
        example: types[0] || "Ordinary",
      };
    }

    if (key === "ShiftName" || key === "Shift_ShiftName" || key === "Shift") {
      const sArr = shiftsList && shiftsList.length > 0 ? shiftsList : [];
      return {
        name: label || "Shift Name",
        format: "Dropdown / Text",
        allowed: sArr.length > 0 ? sArr.join(", ") : "Configured Firm Shifts",
        rule: `Work shift for the employee. Select from dropdown or enter Shift Name configured for this firm in Firm Shift Master. Auto-maps to firm shift settings and saves to Shift History (EmployeeShiftL). If blank for new employee, automatically assigns firm default shift.`,
        example: sArr[0] || "Firm Shift Name",
      };
    }

    if (key === "Shift_EffectiveFrom" || key === "ShiftEffectiveFrom" || key === "ShiftEffectiveDate") {
      return {
        name: label || "Shift Effective From",
        format: "Date (DD/MM/YYYY)",
        allowed: "DD/MM/YYYY (e.g. 01/04/2024)",
        rule: "Effective date from which this shift assignment becomes active. For new employees, if left blank, automatically inherits Date of Joining (DOJ).",
        example: "01/04/2024",
      };
    }

    if (key && EXCEL_COLUMN_GUIDELINES[key]) {
      return { ...EXCEL_COLUMN_GUIDELINES[key], name: label || key };
    }

    // Dynamic Allowances
    if (key && key.startsWith("Allow_")) {
      if (key.endsWith("_Amount")) {
        return {
          name: label || key,
          format: "Currency / Number",
          allowed: "Positive numeric value (e.g. 1500, 2000)",
          rule: `Wage rate or amount for ${label || "this allowance"}.`,
          example: "1500",
        };
      }
      if (key.endsWith("_Calculation")) {
        return {
          name: label || key,
          format: "Dropdown / Code",
          allowed: "M, P, D, F",
          rule: "Calculation basis: M = Monthly Rate, P = Percentage, D = Daily Rate, F = Fixed Amount.",
          example: "M",
        };
      }
      if (key.endsWith("_EffectiveFrom")) {
        return {
          name: label || key,
          format: "Date (DD/MM/YYYY)",
          allowed: "Valid date (e.g. 2024-01-01)",
          rule: "Effective date for this allowance. Blank defaults to Date of Joining.",
          example: "01/01/2024",
        };
      }
      if (
        key.endsWith("_IsOnGross") ||
        key.endsWith("_IsPFApply") ||
        key.endsWith("_IsESIApply") ||
        key.endsWith("_IsRoundOffApply") ||
        key.endsWith("_IsWorkingDaysApply") ||
        key.endsWith("_IsApplicable")
      ) {
        return {
          name: label || key,
          format: "Dropdown (YES / NO)",
          allowed: "YES, NO",
          rule: `Flag determining if this setting applies to ${label || "allowance"}: YES or NO.`,
          example: "YES",
        };
      }
      return {
        name: label || key,
        format: "Text / Value",
        allowed: "Valid value for allowance",
        rule: `Setting value for ${label || key}.`,
        example: "",
      };
    }

    // Dynamic Deductions
    if (key && key.startsWith("Ded_")) {
      if (key.endsWith("_Amount")) {
        return {
          name: label || key,
          format: "Currency / Number",
          allowed: "Positive numeric value (e.g. 500)",
          rule: `Deduction rate or monthly amount for ${label || "this deduction"}.`,
          example: "500",
        };
      }
      if (key.endsWith("_Calculation")) {
        return {
          name: label || key,
          format: "Dropdown / Code",
          allowed: "M, P, D, F",
          rule: "Calculation basis: M = Monthly, P = Percentage, D = Daily, F = Fixed.",
          example: "M",
        };
      }
      if (key.endsWith("_EffectiveFrom")) {
        return {
          name: label || key,
          format: "Date (DD/MM/YYYY)",
          allowed: "Valid date (e.g. 2024-01-01)",
          rule: "Effective date for this deduction. Blank defaults to Date of Joining.",
          example: "01/01/2024",
        };
      }
      if (
        key.endsWith("_IsOnGross") ||
        key.endsWith("_IsPFApply") ||
        key.endsWith("_IsESIApply") ||
        key.endsWith("_IsRoundOffApply") ||
        key.endsWith("_IsWorkingDaysApply") ||
        key.endsWith("_IsApplicable")
      ) {
        return {
          name: label || key,
          format: "Dropdown (YES / NO)",
          allowed: "YES, NO",
          rule: `Flag determining if this setting applies to ${label || "deduction"}: YES or NO.`,
          example: "YES",
        };
      }
      return {
        name: label || key,
        format: "Text / Value",
        allowed: "Valid value for deduction",
        rule: `Setting value for ${label || key}.`,
        example: "",
      };
    }

    // Generic dates
    if (key && (key.includes("Date") || key.includes("From") || key.includes("Till") || key.includes("DOB") || key.includes("DOJ"))) {
      return {
        name: label || key,
        format: "Date (DD/MM/YYYY)",
        allowed: "Valid date (e.g. 2024-01-01)",
        rule: "Date in YYYY-MM-DD format. Leave blank if not applicable.",
        example: "01/01/2024",
      };
    }

    // Generic boolean flags
    if (
      key &&
      (key.startsWith("Is") ||
        key.endsWith("Applicable") ||
        key.endsWith("Apply") ||
        ["Refunded", "TICs", "PICs", "Accident"].includes(key))
    ) {
      return {
        name: label || key,
        format: "Dropdown (YES / NO)",
        allowed: "YES, NO",
        rule: "Boolean flag: YES or NO.",
        example: "YES",
      };
    }

    // Default fallback
    return {
      name: label || key,
      format: "Text",
      allowed: "Valid text or number",
      rule: `Enter appropriate value for ${label || key}.`,
      example: "",
    };
  };

  const getDropdownFormula = (key) => {
    if (!key) return null;
    if (key === "Sex") return '"M,F,Other"';
    if (key === "EmployeeType") {
      const types = employeeTypesList && employeeTypesList.length > 0 ? employeeTypesList : ["Ordinary", "Permanent", "CONTRACTUAL"];
      return `"${types.join(",")}"`;
    }
    if (key === "ShiftName" || key === "Shift_ShiftName" || key === "Shift") {
      const shifts = shiftsList && shiftsList.length > 0 ? shiftsList : [];
      return shifts.length > 0 ? `"${shifts.join(",")}"` : null;
    }
    if (key === "Status" || key === "WorkingStatus") return '"Working,Left,Suspended,Retired"';
    if (key === "Married") return '"YES,NO"';
    if (key === "PaymentMode") return '"Bank,Cash,Cheque,NEFT,RTGS"';
    if (key === "Sal_BasicType" || key === "Sal_BasicTypeName") return '"M,D,H,P"';
    if (key === "Sal_Period") return '"Monthly,Daily,Weekly,Fortnightly"';
    if (key === "WeeklyHoliday") return '"Sunday,Monday,Tuesday,Wednesday,Thursday,Friday,Saturday"';
    if (key === "PFCalculationType" || key.endsWith("_Calculation")) return '"M,P,D,F"';
    if (
      [
        "PFApplicable", "ESIApplicable", "PensionApplicable", "IsDeferredPension",
        "EmplrPFOverLimit", "EmpleeDedOverLimit", "PensionOverLimit", "HigherPension",
        "OldMemberPF", "OldMemberPension", "PMRPY", "PMGKY", "ABRY",
        "BankPayment", "IsHandicap", "Refunded", "TICs", "PICs", "Accident",
        "ESIOldEmployee", "IsInternationalEmployee", "Sal_RoundOff", "Sal_WorkingDays"
      ].includes(key) ||
      key.startsWith("Is") ||
      key.endsWith("_IsOnGross") ||
      key.endsWith("_IsPFApply") ||
      key.endsWith("_IsESIApply") ||
      key.endsWith("_IsRoundOffApply") ||
      key.endsWith("_IsWorkingDaysApply") ||
      key.endsWith("_IsApplicable")
    ) {
      return '"YES,NO"';
    }
    return null;
  };

  // ──────────────────────────────────────────
  // Column Header Formatter with Bracket Guidelines (Format / Allowed Values)
  // ──────────────────────────────────────────
  const getFormattedColumnHeader = (key, label) => {
    let baseLabel = (label || key || "").trim();
    // Strip preexisting trailing brackets if present
    baseLabel = baseLabel.replace(/\s*\([^)]*\)$/, "").trim();

    // 1. Employee Type (Dynamic database values)
    if (key === "EmployeeType") {
      const types = employeeTypesList && employeeTypesList.length > 0 ? employeeTypesList : ["Ordinary", "Permanent", "CONTRACTUAL"];
      return `${baseLabel} (${types.join("/")})`;
    }

    // 1b. Shift Name (Dynamic database values)
    if (key === "ShiftName" || key === "Shift_ShiftName" || key === "Shift") {
      if (shiftsList && shiftsList.length > 0) {
        const sList = shiftsList.slice(0, 3).join("/");
        return `${baseLabel} (${sList})`;
      }
      return baseLabel;
    }

    // 2. Gender
    if (key === "Sex" || key === "Gender") {
      return `${baseLabel} (Male/Female/Other)`;
    }

    // 3. Status & Working Status
    if (key === "Status" || key === "WorkingStatus") {
      return `${baseLabel} (Working/Left/Suspended/Retired)`;
    }

    // 4. Marital Status
    if (key === "Married") {
      return `${baseLabel} (Married/Unmarried)`;
    }

    // 5. Weekly Holiday
    if (key === "WeeklyHoliday") {
      return `${baseLabel} (Sunday/Monday/...)`;
    }

    // 6. Payment Mode
    if (key === "PaymentMode") {
      return `${baseLabel} (Bank/Cash/Cheque)`;
    }

    // 7. Dates (format YYYY-MM-DD)
    if (
      key === "DateOfBirth" || key === "DOB" ||
      key === "DateOfRetire" || key === "DOC" ||
      key === "JoinDate" || key === "DOJ" ||
      key === "LeftDate" || key === "DOL" ||
      key === "ResignDate" ||
      key === "Dept_EffectiveDate" ||
      key === "Shift_EffectiveFrom" ||
      key === "Sal_EffectiveDate" || key === "Sal_EffectiveFrom" ||
      key === "ESIJoiningDate" || key === "ESIEffectiveDate" ||
      key === "PFJoinDate" || key === "PFEffectiveDate" ||
      key === "DLExpiryDate" || key === "PassportFrom" || key === "PassportTill" ||
      key === "RefundFileDate" || key === "GratuityPaidDate" ||
      key === "DateOfExitPr" ||
      key.endsWith("_EffectiveFrom") ||
      key.endsWith("_EffectiveDate") ||
      (key.includes("Date") && !key.includes("Format"))
    ) {
      return `${baseLabel} (DD/MM/YYYY)`;
    }

    // 8. Contact & KYC specifics
    if (key === "MobileNo" || key === "Mobile" || key === "Phone") {
      return `${baseLabel} (10 Digits)`;
    }
    if (key === "PanCardNo" || key === "PanNo") {
      return `${baseLabel} (10 Chars: ABCDE1234F)`;
    }
    if (key === "AadharNumber" || key === "AdharNo" || key === "AadharCardNo") {
      return `${baseLabel} (12 Digits)`;
    }
    if (key === "UAN" || key === "OldUAN" || key === "UANNo") {
      return `${baseLabel} (12 Digits)`;
    }
    if (key === "ESINo") {
      return `${baseLabel} (17 Digits)`;
    }
    if (key === "IFSCCode") {
      return `${baseLabel} (11 Chars: SBIN0001234)`;
    }
    if (key === "PresentPinCode" || key === "PermanentPinCode" || key === "PinCode") {
      return `${baseLabel} (6 Digits)`;
    }
    if (key === "Email") {
      return `${baseLabel} (user@example.com)`;
    }
    if (key === "PensionAge") {
      return `${baseLabel} (Years: e.g. 58)`;
    }

    // 9. Identifiers
    if (key === "EmployeeCode") {
      return `${baseLabel} (Unique Code / Text)`;
    }
    if (key === "Name") {
      return `${baseLabel} (Required Full Name)`;
    }
    if (key === "SerialNo") {
      return `${baseLabel} (Number)`;
    }
    if (key === "PFNo" || key === "PrevPFNo") {
      return `${baseLabel} (PF Number / Text)`;
    }
    if (key === "BankAccountNo" || key === "AccountNo") {
      return `${baseLabel} (Account Number)`;
    }

    // 10. Calculations & Rates
    if (key === "PFCalculationType" || key === "Sal_Calculation" || key.endsWith("_Calculation")) {
      return `${baseLabel} (M/P/D/F)`;
    }
    if (key === "Sal_BasicType" || key === "Sal_BasicTypeName") {
      return `${baseLabel} (M/D/H/P)`;
    }
    if (key === "Sal_Period") {
      return `${baseLabel} (Monthly/Daily/Weekly)`;
    }

    // 11. Salaries & Amounts
    if (
      key === "Sal_BasicSalary" || key === "Sal_GradePay" ||
      key === "ExtraPF" || key === "FixedPFSalary" ||
      key.endsWith("_Amount")
    ) {
      return `${baseLabel} (Amount / Number)`;
    }

    // 12. Yes / No Booleans
    if (
      [
        "PFApplicable", "ESIApplicable", "PensionApplicable", "IsDeferredPension",
        "EmplrPFOverLimit", "EmpleeDedOverLimit", "PensionOverLimit", "HigherPension",
        "OldMemberPF", "OldMemberPension", "PMRPY", "PMGKY", "ABRY",
        "BankPayment", "IsHandicap", "Refunded", "TICs", "PICs", "Accident",
        "ESIOldEmployee", "IsInternationalEmployee", "Sal_RoundOff", "Sal_WorkingDays"
      ].includes(key) ||
      key.endsWith("_IsOnGross") ||
      key.endsWith("_IsPFApply") ||
      key.endsWith("_IsESIApply") ||
      key.endsWith("_IsRoundOffApply") ||
      key.endsWith("_IsWorkingDaysApply") ||
      key.endsWith("_IsApplicable")
    ) {
      return `${baseLabel} (YES/NO)`;
    }

    // 13. Masters
    if (
      [
        "DepartmentName", "DesignationName", "BranchName", "CategoryName",
        "ShiftName", "ReasonOfLeft", "ReasonToLeft", "JoinTypeName", "HandicapCategory"
      ].includes(key)
    ) {
      return `${baseLabel} (Master / Text)`;
    }

    // 14. Fallback from guideline dictionary if available
    const guideline = getExcelColumnGuideline(key, baseLabel);
    if (guideline?.allowed && guideline.allowed.length <= 25 && !guideline.allowed.includes("e.g.")) {
      return `${baseLabel} (${guideline.allowed})`;
    } else if (guideline?.format && guideline.format.length <= 25) {
      return `${baseLabel} (${guideline.format.replace(/^(Text\s*\/\s*|Dropdown\s*\/\s*)/i, "")})`;
    }

    return `${baseLabel} (Text)`;
  };

  const enrichWorkbookWithGuidelines = (workbook, mainSheet, activeColumns, isTemplate = false, rowCount = 0) => {
    if (!mainSheet || !activeColumns || activeColumns.length === 0) return;

    const headerRow = mainSheet.getRow(1);

    // 1. Add Cell Notes & Data Validation Dropdowns to Main Sheet
    activeColumns.forEach((col, colIdx) => {
      const colNum = colIdx + 1;
      const headerCell = headerRow.getCell(colNum);
      const info = getExcelColumnGuideline(col.key, col.label);

      const formattedHeader = getFormattedColumnHeader(col.key, col.label);

      // Build rich hover note text
      const noteLines = [
        `📌 ${formattedHeader}`,
        `• Format: ${info.format}`,
        `• Allowed: ${info.allowed}`,
        `• Rule: ${info.rule}`,
        info.example ? `• Example: ${info.example}` : "",
      ].filter(Boolean);

      headerCell.note = noteLines.join("\n");

      // Check if column has a dropdown list formula
      const dropdownFormula = getDropdownFormula(col.key);
      if (dropdownFormula) {
        // For template: apply validation to first 500 rows
        // For data export: apply validation to existing rows + 50 extra rows
        const maxRow = isTemplate ? 500 : Math.max(rowCount + 50, 100);
        for (let r = 2; r <= maxRow; r++) {
          const cell = mainSheet.getRow(r).getCell(colNum);
          cell.dataValidation = {
            type: "list",
            allowBlank: true,
            formulae: [dropdownFormula],
            showInputMessage: false,
            showErrorMessage: true,
            errorTitle: "Invalid Input",
            error: `Please select a valid option from the dropdown for ${col.label}.`,
          };
        }
      }
    });

    // 2. Add Dedicated "Guidelines & Instructions" Worksheet
    const guideSheet = workbook.addWorksheet("Guidelines & Instructions", {
      views: [{ showGridLines: true }],
    });

    guideSheet.columns = [
      { header: "S.No", key: "sno", width: 8 },
      { header: "Column Header", key: "header", width: 28 },
      { header: "Field Key", key: "key", width: 24 },
      { header: "Format / Type", key: "format", width: 22 },
      { header: "Allowed Values / Options", key: "allowed", width: 32 },
      { header: "Rules & Guidelines", key: "rule", width: 50 },
      { header: "Example Value", key: "example", width: 20 },
    ];

    // Header styling
    const guideHeader = guideSheet.getRow(1);
    guideHeader.height = 30;
    guideHeader.eachCell((cell) => {
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FF1E3A8A" }, // Dark Navy Blue
      };
      cell.font = { bold: true, color: { argb: "FFFFFFFF" }, size: 11, name: "Calibri" };
      cell.alignment = { vertical: "middle", horizontal: "center", wrapText: true };
      cell.border = {
        top: { style: "thin", color: { argb: "FF93C5FD" } },
        bottom: { style: "medium", color: { argb: "FF1D4ED8" } },
        left: { style: "thin", color: { argb: "FF93C5FD" } },
        right: { style: "thin", color: { argb: "FF93C5FD" } },
      };
    });

    activeColumns.forEach((col, idx) => {
      const info = getExcelColumnGuideline(col.key, col.label);
      const formattedHeader = getFormattedColumnHeader(col.key, col.label);
      const row = guideSheet.addRow({
        sno: idx + 1,
        header: formattedHeader,
        key: col.key,
        format: info.format,
        allowed: info.allowed,
        rule: info.rule,
        example: info.example || "-",
      });

      const isEven = idx % 2 === 0;
      const bg = isEven ? "FFFFFFFF" : "FFF8FAFC";
      row.eachCell((cell, cNum) => {
        cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: bg } };
        cell.alignment = {
          vertical: "middle",
          horizontal: cNum === 1 ? "center" : "left",
          wrapText: true,
        };
        cell.font = { size: 10, name: "Calibri" };
        cell.border = {
          top: { style: "thin", color: { argb: "FFE2E8F0" } },
          bottom: { style: "thin", color: { argb: "FFE2E8F0" } },
          left: { style: "thin", color: { argb: "FFE2E8F0" } },
          right: { style: "thin", color: { argb: "FFE2E8F0" } },
        };
      });
      row.height = 24;
    });
  };

  // ──────────────────────────────────────────
  // Direct Export to Excel (All Dynamic Data)
  // ──────────────────────────────────────────
  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await fetch(`${API_WEB_URLS.BASE}EmployeeExport/0/token/${firmId}`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });

      if (!res.ok) {
        throw new Error(`API returned HTTP ${res.status}`);
      }

      const json = await res.json();
      const exportData = json?.data;
      const employees = exportData?.rows || [];
      const columns = exportData?.columns || [];

      if (!employees || employees.length === 0) {
        toast.warning("No employee data found to export.");
        setExporting(false);
        return;
      }

      if (!columns || columns.length === 0) {
        toast.warning("No export columns available.");
        setExporting(false);
        return;
      }

      // Filter to user-selected columns
      let activeColumns = columns;
      if (selectedColumnKeys && selectedColumnKeys.length > 0) {
        activeColumns = columns.filter((col) => selectedColumnKeys.includes(col.key));
        if (!activeColumns.some((c) => c.key === "Name")) {
          const nameCol = columns.find((c) => c.key === "Name");
          if (nameCol) activeColumns.unshift(nameCol);
        }
      }

      // Build workbook
      const workbook = new ExcelJS.Workbook();
      workbook.creator = "Payroll System";
      workbook.created = new Date();

      const firmName = selectedFirm?.FirmName || selectedFirm?.Name
        ? (selectedFirm.FirmName || selectedFirm.Name).replace(/[^a-zA-Z0-9_-]/g, "_")
        : "Employees";
      const sheet = workbook.addWorksheet("Employees", {
        views: [{ state: "frozen", ySplit: 1 }],
      });

      // Column definitions with brackets indicating format / allowed values
      sheet.columns = activeColumns.map((col) => {
        const formattedHeader = getFormattedColumnHeader(col.key, col.label);
        return {
          header: formattedHeader,
          key: col.key,
          width: Math.max(formattedHeader.length + 3, 18),
        };
      });

      // Style header row
      const headerRow = sheet.getRow(1);
      headerRow.eachCell((cell) => {
        cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF4F46E5" } };
        cell.font = { bold: true, color: { argb: "FFFFFFFF" }, size: 11, name: "Calibri" };
        cell.alignment = { vertical: "middle", horizontal: "center", wrapText: true };
        cell.border = {
          top: { style: "thin", color: { argb: "FFCCCCCC" } },
          left: { style: "thin", color: { argb: "FFCCCCCC" } },
          bottom: { style: "thin", color: { argb: "FFCCCCCC" } },
          right: { style: "thin", color: { argb: "FFCCCCCC" } },
        };
      });
      headerRow.height = 36;

      // Add data rows
      employees.forEach((emp, idx) => {
        const rowData = {};
        activeColumns.forEach((col) => {
          let val = emp[col.key];

          // Format booleans (YES / NO)
          if (
            col.type === "boolean" ||
            [
              "PFApplicable", "ESIApplicable", "PensionApplicable", "IsDeferredPension",
              "EmplrPFOverLimit", "EmpleeDedOverLimit", "PensionOverLimit", "HigherPension",
              "OldMemberPF", "OldMemberPension", "PMRPY", "PMGKY", "ABRY",
              "BankPayment", "IsHandicap", "Refunded", "TICs", "PICs", "Accident",
              "ESIOldEmployee", "IsInternationalEmployee",
            ].includes(col.key) ||
            col.key.endsWith("_IsOnGross") ||
            col.key.endsWith("_IsPFApply") ||
            col.key.endsWith("_IsESIApply") ||
            col.key.endsWith("_IsRoundOffApply") ||
            col.key.endsWith("_IsWorkingDaysApply") ||
            col.key.endsWith("_IsApplicable")
          ) {
            if (val !== null && val !== undefined && val !== "") {
              val = fmtBool(val);
            } else {
              val = "";
            }
          }
          // Format gender (M / F)
          else if (col.key === "Sex") {
            val = normalizeGender(val);
          }
          // Format calculation type (M / P / D / F)
          else if (col.key === "PFCalculationType" || col.key.endsWith("_Calculation")) {
            val = normalizeCalcCode(val);
          }
          // Format basic type (M / D / H / P)
          else if (
            col.key === "Sal_BasicType" ||
            col.key === "Sal_BasicTypeName" ||
            col.key === "BasicType" ||
            col.key === "BasicTypeName"
          ) {
            val = normalizeBasicTypeCode(val);
          }
          // Format dates (DD/MM/YYYY)
          else if (
            col.type === "date" ||
            col.key.includes("Date") ||
            col.key.includes("From") ||
            col.key.includes("Till") ||
            col.key.endsWith("_EffectiveFrom")
          ) {
            val = safeDate(val);
          }

          rowData[col.key] = val ?? "";
        });
        const row = sheet.addRow(rowData);

        // Alternating row colors
        const bgColor = idx % 2 === 0 ? "FFFFFFFF" : "FFF9FAFB";
        row.eachCell((cell) => {
          cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: bgColor } };
          cell.alignment = { vertical: "middle", horizontal: "left" };
          cell.border = {
            top: { style: "thin", color: { argb: "FFE5E7EB" } },
            left: { style: "thin", color: { argb: "FFE5E7EB" } },
            bottom: { style: "thin", color: { argb: "FFE5E7EB" } },
            right: { style: "thin", color: { argb: "FFE5E7EB" } },
          };
        });
        row.height = 22;
      });

      // Auto-fit columns (max 50)
      sheet.columns.forEach((col) => {
        let maxLen = col.header ? col.header.length : 10;
        col.eachCell({ includeEmpty: false }, (cell) => {
          const len = cell.value ? String(cell.value).length : 0;
          if (len > maxLen) maxLen = len;
        });
        col.width = Math.min(Math.max(maxLen + 4, 12), 50);
      });

      // Enrich with hover notes, dropdown data validations, and Guidelines worksheet tab
      enrichWorkbookWithGuidelines(workbook, sheet, activeColumns, false, employees.length);

      // Save file
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const dateStr = new Date().toISOString().slice(0, 10);
      saveAs(blob, `Employees_Export_${firmName}_${dateStr}.xlsx`);

      toast.success(`✅ Exported ${employees.length} employees with ${activeColumns.length} columns successfully!`);
    } catch (err) {
      console.error("Export error:", err);
      toast.error("Export failed. Please check network/API.");
    } finally {
      setExporting(false);
    }
  };

  // ──────────────────────────────────────────
  // Template Download (Dynamic Headings, No Data Rows)
  // ──────────────────────────────────────────
  const handleDownloadTemplate = async () => {
    setDownloadingTemplate(true);
    try {
      let columns = exportColumns;

      // If columns not yet in state and firmId is selected, fetch fresh columns from export API for this firm
      if ((!columns || columns.length === 0) && firmId && firmId !== "0") {
        try {
          const res = await fetch(`${API_WEB_URLS.BASE}EmployeeExport/0/token/${firmId}`, {
            headers: { Authorization: `Bearer ${authToken}` },
          });
          if (res.ok) {
            const json = await res.json();
            if (json?.data?.columns && json.data.columns.length > 0) {
              columns = json.data.columns;
              setExportColumns(columns);
            }
          }
        } catch (e) {
          console.warn("Could not fetch firm export columns for template, using default fields:", e);
        }
      }

      // Filter columns based on user selection
      let activeColumns = columns;
      if (selectedColumnKeys && selectedColumnKeys.length > 0) {
        activeColumns = columns.filter((col) => selectedColumnKeys.includes(col.key));
        if (!activeColumns.some((c) => c.key === "Name")) {
          const nameCol = columns.find((c) => c.key === "Name");
          if (nameCol) activeColumns.unshift(nameCol);
        }
      }

      const workbook = new ExcelJS.Workbook();
      workbook.creator = "Payroll System";
      workbook.created = new Date();

      const sheet = workbook.addWorksheet("Employee Template", {
        views: [{ state: "frozen", ySplit: 1 }],
      });

      let templateCols = activeColumns;
      if (templateCols && templateCols.length > 0) {
        sheet.columns = templateCols.map((col) => {
          const formattedHeader = getFormattedColumnHeader(col.key, col.label);
          return {
            header: formattedHeader,
            key: col.key,
            width: Math.max(formattedHeader.length + 3, 18),
          };
        });
      } else {
        const fallback = IMPORT_FLAT_FIELDS.filter(
          (f) => !selectedColumnKeys || selectedColumnKeys.length === 0 || selectedColumnKeys.includes(f.key)
        );
        templateCols = fallback;
        sheet.columns = fallback.map((f) => {
          const formattedHeader = getFormattedColumnHeader(f.key, f.label);
          return {
            header: formattedHeader,
            key: f.key,
            width: Math.max(formattedHeader.length + 3, 22),
          };
        });
      }

      // Style header row
      const headerRow = sheet.getRow(1);
      headerRow.eachCell((cell) => {
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "FF1D4ED8" }, // Clean Professional Blue
        };
        cell.font = { bold: true, color: { argb: "FFFFFFFF" }, size: 11, name: "Calibri" };
        cell.alignment = { vertical: "middle", horizontal: "center", wrapText: true };
        cell.border = {
          top: { style: "thin", color: { argb: "FFCCCCCC" } },
          left: { style: "thin", color: { argb: "FFCCCCCC" } },
          bottom: { style: "thin", color: { argb: "FFCCCCCC" } },
          right: { style: "thin", color: { argb: "FFCCCCCC" } },
        };
      });
      headerRow.height = 36;

      // Enrich template with hover notes on headers, dropdown lists on input cells, and Guidelines & Instructions sheet tab
      enrichWorkbookWithGuidelines(workbook, sheet, templateCols, true, 0);

      // Note: NO data rows or sample rows are added - ONLY headings as requested!

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const firmName = selectedFirm?.FirmName || selectedFirm?.Name
        ? (selectedFirm.FirmName || selectedFirm.Name).replace(/[^a-zA-Z0-9_-]/g, "_")
        : "Firm";

      saveAs(blob, `Employee_Import_Template_${firmName}.xlsx`);
      toast.success(`📄 Template downloaded with ${sheet.columns.length} selected column headings (ready for data entry)!`);
    } catch (err) {
      console.error("Template download error:", err);
      toast.error("Failed to download template.");
    } finally {
      setDownloadingTemplate(false);
    }
  };

  // ──────────────────────────────────────────
  // Import - Parse Excel File
  // ──────────────────────────────────────────
  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const ext = file.name.split(".").pop().toLowerCase();
    if (!["xlsx", "xls"].includes(ext)) {
      toast.error("Invalid file! Please upload an Excel file (.xlsx or .xls)");
      e.target.value = "";
      return;
    }

    setImportFile(file);
    setIsParsing(true);
    setPreviewData([]);
    setShowPreview(false);

    try {
      // Fetch fresh existing employees if not loaded yet
      // Fetch fresh existing employees from server for the active firm
      // Fetch fresh existing employees, allowances, deductions and export columns for active firm
      let currentExisting = existingEmployees;
      let currentAllowances = firmAllowancesList;
      let currentDeductions = firmDeductionsList;
      let currentColumns = exportColumns;

      if (firmId && firmId !== "0") {
        try {
          const freshRes = await fetch(`${API_WEB_URLS.BASE}EmployeeExport/0/token/${firmId}`, {
            headers: { Authorization: `Bearer ${authToken}` },
          });
          if (freshRes.ok) {
            const freshJson = await freshRes.json();
            if (freshJson?.data) {
              if (freshJson.data.rows) {
                currentExisting = freshJson.data.rows;
                setExistingEmployees(currentExisting);
              }
              if (Array.isArray(freshJson.data.allowances)) {
                currentAllowances = freshJson.data.allowances;
                setFirmAllowancesList(currentAllowances);
              }
              if (Array.isArray(freshJson.data.deductions)) {
                currentDeductions = freshJson.data.deductions;
                setFirmDeductionsList(currentDeductions);
              }
              if (Array.isArray(freshJson.data.columns)) {
                currentColumns = freshJson.data.columns;
                setExportColumns(currentColumns);
              }
            }
          }
        } catch (fetchErr) {
          console.warn("Could not fetch fresh firm metadata on file parse:", fetchErr);
        }
      }

      const arrayBuffer = await file.arrayBuffer();
      const workbook = new ExcelJS.Workbook();
      await workbook.xlsx.load(arrayBuffer);

      const sheet = workbook.worksheets[0];
      if (!sheet) {
        toast.error("No sheet found in the Excel file.");
        setIsParsing(false);
        return;
      }

      // Read "Guidelines & Instructions" sheet if present to build 100% exact header-to-key mapping
      const gSheet = workbook.getWorksheet("Guidelines & Instructions");
      const guidelineKeyMap = new Map();
      if (gSheet) {
        gSheet.eachRow((r, i) => {
          if (i > 1) {
            // Check Cell 2 (Header) & Cell 3 (Key)
            const c2 = String(r.getCell(2).value || "").trim();
            const c3 = String(r.getCell(3).value || "").trim();
            const c4 = String(r.getCell(4).value || "").trim();

            let h = "", k = "";
            if (c3 && (/^(Allow_|Ded_|Sal_|Join_|Shift_|[A-Z])/i.test(c3) || c3.includes("_"))) {
              h = c2;
              k = c3;
            } else if (c4 && (/^(Allow_|Ded_|Sal_|Join_|Shift_|[A-Z])/i.test(c4) || c4.includes("_"))) {
              h = c3;
              k = c4;
            } else {
              h = c2;
              k = c3;
            }

            if (h && k && !INVALID_GUIDELINE_KEYS.has(k.toLowerCase())) {
              guidelineKeyMap.set(h, k);
              guidelineKeyMap.set(h.toLowerCase().replace(/[_\s]+/g, " ").trim(), k);
              guidelineKeyMap.set(h.replace(/[\s_]+/g, "").toLowerCase(), k);
            }
          }
        });
      }

      // Get headers from first row
      const headers = [];
      sheet.getRow(1).eachCell((cell, colNum) => {
        headers[colNum - 1] = String(cell.value || "").trim();
      });

      // Map labels and direct keys
      const labelToKey = {};
      IMPORT_FLAT_FIELDS.forEach((f) => {
        labelToKey[f.label] = f.key;
        labelToKey[f.key] = f.key;
      });

      // Also map export column labels if available
      if (exportColumns && exportColumns.length > 0) {
        exportColumns.forEach((c) => {
          if (c.label) labelToKey[c.label] = c.key;
          if (c.key) labelToKey[c.key] = c.key;
        });
      }

      // Extended aliases for flexible excel headers
      const HEADER_ALIASES = {
        "employee code": "EmployeeCode", "employeecode": "EmployeeCode", "emp code": "EmployeeCode", "emp. code": "EmployeeCode",
        "card no": "EmployeeCode", "cardno": "EmployeeCode", "card": "EmployeeCode", "code": "EmployeeCode",
        "serial no": "SerialNo", "serialno": "SerialNo", "sno": "SerialNo", "s.no": "SerialNo",
        "employee name": "Name", "emp name": "Name", "emp. name": "Name", "name": "Name",
        "father name": "FatherName", "father's name": "FatherName", "father’s name": "FatherName",
        "fathers name": "FatherName", "fathername": "FatherName", "father": "FatherName",
        "f/name": "FatherName", "f name": "FatherName", "f.name": "FatherName", "fname": "FatherName",
        "father / husband name": "FatherName", "father/husband name": "FatherName",
        "father / husband": "FatherName", "father/husband": "FatherName",
        "father name / husband name": "FatherName", "father or husband name": "FatherName",
        "guardian name": "FatherName", "guardian": "FatherName", "parent name": "FatherName",
        "fatherhusbandname": "FatherName", "fatherhusband": "FatherName",
        "husband name": "HusbandName", "husband's name": "HusbandName", "husbandname": "HusbandName",
        "mother name": "MotherName", "mother's name": "MotherName",
        "gender": "Sex", "sex": "Sex",
        "date of birth": "DateOfBirth", "dob": "DateOfBirth", "dateofbirth": "DateOfBirth", "birth date": "DateOfBirth",
        "date of retirement": "DateOfRetire", "date of retire": "DateOfRetire", "retire date": "DateOfRetire",
        "employee type": "EmployeeType", "emp type": "EmployeeType", "emp. type": "EmployeeType", "emptype": "EmployeeType",
        "employment type": "EmployeeType",
        "status": "Status", "working status": "Status", "workingstatus": "Status", "work status": "Status", "workstatus": "Status",
        "marital status": "Married", "maritalstatus": "Married", "married": "Married",
        "weekly holiday": "WeeklyHoliday", "weeklyholiday": "WeeklyHoliday", "weekly off": "WeeklyHoliday",
        "mobile": "MobileNo", "mobile no": "MobileNo", "mobile number": "MobileNo", "mobileno": "MobileNo", "phone": "MobileNo", "phone no": "MobileNo",
        "email": "Email", "email id": "Email", "emailid": "Email",
        "permanent address": "PermanentAddress", "permanentaddress": "PermanentAddress",
        "present address": "PresentAddress", "presentaddress": "PresentAddress", "address": "PresentAddress",
        "remark": "Remark", "remarks": "Remark",
        "education qualification": "EducationQualification", "education": "EducationQualification", "qualification": "EducationQualification",

        // Department & Designation & Branch
        "department": "DepartmentName", "department name": "DepartmentName", "dept": "DepartmentName", "dept name": "DepartmentName",
        "latest department": "DepartmentName", "dept_departmentname": "DepartmentName", "deptdepartmentname": "DepartmentName",
        "designation": "DesignationName", "designation name": "DesignationName", "desig": "DesignationName", "desig name": "DesignationName",
        "latest designation": "DesignationName", "dept_designationname": "DesignationName", "deptdesignationname": "DesignationName",
        "dept effective date": "Dept_EffectiveDate", "dept_effectivedate": "Dept_EffectiveDate", "depteffectivedate": "Dept_EffectiveDate",
        "branch": "BranchName", "branch name": "BranchName", "branchname": "BranchName",

        // Shift & Shift History
        "shift": "ShiftName", "shift name": "ShiftName", "latest shift": "ShiftName",
        "shift timing": "ShiftName", "shift timings": "ShiftName", "shift code": "ShiftName",
        "shiftmaster": "ShiftName", "shift master": "ShiftName",
        "shift_shiftname": "ShiftName", "shiftshiftname": "ShiftName",
        "shift effective from": "Shift_EffectiveFrom", "shift_effectivefrom": "Shift_EffectiveFrom", "shifteffectivefrom": "Shift_EffectiveFrom",
        "latest shift effective from": "Shift_EffectiveFrom", "shift effective date": "Shift_EffectiveFrom",

        // Joining & Leaving History
        "join date": "JoinDate", "latest join date": "JoinDate", "joining date": "JoinDate", "date of joining": "JoinDate",
        "doj": "JoinDate", "joindate": "JoinDate", "join_joindate": "JoinDate", "joinjoindate": "JoinDate",
        "join type": "JoinTypeName", "join type name": "JoinTypeName", "join_jointype": "JoinTypeName", "join_jointypename": "JoinTypeName",
        "left date": "LeftDate", "date of left": "LeftDate", "date of leaving": "LeftDate", "leaving date": "LeftDate", "dol": "LeftDate", "leftdate": "LeftDate", "join_leftdate": "LeftDate",
        "reason of left": "ReasonOfLeft", "reason to left": "ReasonOfLeft", "reason for left": "ReasonOfLeft", "reason for leaving": "ReasonOfLeft",
        "left reason": "ReasonOfLeft", "leaving reason": "ReasonOfLeft", "reason": "ReasonOfLeft", "join_reasonofleft": "ReasonOfLeft",

        // Salary & Wages
        "basic salary": "Sal_BasicSalary", "basicsalary": "Sal_BasicSalary", "basic": "Sal_BasicSalary", "sal_basicsalary": "Sal_BasicSalary",
        "basic type": "Sal_BasicTypeName", "basic type name": "Sal_BasicTypeName", "sal_basictype": "Sal_BasicTypeName", "sal_basictypename": "Sal_BasicTypeName",
        "basictype": "Sal_BasicTypeName", "basictypename": "Sal_BasicTypeName", "basic calculation type": "Sal_BasicTypeName",
        "rate of wage": "Sal_BasicTypeName", "rate of wages": "Sal_BasicTypeName",
        "f_basictypemaster": "Sal_F_BasicTypeMaster", "sal_f_basictypemaster": "Sal_F_BasicTypeMaster",
        "grade pay": "Sal_GradePay", "gradepay": "Sal_GradePay", "sal_gradepay": "Sal_GradePay",
        "salary effective date": "Sal_EffectiveDate", "sal_effectivedate": "Sal_EffectiveDate", "saleffectivedate": "Sal_EffectiveDate",
        "pay mode": "Sal_PayMode", "payment mode": "PaymentMode",

        // PF & ESI Compliance
        "pf applicable": "PFApplicable", "pf apply": "PFApplicable", "pf deduction": "PFApplicable", "pfapplicable": "PFApplicable",
        "pf no": "PFNo", "pf number": "PFNo", "pfno": "PFNo",
        "uan": "UAN", "uan no": "UAN", "uanno": "UAN", "uan number": "UAN",
        "pf join date": "PFJoinDate", "pfjoindate": "PFJoinDate",
        "pf calculation": "PFCalculationType", "pf calculation type": "PFCalculationType", "pfcalculationtype": "PFCalculationType",
        "extra pf": "ExtraPF", "extrapf": "ExtraPF",
        "employer pf over limit": "EmplrPFOverLimit", "emplr pf over limit": "EmplrPFOverLimit", "emplrpfoverlimit": "EmplrPFOverLimit",
        "employee ded over limit": "EmpleeDedOverLimit", "emplee ded over limit": "EmpleeDedOverLimit", "empleededoverlimit": "EmpleeDedOverLimit",
        "pension applicable": "PensionApplicable", "pension": "PensionApplicable", "pensionapplicable": "PensionApplicable",
        "pension over limit": "PensionOverLimit", "pensionoverlimit": "PensionOverLimit",
        "higher pension": "HigherPension", "higherpension": "HigherPension",
        "fixed pf salary": "FixedPFSalary", "fixedpfsalary": "FixedPFSalary",
        "old member pf": "OldMemberPF", "oldmemberpf": "OldMemberPF",
        "old member pension": "OldMemberPension", "oldmemberpension": "OldMemberPension",
        "previous pf no": "PrevPFNo", "prev pf no": "PrevPFNo", "prevpfno": "PrevPFNo",
        "old uan": "OldUAN", "olduan": "OldUAN",
        "date of exit prev": "DateOfExitPr", "dateofexitpr": "DateOfExitPr",
        "pmrpy": "PMRPY", "pmgky": "PMGKY", "abry": "ABRY",
        "is deferred pension": "IsDeferredPension", "pension age": "PensionAge",

        // ESI
        "esi applicable": "ESIApplicable", "esi apply": "ESIApplicable", "esi deduction": "ESIApplicable", "esiapplicable": "ESIApplicable",
        "esi no": "ESINo", "esi number": "ESINo", "esino": "ESINo",
        "esi join date": "ESIJoiningDate", "esi joining date": "ESIJoiningDate", "esijoiningdate": "ESIJoiningDate",
        "esi old employee": "ESIOldEmployee", "tics": "TICs", "pics": "PICs", "accident": "Accident",

        // Bank & KYC
        "bank name": "BankName", "bank branch": "BankBranch",
        "bank account": "BankAccountNo", "bank account no": "BankAccountNo", "bank account number": "BankAccountNo",
        "account no": "BankAccountNo", "account number": "BankAccountNo", "acc no": "BankAccountNo",
        "bankaccno": "BankAccountNo", "bankaccountno": "BankAccountNo",
        "name as per bank": "NameAsBank", "name as bank": "NameAsBank",
        "ifsc": "IFSCCode", "ifsc code": "IFSCCode", "bank ifsc": "IFSCCode", "bankifsc": "IFSCCode", "ifsccode": "IFSCCode",
        "bank payment": "BankPayment", "bank payment mode": "BankPayment", "bankpayment": "BankPayment",
        "pan": "PanCardNo", "pan no": "PanCardNo", "pan card": "PanCardNo", "pan card no": "PanCardNo", "pancardno": "PanCardNo",
        "name as per pan": "NameAsPerPan",
        "aadhar": "AadharNumber", "aadhar no": "AadharNumber", "aadhar number": "AadharNumber", "aadharnumber": "AadharNumber", "aadharcardno": "AadharNumber",
        "aadhaar": "AadharNumber", "aadhaar no": "AadharNumber", "aadhaarnumber": "AadharNumber",
        "name in aadhar": "NameInAadhar", "name as per aadhar": "NameInAadhar",
        "driving licence no": "DrivingLicenceNo", "dl no": "DrivingLicenceNo", "dl expiry date": "DLExpiryDate", "name as per dl": "NameAsPerDL",
        "passport no": "PassportNo", "passport valid from": "PassportFrom", "passport valid till": "PassportTill", "name as per passport": "PassportName",
        "is international employee": "IsInternationalEmployee", "country origin": "CountryOrigin", "nationality": "Nationality", "country name": "CountryName",
        "voter id no": "VoterIDNo", "ration card no": "RationCardNo",
        "is handicap": "IsHandicap", "handicap": "IsHandicap", "handicap category": "HandicapCategory",
        "refund file date": "RefundFileDate", "refunded": "Refunded", "gratuity paid date": "GratuityPaidDate",
      };

      // Build dynamic matchers from current firm allowances & deductions
      const allowanceMatchers = buildAllowanceMatchers(currentAllowances);
      const deductionMatchers = buildDeductionMatchers(currentDeductions);
      const activeAllowIdSet = new Set((currentAllowances || []).map((a) => Number(a.Id || a.id)));
      const activeDedIdSet = new Set((currentDeductions || []).map((d) => Number(d.Id || d.id)));

      // Header Resolution function: Checks Guidelines Sheet -> Labels -> Aliases -> Dynamic Allowances & Deductions
      const resolveHeaderKey = (headerStr) => {
        if (!headerStr) return "";
        const raw = String(headerStr).trim();

        // 1. Direct from Guidelines Sheet
        if (guidelineKeyMap.has(raw)) return guidelineKeyMap.get(raw);
        const clean = raw.replace(/\s*\([^)]*\)/g, "").trim();
        if (guidelineKeyMap.has(clean)) return guidelineKeyMap.get(clean);

        const normRaw = raw.toLowerCase().replace(/[_\s]+/g, " ").trim();
        const normClean = clean.toLowerCase().replace(/[_\s]+/g, " ").trim();
        const compactClean = clean.replace(/[\s_]+/g, "").toLowerCase();
        const alphaClean = clean.toLowerCase().replace(/[^a-z0-9]/g, "");

        // 2. Direct exact / label match
        if (labelToKey[raw]) return labelToKey[raw];
        if (labelToKey[clean]) return labelToKey[clean];

        // 3. Extended aliases match
        if (HEADER_ALIASES[normRaw]) return HEADER_ALIASES[normRaw];
        if (HEADER_ALIASES[normClean]) return HEADER_ALIASES[normClean];
        if (HEADER_ALIASES[compactClean]) return HEADER_ALIASES[compactClean];
        if (HEADER_ALIASES[alphaClean]) return HEADER_ALIASES[alphaClean];

        // 4. Direct regex for Allow_ID_Prop or Ded_ID_Prop
        const allowDirectMatch = raw.match(/^Allow_(\d+)_(.+)$/i) || clean.match(/^Allow_(\d+)_(.+)$/i);
        if (allowDirectMatch) {
          const directId = Number(allowDirectMatch[1]);
          if (activeAllowIdSet.has(directId)) {
            return `Allow_${directId}_${allowDirectMatch[2]}`;
          }
        }

        const dedDirectMatch = raw.match(/^Ded_(\d+)_(.+)$/i) || clean.match(/^Ded_(\d+)_(.+)$/i);
        if (dedDirectMatch) {
          const directId = Number(dedDirectMatch[1]);
          if (activeDedIdSet.has(directId)) {
            return `Ded_${directId}_${dedDirectMatch[2]}`;
          }
        }

        // 5. Dynamic Allowance matching using multi-variant engine
        const dynAllow = resolveDynamicAllowanceHeader(raw, allowanceMatchers) || resolveDynamicAllowanceHeader(clean, allowanceMatchers);
        if (dynAllow) return dynAllow;

        // 6. Dynamic Deduction matching using multi-variant engine
        const dynDed = resolveDynamicDeductionHeader(raw, deductionMatchers) || resolveDynamicDeductionHeader(clean, deductionMatchers);
        if (dynDed) return dynDed;

        return clean || raw;
      };

      // Extract detected columns from Row 1
      const detectedColumns = [];
      headers.forEach((h, colIdx) => {
        if (!h || String(h).trim() === "") return;
        const rawHeader = String(h).trim();
        const mappedKey = resolveHeaderKey(rawHeader);

        // Determine user-friendly label
        let label = rawHeader;
        const flatMatch = IMPORT_FLAT_FIELDS.find(
          (f) => f.key === mappedKey || f.label.toLowerCase() === rawHeader.toLowerCase()
        );
        if (flatMatch) {
          label = flatMatch.label;
        } else {
          const exportMatch = (exportColumns || []).find(
            (c) => c.key === mappedKey || c.label.toLowerCase() === rawHeader.toLowerCase()
          );
          if (exportMatch) {
            label = exportMatch.label;
          } else {
            // Allowance / Deduction Master name resolution fallback
            const allowM = mappedKey.match(/^Allow_(\d+)_(.+)$/);
            if (allowM) {
              const aId = Number(allowM[1]);
              const prop = allowM[2];
              const foundA = (firmAllowancesList || []).find((a) => Number(a.Id || a.id) === aId);
              if (foundA) {
                label = `${foundA.Name || foundA.name} ${prop === "Amount" ? "Allowance" : prop}`;
              }
            }
            const dedM = mappedKey.match(/^Ded_(\d+)_(.+)$/);
            if (dedM) {
              const dId = Number(dedM[1]);
              const prop = dedM[2];
              const foundD = (firmDeductionsList || []).find((d) => Number(d.Id || d.id) === dId);
              if (foundD) {
                label = `${foundD.Name || foundD.name} ${prop === "Amount" ? "Deduction" : prop}`;
              }
            }
          }
        }

        detectedColumns.push({
          key: mappedKey,
          label: label,
          originalHeader: rawHeader,
          colIndex: colIdx,
        });
      });

      setImportedColumns(detectedColumns);

      // High-precision normalization helper for person names (stripping honorifics, punctuation, and extra spaces)
      const cleanPersonName = (val) => {
        if (val === null || val === undefined) return "";
        let s = String(val)
          .trim()
          .toLowerCase()
          .replace(/['’`.]/g, "")
          .replace(/[^a-z0-9\s]/gi, " ")
          .replace(/\s+/g, " ")
          .trim();
        s = s.replace(/^(mr|mrs|ms|shri|shree|smt|dr|late)\s+/i, "").trim();
        return s;
      };

      // Pre-build O(1) multi-index lookup maps for existing employees to eliminate parsing lag
      const existingByCode = new Map();
      const existingByNameAndFather = new Map();
      const existingByNameOnly = new Map();

      if (Array.isArray(currentExisting)) {
        currentExisting.forEach((r) => {
          // Index by exact code & card no
          const c1 = String(r.EmployeeCode || "").trim().toLowerCase();
          const c2 = String(r.CardNo || "").trim().toLowerCase();
          if (c1) existingByCode.set(c1, r);
          if (c2 && !existingByCode.has(c2)) existingByCode.set(c2, r);

          // Index by numeric code (without leading zeros e.g. "005" -> "5")
          if (c1) {
            const parsed = parseInt(c1, 10);
            if (!isNaN(parsed) && parsed > 0 && !existingByCode.has(String(parsed))) {
              existingByCode.set(String(parsed), r);
            }
          }
          if (c2) {
            const parsed = parseInt(c2, 10);
            if (!isNaN(parsed) && parsed > 0 && !existingByCode.has(String(parsed))) {
              existingByCode.set(String(parsed), r);
            }
          }

          // Index by Name + FatherName
          const cName = cleanPersonName(r.Name);
          const cFather = cleanPersonName(r.FatherName);
          if (cName && cFather) {
            const nfKey = `${cName}__${cFather}`;
            if (!existingByNameAndFather.has(nfKey)) {
              existingByNameAndFather.set(nfKey, r);
            }
          }

          // Index by Name only (candidate grouping)
          if (cName) {
            if (!existingByNameOnly.has(cName)) {
              existingByNameOnly.set(cName, []);
            }
            existingByNameOnly.get(cName).push(r);
          }
        });
      }

      const rows = [];
      sheet.eachRow((row, rowNumber) => {
        if (rowNumber === 1) return; // skip header row

        const obj = {};
        const allowancesMap = {};
        const deductionsMap = {};

        row.eachCell({ includeEmpty: true }, (cell, colNum) => {
          const headerName = headers[colNum - 1];
          if (!headerName) return;

          const mappedKey = resolveHeaderKey(headerName);
          let val = cell.value;
          if (val && typeof val === "object" && val.result !== undefined) val = val.result;
          if (val instanceof Date) val = safeDate(val);
          const valStr = val !== null && val !== undefined ? String(val).trim() : "";

          // Check dynamic allowance column (e.g. Allow_74_Amount)
          // ROOT FIX: Only add to allowancesMap when cell has a non-empty value
          const allowMatch = mappedKey.match(/^Allow_(\d+)_(.+)$/);
          if (allowMatch) {
            const allowId = Number(allowMatch[1]);
            const prop = allowMatch[2];
            if (valStr !== "") {
              if (!allowancesMap[allowId]) allowancesMap[allowId] = { F_AllowanceMaster: allowId };
              allowancesMap[allowId][prop] = valStr;
              obj[mappedKey] = valStr;
            }
            return;
          }

          // Check dynamic deduction column (e.g. Ded_18_Amount)
          // ROOT FIX: Only add to deductionsMap when cell has a non-empty value
          const dedMatch = mappedKey.match(/^Ded_(\d+)_(.+)$/);
          if (dedMatch) {
            const dedId = Number(dedMatch[1]);
            const prop = dedMatch[2];
            if (valStr !== "") {
              if (!deductionsMap[dedId]) deductionsMap[dedId] = { F_DeductionMaster: dedId };
              deductionsMap[dedId][prop] = valStr;
              obj[mappedKey] = valStr;
            }
            return;
          }

          // Avoid overwriting populated non-empty property with empty string from secondary column
          if (valStr !== "" || obj[mappedKey] === undefined) {
            obj[mappedKey] = valStr;
          }
          if (headerName !== mappedKey && (valStr !== "" || obj[headerName] === undefined)) {
            obj[headerName] = valStr;
          }
        });

        // Skip completely empty rows
        // ROOT FIX: Count only allowance/deduction map entries that have actual data
        const allowancesHasData = Object.values(allowancesMap).some((a) =>
          Object.keys(a).some((k) => k !== "F_AllowanceMaster" && String(a[k] || "").trim() !== "")
        );
        const deductionsHasData = Object.values(deductionsMap).some((d) =>
          Object.keys(d).some((k) => k !== "F_DeductionMaster" && String(d[k] || "").trim() !== "")
        );
        const hasData = Object.values(obj).some((v) => v && String(v).trim() !== "") ||
          allowancesHasData || deductionsHasData;
        if (!hasData) return;

        // Normalization of Dates to ISO YYYY-MM-DD
        if (obj.JoinDate) obj.JoinDate = toIsoDate(obj.JoinDate);
        if (obj.Join_JoinDate && !obj.JoinDate) obj.JoinDate = toIsoDate(obj.Join_JoinDate);
        if (obj.DateOfBirth) obj.DateOfBirth = toIsoDate(obj.DateOfBirth);
        if (obj.DateOfRetire) obj.DateOfRetire = toIsoDate(obj.DateOfRetire);
        if (obj.Dept_EffectiveDate) obj.Dept_EffectiveDate = toIsoDate(obj.Dept_EffectiveDate);
        if (obj.Shift_EffectiveFrom) obj.Shift_EffectiveFrom = toIsoDate(obj.Shift_EffectiveFrom);
        if (obj.Sal_EffectiveDate) obj.Sal_EffectiveDate = toIsoDate(obj.Sal_EffectiveDate);
        if (obj.ESIJoiningDate) obj.ESIJoiningDate = toIsoDate(obj.ESIJoiningDate);
        if (obj.PFJoinDate) obj.PFJoinDate = toIsoDate(obj.PFJoinDate);
        if (obj.DLExpiryDate) obj.DLExpiryDate = toIsoDate(obj.DLExpiryDate);
        if (obj.PassportFrom) obj.PassportFrom = toIsoDate(obj.PassportFrom);
        if (obj.PassportTill) obj.PassportTill = toIsoDate(obj.PassportTill);
        if (obj.RefundFileDate) obj.RefundFileDate = toIsoDate(obj.RefundFileDate);
        if (obj.GratuityPaidDate) obj.GratuityPaidDate = toIsoDate(obj.GratuityPaidDate);
        if (obj.DateOfExitPr) obj.DateOfExitPr = toIsoDate(obj.DateOfExitPr);

        // Department Master ID Resolution
        const dName = (obj.DepartmentName || obj.Dept_DepartmentName || "").trim().toLowerCase();
        if (dName) {
          const match = (departmentsList || []).find((d) => (d.Name || "").trim().toLowerCase() === dName);
          if (match) {
            obj.F_DepartmentMaster = match.Id;
            obj.Dept_F_DepartmentMaster = match.Id;
            obj.DepartmentName = match.Name;
          }
        }

        // Designation Master ID Resolution
        const desName = (obj.DesignationName || obj.Dept_DesignationName || "").trim().toLowerCase();
        if (desName) {
          const match = (designationsList || []).find((d) => (d.Name || "").trim().toLowerCase() === desName);
          if (match) {
            obj.F_DesignationMaster = match.Id;
            obj.Dept_F_DesignationMaster = match.Id;
            obj.DesignationName = match.Name;
          }
        }

        // Shift Master ID Resolution
        const sName = (obj.ShiftName || obj.Shift_ShiftName || obj.Shift || "").trim().toLowerCase();
        if (sName) {
          const match = (firmShiftsList || []).find((s) => (s.Name || "").trim().toLowerCase() === sName);
          if (match) {
            obj.F_ShiftMaster = match.Id;
            obj.Shift_F_ShiftMaster = match.Id;
            obj.ShiftName = match.Name;
          }
        } else if ((firmShiftsList || []).length > 0) {
          // Default to first firm shift if blank for new employee
          obj.F_ShiftMaster = firmShiftsList[0].Id;
          obj.Shift_F_ShiftMaster = firmShiftsList[0].Id;
          obj.ShiftName = firmShiftsList[0].Name;
        }

        // Branch Master ID Resolution
        const brName = (obj.BranchName || obj.Branch || "").trim().toLowerCase();
        if (brName) {
          const match = (branchesList || []).find((b) => (b.Name || "").trim().toLowerCase() === brName);
          if (match) {
            obj.F_BranchMaster = match.Id;
            obj.BranchName = match.Name;
          }
        } else if ((branchesList || []).length > 0) {
          obj.F_BranchMaster = branchesList[0].Id;
          obj.BranchName = branchesList[0].Name;
        }

        // Join Type Master ID Resolution & Defaulting
        const jName = (obj.JoinTypeName || obj.Join_JoinTypeName || obj.JoinType || "").trim().toLowerCase();
        if (jName) {
          const match = (joinTypesList || []).find((j) => (j.Name || "").trim().toLowerCase() === jName || String(j.Id) === jName);
          if (match) {
            obj.F_JoinTypeMaster = match.Id;
            obj.Join_F_JoinTypeMaster = match.Id;
            obj.JoinTypeName = match.Name;
            obj.Join_JoinTypeName = match.Name;
          }
        } else {
          // Auto-default to 'Join' for newly joining/imported employee
          const defaultJoin = (joinTypesList || []).find((j) => (j.Name || "").trim().toLowerCase() === "join" || j.Id === 1) || joinTypesList?.[0];
          if (defaultJoin) {
            obj.F_JoinTypeMaster = defaultJoin.Id;
            obj.Join_F_JoinTypeMaster = defaultJoin.Id;
            obj.JoinTypeName = defaultJoin.Name;
            obj.Join_JoinTypeName = defaultJoin.Name;
          } else {
            obj.F_JoinTypeMaster = 1;
            obj.Join_F_JoinTypeMaster = 1;
            obj.JoinTypeName = "Join";
            obj.Join_JoinTypeName = "Join";
          }
        }

        // Basic Type Resolution (1=M, 2=D, 3=H, 4=P)
        const rawBType = String(
          obj.Sal_F_BasicTypeMaster ||
          obj.F_BasicTypeMaster ||
          obj.Sal_BasicTypeName ||
          obj.Sal_BasicType ||
          obj.BasicTypeName ||
          obj.BasicType ||
          ""
        ).trim().toUpperCase();

        if (rawBType === "2" || rawBType === "D" || rawBType.includes("DAY") || rawBType.includes("DAILY")) {
          obj.F_BasicTypeMaster = 2;
          obj.Sal_F_BasicTypeMaster = 2;
          obj.Sal_BasicType = "D";
          obj.Sal_BasicTypeName = "D";
          obj.BasicTypeName = "D";
        } else if (rawBType === "3" || rawBType === "H" || rawBType.includes("HOUR")) {
          obj.F_BasicTypeMaster = 3;
          obj.Sal_F_BasicTypeMaster = 3;
          obj.Sal_BasicType = "H";
          obj.Sal_BasicTypeName = "H";
          obj.BasicTypeName = "H";
        } else if (rawBType === "4" || rawBType === "P" || rawBType.includes("PIECE")) {
          obj.F_BasicTypeMaster = 4;
          obj.Sal_F_BasicTypeMaster = 4;
          obj.Sal_BasicType = "P";
          obj.Sal_BasicTypeName = "P";
          obj.BasicTypeName = "P";
        } else if (rawBType === "1" || rawBType === "M" || rawBType.includes("MONTH")) {
          obj.F_BasicTypeMaster = 1;
          obj.Sal_F_BasicTypeMaster = 1;
          obj.Sal_BasicType = "M";
          obj.Sal_BasicTypeName = "M";
          obj.BasicTypeName = "M";
        } else if (rawBType !== "") {
          const code = normalizeBasicTypeCode(rawBType);
          obj.Sal_BasicTypeName = code;
          obj.BasicTypeName = code;
          obj.Sal_BasicType = code;
          if (code === "M") { obj.F_BasicTypeMaster = 1; obj.Sal_F_BasicTypeMaster = 1; }
          else if (code === "D") { obj.F_BasicTypeMaster = 2; obj.Sal_F_BasicTypeMaster = 2; }
          else if (code === "H") { obj.F_BasicTypeMaster = 3; obj.Sal_F_BasicTypeMaster = 3; }
          else if (code === "P") { obj.F_BasicTypeMaster = 4; obj.Sal_F_BasicTypeMaster = 4; }
        } else {
          obj.F_BasicTypeMaster = 1;
          obj.Sal_F_BasicTypeMaster = 1;
          obj.Sal_BasicType = "M";
          obj.Sal_BasicTypeName = "M";
          obj.BasicTypeName = "M";
        }

        // String cast for numbers (Aadhar, Mobile, Bank Account, UAN, ESI)
        if (obj.AadharNumber) obj.AadharNumber = String(obj.AadharNumber).trim();
        if (obj.AadharCardNo) obj.AadharCardNo = String(obj.AadharCardNo).trim();
        if (!obj.AadharNumber && obj.AadharCardNo) obj.AadharNumber = obj.AadharCardNo;
        if (!obj.AadharCardNo && obj.AadharNumber) obj.AadharCardNo = obj.AadharNumber;

        if (obj.MobileNo) obj.MobileNo = String(obj.MobileNo).trim();
        if (obj.BankAccountNo) obj.BankAccountNo = String(obj.BankAccountNo).trim();
        if (obj.BankAccNo) obj.BankAccNo = String(obj.BankAccNo).trim();
        if (!obj.BankAccountNo && obj.BankAccNo) obj.BankAccountNo = obj.BankAccNo;
        if (!obj.BankAccNo && obj.BankAccountNo) obj.BankAccNo = obj.BankAccountNo;

        if (obj.IFSCCode) obj.IFSCCode = String(obj.IFSCCode).trim();
        if (obj.BankIFSC) obj.BankIFSC = String(obj.BankIFSC).trim();
        if (!obj.IFSCCode && obj.BankIFSC) obj.IFSCCode = obj.BankIFSC;
        if (!obj.BankIFSC && obj.IFSCCode) obj.BankIFSC = obj.IFSCCode;

        if (obj.UAN) obj.UAN = String(obj.UAN).replace(/\D/g, "").slice(0, 12);
        if (obj.UANNo) obj.UANNo = String(obj.UANNo).replace(/\D/g, "").slice(0, 12);
        if (!obj.UAN && obj.UANNo) obj.UAN = obj.UANNo;
        if (!obj.UANNo && obj.UAN) obj.UANNo = obj.UAN;
        if (obj.OldUAN) obj.OldUAN = String(obj.OldUAN).replace(/\D/g, "").slice(0, 12);

        if (obj.ESINo) obj.ESINo = String(obj.ESINo).trim();
        if (obj.PFNo) obj.PFNo = String(obj.PFNo).trim();
        if (obj.PanCardNo) obj.PanCardNo = String(obj.PanCardNo).trim();

        // Dynamic Allowances Array
        const effectiveBaseDate = obj.JoinDate || obj.Sal_EffectiveDate || new Date().toISOString().substring(0, 10);
        const normalizeAllowanceCalc = (c) => {
          if (!c) return "MANUAL";
          const s = String(c).trim().toUpperCase();
          if (["P", "PERCENTAGE", "%"].includes(s)) return "PERCENTAGE";
          if (["D", "DAILY"].includes(s)) return "DAILY";
          if (["F", "FIXED"].includes(s)) return "FIXED";
          return "MANUAL";
        };

        const validAllowances = Object.values(allowancesMap).map((a) => {
          const hasAmount = a.Amount !== undefined && a.Amount !== null && String(a.Amount).trim() !== "";
          const amtNum = hasAmount ? parseFloat(a.Amount) : 0;
          const isApplicableVal = a.IsApplicable !== undefined && String(a.IsApplicable).trim() !== "" ? parseBool(a.IsApplicable) : true;

          // STRICT USER REQUIREMENT: Only insert/update allowance if user explicitly gave a positive amount (> 0)
          const isValid = hasAmount && !isNaN(amtNum) && amtNum > 0 && isApplicableVal;

          return {
            F_AllowanceMaster: a.F_AllowanceMaster,
            Amount: isNaN(amtNum) ? 0 : amtNum,
            Calculation: normalizeAllowanceCalc(a.Calculation),
            IsPFApply: parseBool(a.IsPFApply),
            IsESIApply: parseBool(a.IsESIApply),
            IsOnGross: parseBool(a.IsOnGross),
            IsRoundOffApply: a.IsRoundOffApply !== undefined && String(a.IsRoundOffApply).trim() !== "" ? parseBool(a.IsRoundOffApply) : true,
            IsWorkingDaysApply: parseBool(a.IsWorkingDaysApply),
            IsApplicable: isApplicableVal,
            EffectiveFrom: a.EffectiveFrom ? toIsoDate(a.EffectiveFrom) : effectiveBaseDate,
            _isValid: isValid,
          };
        }).filter((a) => a._isValid && activeAllowIdSet.has(Number(a.F_AllowanceMaster))).map(({ _isValid, ...rest }) => rest);

        // Dynamic Deductions Array
        const validDeductions = Object.values(deductionsMap).map((d) => {
          const hasAmount = d.Amount !== undefined && d.Amount !== null && String(d.Amount).trim() !== "";
          const amtNum = hasAmount ? parseFloat(d.Amount) : 0;
          const isApplicableVal = d.IsApplicable !== undefined && String(d.IsApplicable).trim() !== "" ? parseBool(d.IsApplicable) : true;

          // STRICT USER REQUIREMENT: Only insert/update deduction if user explicitly gave a positive amount (> 0)
          const isValid = hasAmount && !isNaN(amtNum) && amtNum > 0 && isApplicableVal;

          return {
            F_DeductionMaster: d.F_DeductionMaster,
            Amount: isNaN(amtNum) ? 0 : amtNum,
            Calculation: normalizeAllowanceCalc(d.Calculation),
            IsOnGross: parseBool(d.IsOnGross),
            IsPFApply: parseBool(d.IsPFApply),
            IsESIApply: parseBool(d.IsESIApply),
            IsRoundOffApply: d.IsRoundOffApply !== undefined && String(d.IsRoundOffApply).trim() !== "" ? parseBool(d.IsRoundOffApply) : true,
            IsWorkingDaysApply: parseBool(d.IsWorkingDaysApply),
            IsApplicable: isApplicableVal,
            EffectiveFrom: d.EffectiveFrom ? toIsoDate(d.EffectiveFrom) : effectiveBaseDate,
            _isValid: isValid,
          };
        }).filter((d) => d._isValid && activeDedIdSet.has(Number(d.F_DeductionMaster))).map(({ _isValid, ...rest }) => rest);

        // ONLY attach Allowances array if the user explicitly provided valid allowance data (> 0)
        if (validAllowances && validAllowances.length > 0) {
          obj.Allowances = validAllowances;
        } else {
          delete obj.Allowances;
        }

        // ONLY attach Deductions array if the user explicitly provided valid deduction data (> 0)
        if (validDeductions && validDeductions.length > 0) {
          obj.Deductions = validDeductions;
        } else {
          delete obj.Deductions;
        }

        // Clean up empty, zero, or unprovided dynamic keys so only genuinely populated values remain for preview
        Object.keys(obj).forEach((key) => {
          if (/^(Allow_|Ded_)\d+_.+$/.test(key)) {
            const v = String(obj[key] || "").trim();
            if (v === "" || v === "0" || v === "0.00") {
              delete obj[key];
            }
          }
        });

        // Normalize Standard Values
        if (obj.Sex) obj.Sex = normalizeGender(obj.Sex);
        if (obj.PFCalculationType) obj.PFCalculationType = normalizeCalcCode(obj.PFCalculationType);

        // EmployeeType handling: match with active types from EmployeeTypeMaster
        if (!obj.EmployeeType || String(obj.EmployeeType).trim() === "" || String(obj.EmployeeType).trim() === "0") {
          obj.EmployeeType = "Ordinary";
        } else {
          const rawType = String(obj.EmployeeType).trim().toLowerCase();
          const matched = (employeeTypesList || []).find((t) => t.toLowerCase() === rawType);
          if (matched) {
            obj.EmployeeType = matched;
          }
        }

        // Working Status handling: normalize to Working, Left, Suspended, or Retired (default: Working)
        const rawStatus = String(obj.WorkingStatus || obj.Status || "").trim().toLowerCase();
        if (["left", "l"].includes(rawStatus)) {
          obj.Status = "Left";
        } else if (["suspended", "suspend", "s"].includes(rawStatus)) {
          obj.Status = "Suspended";
        } else if (["retired", "retire", "r"].includes(rawStatus)) {
          obj.Status = "Retired";
        } else {
          obj.Status = "Working";
        }
        obj.WorkingStatus = obj.Status;

        // Canonical field synchronization
        if (!obj.ShiftName) obj.ShiftName = obj.Shift_ShiftName || obj.Shift || "";
        if (!obj.Shift_ShiftName) obj.Shift_ShiftName = obj.ShiftName || "";
        if (!obj.Shift_EffectiveFrom) obj.Shift_EffectiveFrom = obj.Shift_EffectiveDate || obj.ShiftEffectiveFrom || "";
        if (!obj.ReasonOfLeft) obj.ReasonOfLeft = obj.Join_ReasonOfLeft || obj.Reason || "";
        if (!obj.LeftDate) obj.LeftDate = obj.Join_LeftDate || "";
        if (!obj.JoinDate) obj.JoinDate = obj.Join_JoinDate || "";
        if (!obj.JoinTypeName) obj.JoinTypeName = obj.Join_JoinTypeName || obj.JoinType || "Join";
        if (!obj.Join_JoinTypeName) obj.Join_JoinTypeName = obj.JoinTypeName || "Join";
        if (!obj.F_JoinTypeMaster) obj.F_JoinTypeMaster = obj.Join_F_JoinTypeMaster || 1;
        if (!obj.Join_F_JoinTypeMaster) obj.Join_F_JoinTypeMaster = obj.F_JoinTypeMaster || 1;
        if (!obj.DepartmentName) obj.DepartmentName = obj.Dept_DepartmentName || "";
        if (!obj.DesignationName) obj.DesignationName = obj.Dept_DesignationName || "";
        if (!obj.Sal_BasicSalary && obj.BasicSalary) obj.Sal_BasicSalary = obj.BasicSalary;
        if (!obj.Sal_GradePay && obj.GradePay) obj.Sal_GradePay = obj.GradePay;
        if (!obj.Sal_BasicTypeName) obj.Sal_BasicTypeName = obj.BasicTypeName || obj.Sal_BasicType || obj.BasicType || "";
        if (!obj.Sal_BasicType) obj.Sal_BasicType = obj.Sal_BasicTypeName || "";
        if (!obj.F_BasicTypeMaster && obj.Sal_F_BasicTypeMaster) obj.F_BasicTypeMaster = obj.Sal_F_BasicTypeMaster;
        if (!obj.Sal_F_BasicTypeMaster && obj.F_BasicTypeMaster) obj.Sal_F_BasicTypeMaster = obj.F_BasicTypeMaster;
        if (!obj.EmployeeCode) obj.EmployeeCode = obj.CardNo || "";
        if (!obj.CardNo) obj.CardNo = obj.EmployeeCode || "";

        // Required field validation
        const errors = [];
        if (!obj.Name || String(obj.Name).trim() === "") {
          errors.push("Employee Name is required");
        }

        // Validate mobile
        if (obj.MobileNo && !/^\d{0,12}$/.test(obj.MobileNo.replace(/[-+\s]/g, ""))) {
          errors.push("Mobile No format invalid");
        }

        // Validate UAN (maximum 12 numeric digits)
        if (obj.UAN && !/^\d{1,12}$/.test(String(obj.UAN).trim())) {
          errors.push("UAN must be numeric and maximum 12 digits");
        }
        if (obj.OldUAN && !/^\d{1,12}$/.test(String(obj.OldUAN).trim())) {
          errors.push("Old UAN must be numeric and maximum 12 digits");
        }

        // Match existing employee in DB:
        let existingRecord = null;
        let matchedBy = null; // "NAME_FATHER" | "CODE" | "NAME_ONLY"

        const rowNameClean = cleanPersonName(obj.Name);
        const rowFatherClean = cleanPersonName(obj.FatherName);
        const rowCodeRaw = String(obj.EmployeeCode || obj.CardNo || "").trim().toLowerCase();
        const rowCodeParsed = parseInt(rowCodeRaw, 10);
        const rowCodeNum = !isNaN(rowCodeParsed) && rowCodeParsed > 0 ? String(rowCodeParsed) : "";

        // 1. PRIMARY MATCHING RULE: Same Name AND Father Name in the active firm
        // When Name and Father Name match an existing employee, route for UPDATE
        if (rowNameClean && rowFatherClean) {
          const nfKey = `${rowNameClean}__${rowFatherClean}`;
          if (existingByNameAndFather.has(nfKey)) {
            existingRecord = existingByNameAndFather.get(nfKey);
            matchedBy = "NAME_FATHER";
          }
        }

        // 2. SECONDARY MATCHING RULE: Employee Code / Card No (if provided and valid)
        if (!existingRecord && rowCodeRaw && rowCodeRaw !== "0") {
          if (existingByCode.has(rowCodeRaw)) {
            existingRecord = existingByCode.get(rowCodeRaw);
            matchedBy = "CODE";
          } else if (rowCodeNum && existingByCode.has(rowCodeNum)) {
            existingRecord = existingByCode.get(rowCodeNum);
            matchedBy = "CODE";
          }
        }

        // 3. TERTIARY MATCHING RULE: Candidate by Name where FatherName is compatible
        if (!existingRecord && rowNameClean) {
          const candidates = existingByNameOnly.get(rowNameClean) || [];
          if (candidates.length === 1) {
            const cand = candidates[0];
            const candFather = cleanPersonName(cand.FatherName);
            if (!rowFatherClean || !candFather || rowFatherClean === candFather) {
              existingRecord = cand;
              matchedBy = rowFatherClean && candFather ? "NAME_FATHER" : "NAME_ONLY";
            }
          } else if (candidates.length > 1 && rowFatherClean) {
            const cand = candidates.find((c) => cleanPersonName(c.FatherName) === rowFatherClean);
            if (cand) {
              existingRecord = cand;
              matchedBy = "NAME_FATHER";
            }
          }
        }

        const isNew = !existingRecord;

        // Effective dates inheritance:
        // For new employees, all effective dates inherit JoinDate if not explicitly provided
        if (isNew && obj.JoinDate && String(obj.JoinDate).trim() !== "") {
          if (!obj.Dept_EffectiveDate || String(obj.Dept_EffectiveDate).trim() === "") {
            obj.Dept_EffectiveDate = obj.JoinDate;
          }
          if (!obj.Shift_EffectiveFrom || String(obj.Shift_EffectiveFrom).trim() === "") {
            obj.Shift_EffectiveFrom = obj.JoinDate;
          }
          if (!obj.Sal_EffectiveDate || String(obj.Sal_EffectiveDate).trim() === "") {
            obj.Sal_EffectiveDate = obj.JoinDate;
          }
        }

        // PF & ESI:
        if (parseBool(obj.ESIApplicable)) {
          if (!obj.ESIJoiningDate || String(obj.ESIJoiningDate).trim() === "") {
            obj.ESIJoiningDate = obj.JoinDate || obj.Dept_EffectiveDate || "";
          }
        } else {
          obj.ESIJoiningDate = "";
        }

        if (parseBool(obj.PFApplicable)) {
          if (!obj.PFJoinDate || String(obj.PFJoinDate).trim() === "") {
            obj.PFJoinDate = obj.JoinDate || obj.Dept_EffectiveDate || "";
          }
          if (obj.PensionApplicable === undefined || obj.PensionApplicable === null || String(obj.PensionApplicable).trim() === "") {
            obj.PensionApplicable = "YES";
          }
        } else {
          obj.PFJoinDate = "";
        }

        let isModified = false;
        let rowType = "INSERT";

        if (existingRecord) {
          // BIND CANONICAL CODE AND ID FROM DB:
          // Crucial so backend matches this employee by EmployeeCode and runs UPDATE instead of INSERT
          const canonicalCode = existingRecord.EmployeeCode || existingRecord.CardNo;
          obj.EmployeeCode = canonicalCode;
          obj.CardNo = canonicalCode;
          if (existingRecord.Id) {
            obj.Id = existingRecord.Id;
            obj.F_EmployeeMaster = existingRecord.Id;
          }

          const modCheck = isEmployeeModified(obj, existingRecord);
          isModified = modCheck.modified;

          const hasProvidedAllowances = Array.isArray(obj.Allowances) && obj.Allowances.length > 0;
          const hasProvidedDeductions = Array.isArray(obj.Deductions) && obj.Deductions.length > 0;

          // When matched by Name + Father Name, or if user provided allowances/deductions, or any field modified -> route for UPDATE
          if (matchedBy === "NAME_FATHER" || isModified || hasProvidedAllowances || hasProvidedDeductions) {
            rowType = "UPDATE";
            isModified = true; // Always send to backend to update existing employee
          } else {
            rowType = isModified ? "UPDATE" : "EXISTING";
          }
        } else {
          rowType = "INSERT";
          isModified = false;
        }

        // Precompute lowercase search index
        const searchParts = [
          obj.Name || "",
          obj.EmployeeCode || "",
          obj.CardNo || "",
          obj.DepartmentName || "",
          obj.DesignationName || "",
          obj.MobileNo || "",
          rowType,
          errors.length > 0 ? "error" : "pending",
          (errors || []).join(" "),
        ];
        Object.values(obj).forEach((v) => {
          if (typeof v === "string" || typeof v === "number") searchParts.push(v);
        });
        const _searchStr = searchParts.join(" ").toLowerCase();

        rows.push({
          sNo: rows.length + 1,
          rowIndex: rowNumber,
          data: obj,
          errors,
          status: errors.length > 0 ? "error" : "pending",
          type: rowType, // "INSERT" | "UPDATE" | "EXISTING"
          isNew: isNew,
          isModified: isModified,
          matchedBy: matchedBy,
          existingRecord: existingRecord,
          _searchStr,
        });
      });

      if (rows.length === 0) {
        toast.warning("No data rows found in the Excel file.");
        setIsParsing(false);
        return;
      }

      setPreviewData(rows);
      setShowPreview(true);
      toast.success(`📊 Parsed ${rows.length} rows. Review the data and click "Run Import".`);
    } catch (err) {
      console.error("Parse error:", err);
      toast.error("Failed to parse Excel file. Please check the file structure.");
    } finally {
      setIsParsing(false);
    }
  };

  // ──────────────────────────────────────────
  // Import - Unified Auto Insert / Update
  // ──────────────────────────────────────────
  const handleImport = async () => {
    const validRows = previewData.filter((r) => r.status !== "error");
    if (validRows.length === 0) {
      toast.warning("No valid rows to import. Please fix errors first.");
      return;
    }

    // Process only INSERT and UPDATE rows to avoid modifying unchanged records
    const rowsToProcess = validRows.filter((r) => r.type === "INSERT" || r.type === "UPDATE");
    const unchangedRows = validRows.filter((r) => r.type === "EXISTING");

    setImporting(true);
    setImportProgress(10);

    try {
      let inserted = 0;
      let updatedCount = 0;
      let failed = 0;
      const errors = [];
      const rowResults = [];
      const createdDepartments = [];
      const createdDesignations = [];

      // 1. Identify any new Department or Designation in the rows to be imported
      const existingDeptMap = new Map(
        (departmentsList || []).map((d) => [(d.Name || "").trim().toLowerCase(), d])
      );
      const existingDesigMap = new Map(
        (designationsList || []).map((d) => [(d.Name || "").trim().toLowerCase(), d])
      );

      const newDeptMap = new Map(); // lowercase -> { name, branchId }
      const newDesigMap = new Map(); // lowercase -> { name }

      rowsToProcess.forEach((r) => {
        const dName = String(r.data?.DepartmentName || r.data?.Dept_DepartmentName || "").trim();
        if (dName && !existingDeptMap.has(dName.toLowerCase())) {
          if (!newDeptMap.has(dName.toLowerCase())) {
            newDeptMap.set(dName.toLowerCase(), {
              name: dName,
              branchId: r.data?.F_BranchMaster || branchId || branchesList?.[0]?.Id || 0,
            });
          }
        }

        const desName = String(r.data?.DesignationName || r.data?.Dept_DesignationName || "").trim();
        if (desName && !existingDesigMap.has(desName.toLowerCase())) {
          if (!newDesigMap.has(desName.toLowerCase())) {
            newDesigMap.set(desName.toLowerCase(), {
              name: desName,
            });
          }
        }
      });

      const newDeptsToCreate = Array.from(newDeptMap.values());
      const newDesigsToCreate = Array.from(newDesigMap.values());

      // 2. Auto-save new Departments
      if (newDeptsToCreate.length > 0) {
        setImportProgress(20);
        for (const dept of newDeptsToCreate) {
          try {
            const fd = new FormData();
            fd.append("Name", dept.name);
            fd.append("F_FirmMaster", firmId || 0);
            fd.append("F_BranchMaster", dept.branchId || branchId || 0);
            fd.append("Ordering", 0);
            fd.append("IsSundayInclude", false);
            fd.append("IsDirectWages", false);
            fd.append("IsNotInUse", false);

            const res = await fetch(`${API_WEB_URLS.BASE}DepartmentMaster/0/token`, {
              method: "POST",
              headers: authToken ? { Authorization: `Bearer ${authToken}` } : {},
              body: fd,
            });
            if (res.ok) {
              const resJson = await res.json().catch(() => null);
              const resData = resJson?.response ? resJson.response[0] : (resJson?.data?.data || resJson?.data || resJson);
              const resId = resData?.Id ?? resData?.id ?? (typeof resData === "number" ? resData : null);
              if (resId !== -2) {
                createdDepartments.push(dept.name);
              } else {
                errors.push(`Failed to auto-save department "${dept.name}": Details missing`);
              }
            } else {
              errors.push(`Failed to auto-save department "${dept.name}" (HTTP ${res.status})`);
            }
          } catch (dErr) {
            console.error("Error creating department:", dept.name, dErr);
            errors.push(`Error saving department "${dept.name}": ${dErr.message}`);
          }
        }
      }

      // 3. Auto-save new Designations
      if (newDesigsToCreate.length > 0) {
        setImportProgress(35);
        const defaultWorkerCatId = workerCategoriesList?.[0]?.Id || 1;
        for (const desig of newDesigsToCreate) {
          try {
            const fd = new FormData();
            fd.append("Name", desig.name);
            fd.append("F_FirmMaster", firmId || 0);
            fd.append("F_WorkerCategory", defaultWorkerCatId);
            fd.append("IsNotInUse", false);

            const res = await fetch(`${API_WEB_URLS.BASE}DesignationMaster/0/token`, {
              method: "POST",
              headers: authToken ? { Authorization: `Bearer ${authToken}` } : {},
              body: fd,
            });
            if (res.ok) {
              const resJson = await res.json().catch(() => null);
              const resData = resJson?.response ? resJson.response[0] : (resJson?.data?.data || resJson?.data || resJson);
              const resId = resData?.Id ?? resData?.id ?? (typeof resData === "number" ? resData : null);
              if (resId !== -2) {
                createdDesignations.push(desig.name);
              } else {
                errors.push(`Failed to auto-save designation "${desig.name}": Details missing`);
              }
            } else {
              errors.push(`Failed to auto-save designation "${desig.name}" (HTTP ${res.status})`);
            }
          } catch (dgErr) {
            console.error("Error creating designation:", desig.name, dgErr);
            errors.push(`Error saving designation "${desig.name}": ${dgErr.message}`);
          }
        }
      }

      // 4. Refresh Department & Designation lists if any were created
      let freshDepts = departmentsList;
      let freshDesigs = designationsList;
      if (createdDepartments.length > 0) {
        freshDepts = await fetchDepartments(firmId);
      }
      if (createdDesignations.length > 0) {
        freshDesigs = await fetchDesignations(firmId);
      }

      // 5. Update rowsToProcess with resolved IDs
      if (createdDepartments.length > 0 || createdDesignations.length > 0) {
        rowsToProcess.forEach((r) => {
          const dName = String(r.data?.DepartmentName || r.data?.Dept_DepartmentName || "").trim().toLowerCase();
          if (dName) {
            const match = freshDepts.find((d) => (d.Name || "").trim().toLowerCase() === dName);
            if (match) {
              r.data.F_DepartmentMaster = match.Id;
              r.data.Dept_F_DepartmentMaster = match.Id;
              r.data.DepartmentName = match.Name;
            }
          }
          const desName = String(r.data?.DesignationName || r.data?.Dept_DesignationName || "").trim().toLowerCase();
          if (desName) {
            const match = freshDesigs.find((d) => (d.Name || "").trim().toLowerCase() === desName);
            if (match) {
              r.data.F_DesignationMaster = match.Id;
              r.data.Dept_F_DesignationMaster = match.Id;
              r.data.DesignationName = match.Name;
            }
          }
        });
      }

      // 6. Notify user immediately about created masters
      if (createdDepartments.length > 0 || createdDesignations.length > 0) {
        const parts = [];
        if (createdDepartments.length > 0) {
          parts.push(`📁 Department(s): ${createdDepartments.join(", ")}`);
        }
        if (createdDesignations.length > 0) {
          parts.push(`💼 Designation(s): ${createdDesignations.join(", ")}`);
        }
        toast.info(`✨ Auto-Created in Masters:\n${parts.join(" | ")}`, { autoClose: 7000 });
      }

      // 7. Process employee import
      if (rowsToProcess.length > 0) {
        const employeePayload = rowsToProcess.map((r) => r.data);
        setImportProgress(60);

        const res = await fetch(
          `${API_WEB_URLS.BASE}EmployeeImport/${userId || 0}/token/${firmId}`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${authToken}`,
            },
            body: JSON.stringify(employeePayload),
          }
        );

        setImportProgress(85);

        if (!res.ok) {
          const errJson = await res.json().catch(() => null);
          throw new Error(errJson?.message || `API returned HTTP ${res.status}`);
        }

        const json = await res.json();
        const summary = json?.data?.summary || {};
        const apiResults = json?.data?.results || [];

        inserted = summary.InsertedCount || 0;
        updatedCount = summary.UpdatedCount || 0;
        failed = summary.FailedCount || 0;

        apiResults.forEach((r) => {
          rowResults.push(r);
          if (r.Status !== "SUCCESS") {
            errors.push(`Row ${r.RowIndex} (${r.EmployeeCode || r.CardNo || r.Name}): ${r.Message}`);
          }
        });
      }

      // Add unchanged records
      unchangedRows.forEach((r) => {
        rowResults.push({
          RowIndex: r.rowIndex,
          EmployeeCode: r.data.EmployeeCode || r.data.CardNo,
          CardNo: r.data.EmployeeCode || r.data.CardNo,
          Name: r.data.Name,
          Action: "UNCHANGED",
          Status: "SUCCESS",
          Message: "Already up to date",
        });
      });

      // Update preview data statuses
      const updated = previewData.map((rowItem) => {
        if (rowItem.status === "error") return { ...rowItem, status: "skipped" };
        if (rowItem.type === "EXISTING") {
          return {
            ...rowItem,
            status: "success",
            apiAction: "UNCHANGED",
            apiError: null,
          };
        }
        const match = rowResults.find(
          (r) =>
            ((r.EmployeeCode && r.EmployeeCode === (rowItem.data.EmployeeCode || rowItem.data.CardNo)) ||
             (r.CardNo && r.CardNo === (rowItem.data.EmployeeCode || rowItem.data.CardNo))) ||
            r.Name === rowItem.data.Name
        );
        if (match) {
          return {
            ...rowItem,
            status: match.Status === "SUCCESS" ? "success" : "failed",
            apiAction: match.Action,
            apiError: match.Status !== "SUCCESS" ? match.Message : null,
          };
        }
        return { ...rowItem, status: "success" };
      });

      setPreviewData(updated);
      setImportProgress(100);

      const unchangedCount = unchangedRows.length;
      const total = validRows.length;
      const success = inserted + updatedCount + unchangedCount;

      setImportResults({
        total,
        success,
        inserted,
        updated: updatedCount,
        unchanged: unchangedCount,
        failed,
        errors,
        details: rowResults,
        createdDepartments,
        createdDesignations,
      });

      setShowResultModal(true);
      loadExportSummary(); // refresh stats

      if (inserted > 0 || updatedCount > 0) {
        const masterNote =
          createdDepartments.length > 0 || createdDesignations.length > 0
            ? ` (${createdDepartments.length + createdDesignations.length} new masters saved)`
            : "";
        toast.success(
          `🎉 Import Complete! ${inserted} inserted, ${updatedCount} updated${masterNote}${unchangedCount > 0 ? `, ${unchangedCount} unchanged` : ""}.`
        );
      } else if (unchangedCount > 0) {
        toast.info(`ℹ️ All ${unchangedCount} existing employees were already up to date.`);
      }
      if (failed > 0) {
        toast.error(`❌ ${failed} records failed during import.`);
      }
    } catch (err) {
      console.error("Import error:", err);
      toast.error(`Import failed: ${err.message}`);
    } finally {
      setImporting(false);
    }
  };

  // ──────────────────────────────────────────
  // Reset Import
  // ──────────────────────────────────────────
  const resetImport = () => {
    setImportFile(null);
    setPreviewData([]);
    setImportedColumns([]);
    setPreviewSearch("");
    setShowPreview(false);
    setImportProgress(0);
    setImporting(false);
    setImportResults({
      total: 0,
      success: 0,
      inserted: 0,
      updated: 0,
      unchanged: 0,
      failed: 0,
      errors: [],
      details: [],
      createdDepartments: [],
      createdDesignations: [],
    });
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  // ──────────────────────────────────────────
  // UI Helpers
  // ──────────────────────────────────────────
  const StatusBadge = ({ status, action }) => {
    const map = {
      pending: { bg: "#6c757d", label: "Pending" },
      success: {
        bg: action === "INSERTED" ? "#059669" : action === "UNCHANGED" ? "#64748b" : "#2563eb",
        label: action === "INSERTED" ? "✓ Inserted" : action === "UNCHANGED" ? "— Unchanged" : "✓ Updated",
      },
      failed: { bg: "#dc3545", label: "✗ Failed" },
      skipped: { bg: "#fd7e14", label: "⚠ Skipped" },
      error: { bg: "#dc3545", label: "✗ Invalid" },
    };
    const info = map[status] || map["pending"];
    return (
      <span
        style={{
          background: info.bg,
          color: "#fff",
          borderRadius: 4,
          padding: "2px 8px",
          fontSize: 11,
          fontWeight: 600,
          whiteSpace: "nowrap",
        }}
      >
        {info.label}
      </span>
    );
  };

  const TypeBadge = ({ type }) => {
    if (type === "INSERT") {
      return (
        <span
          style={{
            background: "#e0e7ff",
            color: "#3730a3",
            padding: "3px 8px",
            borderRadius: 4,
            fontSize: 11,
            fontWeight: 700,
            whiteSpace: "nowrap",
            display: "inline-block",
          }}
        >
          + INSERT
        </span>
      );
    }
    if (type === "UPDATE") {
      return (
        <span
          style={{
            background: "#fef3c7",
            color: "#92400e",
            padding: "3px 8px",
            borderRadius: 4,
            fontSize: 11,
            fontWeight: 700,
            whiteSpace: "nowrap",
            display: "inline-block",
          }}
        >
          ✏️ UPDATE
        </span>
      );
    }
    return (
      <span
        style={{
          background: "#f1f5f9",
          color: "#64748b",
          border: "1px solid #e2e8f0",
          padding: "3px 8px",
          borderRadius: 4,
          fontSize: 11,
          fontWeight: 600,
          whiteSpace: "nowrap",
          display: "inline-block",
        }}
      >
        — EXISTING
      </span>
    );
  };

  const validRows = previewData.filter((r) => r.status !== "error").length;
  const invalidRows = previewData.filter((r) => r.status === "error").length;
  const insertCount = previewData.filter((r) => r.status !== "error" && r.type === "INSERT").length;
  const updateCount = previewData.filter((r) => r.status !== "error" && r.type === "UPDATE").length;
  const existingCount = previewData.filter((r) => r.status !== "error" && r.type === "EXISTING").length;

  // Detect distinct new Departments and Designations that do not exist yet in DB
  const newDeptsDetected = useMemo(() => {
    if (!previewData || previewData.length === 0) return [];
    const existingDeptNames = new Set((departmentsList || []).map((d) => (d.Name || "").trim().toLowerCase()));
    const found = new Set();
    previewData.forEach((r) => {
      const d = String(r.data?.DepartmentName || r.data?.Dept_DepartmentName || "").trim();
      if (d && !existingDeptNames.has(d.toLowerCase())) {
        found.add(d);
      }
    });
    return Array.from(found);
  }, [previewData, departmentsList]);

  const newDesigsDetected = useMemo(() => {
    if (!previewData || previewData.length === 0) return [];
    const existingDesigNames = new Set((designationsList || []).map((d) => (d.Name || "").trim().toLowerCase()));
    const found = new Set();
    previewData.forEach((r) => {
      const d = String(r.data?.DesignationName || r.data?.Dept_DesignationName || "").trim();
      if (d && !existingDesigNames.has(d.toLowerCase())) {
        found.add(d);
      }
    });
    return Array.from(found);
  }, [previewData, designationsList]);

  // Filtered guidelines for search
  const filteredGuidelines = FORMAT_GUIDELINES.map((cat) => ({
    ...cat,
    fields: cat.fields.filter(
      (f) =>
        f.field.toLowerCase().includes(guidelineSearch.toLowerCase()) ||
        f.format.toLowerCase().includes(guidelineSearch.toLowerCase()) ||
        f.allowed.toLowerCase().includes(guidelineSearch.toLowerCase()) ||
        f.rule.toLowerCase().includes(guidelineSearch.toLowerCase())
    ),
  })).filter((cat) => cat.fields.length > 0);

  // Dynamic columns to render in preview table (excluding EmployeeCode, CardNo & Name which are rendered in sticky columns)
  const previewColumns = React.useMemo(() => {
    if (importedColumns && importedColumns.length > 0) {
      return importedColumns.filter((c) => c.key !== "EmployeeCode" && c.key !== "CardNo" && c.key !== "Name");
    }
    // Fallback if no importedColumns detected yet
    return IMPORT_FLAT_FIELDS.filter((f) => f.key !== "EmployeeCode" && f.key !== "CardNo" && f.key !== "Name");
  }, [importedColumns]);

  // Instant search filter using precomputed _searchStr
  const filteredPreviewData = React.useMemo(() => {
    if (!previewSearch.trim()) return previewData;
    const q = previewSearch.toLowerCase().trim();
    return previewData.filter((row) => {
      if (row._searchStr) return row._searchStr.includes(q);
      const name = String(row.data?.Name || "").toLowerCase();
      const code = String(row.data?.EmployeeCode || row.data?.CardNo || "").toLowerCase();
      return name.includes(q) || code.includes(q);
    });
  }, [previewData, previewSearch]);

  // Reset import page when search or data changes
  useEffect(() => {
    setImportPage(1);
  }, [previewSearch, previewData.length]);

  const importTotalPages = Math.max(1, Math.ceil(filteredPreviewData.length / importPageSize));
  const currentImportPage = Math.min(importPage, importTotalPages);

  const paginatedPreviewData = React.useMemo(() => {
    const start = (currentImportPage - 1) * importPageSize;
    return filteredPreviewData.slice(start, start + importPageSize);
  }, [filteredPreviewData, currentImportPage, importPageSize]);

  // Export search & pagination computation
  const filteredExportEmployees = React.useMemo(() => {
    if (!exportSearch.trim()) return existingEmployees;
    const q = exportSearch.toLowerCase().trim();
    return existingEmployees.filter((emp) => {
      const name = String(emp.Name || "").toLowerCase();
      const code = String(emp.EmployeeCode || emp.CardNo || "").toLowerCase();
      const dept = String(emp.DepartmentName || emp.Dept_DepartmentName || "").toLowerCase();
      const desig = String(emp.DesignationName || emp.Dept_DesignationName || "").toLowerCase();
      const mobile = String(emp.MobileNo || "").toLowerCase();
      return name.includes(q) || code.includes(q) || dept.includes(q) || desig.includes(q) || mobile.includes(q);
    });
  }, [existingEmployees, exportSearch]);

  useEffect(() => {
    setExportPage(1);
  }, [exportSearch, existingEmployees.length]);

  const exportTotalPages = Math.max(1, Math.ceil(filteredExportEmployees.length / exportPageSize));
  const currentExportPage = Math.min(exportPage, exportTotalPages);

  const paginatedExportEmployees = React.useMemo(() => {
    const start = (currentExportPage - 1) * exportPageSize;
    return filteredExportEmployees.slice(start, start + exportPageSize);
  }, [filteredExportEmployees, currentExportPage, exportPageSize]);

  const renderPreviewCell = (colKey, val, rowData) => {
    // If undefined/null or empty string, fallback to checking alt keys or empty
    let cellVal = val;
    if ((cellVal === null || cellVal === undefined || cellVal === "") && rowData) {
      // Check alternative keys (e.g. BasicSalary vs Sal_BasicSalary)
      if (colKey === "Sal_BasicSalary") cellVal = rowData.BasicSalary || rowData.Sal_BasicSalary;
      else if (colKey === "Sal_GradePay") cellVal = rowData.GradePay || rowData.Sal_GradePay;
      else if (colKey === "Sal_BasicType" || colKey === "Sal_BasicTypeName") cellVal = rowData.Sal_BasicTypeName || rowData.BasicTypeName || rowData.Sal_BasicType || rowData.BasicType;
      else if (colKey === "DepartmentName") cellVal = rowData.Dept_DepartmentName || rowData.DepartmentName;
      else if (colKey === "DesignationName") cellVal = rowData.Dept_DesignationName || rowData.DesignationName;
      else if (colKey === "ShiftName") cellVal = rowData.Shift_ShiftName || rowData.ShiftName || rowData.Shift;
      else if (colKey === "JoinDate") cellVal = rowData.Join_JoinDate || rowData.JoinDate;
      else if (colKey === "ReasonOfLeft") cellVal = rowData.Join_ReasonOfLeft || rowData.ReasonOfLeft || rowData.Reason;
      else if (colKey === "LeftDate") cellVal = rowData.Join_LeftDate || rowData.LeftDate;
      else if (colKey === "AadharCardNo") cellVal = rowData.AadharNumber || rowData.AadharCardNo;
      else if (colKey === "BankAccNo") cellVal = rowData.BankAccountNo || rowData.BankAccNo;
      else if (colKey === "BankIFSC") cellVal = rowData.IFSCCode || rowData.BankIFSC;
      else if (colKey === "UANNo") cellVal = rowData.UAN || rowData.UANNo;

      // Dynamic allowance fallback from parsed Allowances array
      const allowM = colKey && colKey.match(/^Allow_(\d+)_(.+)$/);
      if (allowM && Array.isArray(rowData.Allowances)) {
        const aId = Number(allowM[1]);
        const prop = allowM[2];
        const found = rowData.Allowances.find((a) => Number(a.F_AllowanceMaster) === aId);
        if (found && found[prop] !== undefined) cellVal = found[prop];
      }

      // Dynamic deduction fallback from parsed Deductions array
      const dedM = colKey && colKey.match(/^Ded_(\d+)_(.+)$/);
      if (dedM && Array.isArray(rowData.Deductions)) {
        const dId = Number(dedM[1]);
        const prop = dedM[2];
        const found = rowData.Deductions.find((d) => Number(d.F_DeductionMaster) === dId);
        if (found && found[prop] !== undefined) cellVal = found[prop];
      }
    }

    if (cellVal === null || cellVal === undefined || String(cellVal).trim() === "") {
      return <span style={{ color: "#9ca3af", fontStyle: "italic" }}>—</span>;
    }

    const sVal = String(cellVal).trim();

    // Booleans
    if (
      colKey.endsWith("Applicable") ||
      colKey.endsWith("Apply") ||
      colKey.endsWith("IsOnGross") ||
      colKey.endsWith("IsApplicable") ||
      colKey.endsWith("OverLimit") ||
      [
        "BankPayment", "IsHandicap", "Refunded", "TICs", "PICs", "Accident",
        "ESIOldEmployee", "HigherPension", "OldMemberPF", "OldMemberPension",
        "PMRPY", "PMGKY", "ABRY", "IsDeferredPension",
      ].includes(colKey)
    ) {
      const isYes = parseBool(sVal);
      return (
        <span
          style={{
            background: isYes ? "#ecfdf5" : "#f1f5f9",
            color: isYes ? "#059669" : "#64748b",
            padding: "2px 8px",
            borderRadius: 4,
            fontWeight: 600,
            fontSize: 11,
            whiteSpace: "nowrap",
          }}
        >
          {isYes ? "YES" : "NO"}
        </span>
      );
    }

    // DepartmentName
    if (colKey === "DepartmentName") {
      const isNewDept = !departmentsList.some(
        (d) => (d.Name || "").trim().toLowerCase() === sVal.toLowerCase()
      );
      return (
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
          <span>{sVal}</span>
          {isNewDept && (
            <span
              style={{
                background: "#ecfdf5",
                color: "#047857",
                border: "1px solid #a7f3d0",
                borderRadius: 4,
                padding: "1px 5px",
                fontSize: 10,
                fontWeight: 600,
                whiteSpace: "nowrap",
              }}
              title="New Department - Will be auto-created in Masters on import"
            >
              New ✨
            </span>
          )}
        </span>
      );
    }

    // DesignationName
    if (colKey === "DesignationName") {
      const isNewDesig = !designationsList.some(
        (d) => (d.Name || "").trim().toLowerCase() === sVal.toLowerCase()
      );
      return (
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
          <span>{sVal}</span>
          {isNewDesig && (
            <span
              style={{
                background: "#eff6ff",
                color: "#1d4ed8",
                border: "1px solid #bfdbfe",
                borderRadius: 4,
                padding: "1px 5px",
                fontSize: 10,
                fontWeight: 600,
                whiteSpace: "nowrap",
              }}
              title="New Designation - Will be auto-created in Masters on import"
            >
              New ✨
            </span>
          )}
        </span>
      );
    }

    // EmployeeType
    if (colKey === "EmployeeType") {
      return (
        <span
          style={{
            background: "#eff6ff",
            color: "#1d4ed8",
            border: "1px solid #bfdbfe",
            padding: "2px 8px",
            borderRadius: 4,
            fontWeight: 600,
            fontSize: 11,
            whiteSpace: "nowrap",
          }}
        >
          {sVal}
        </span>
      );
    }

    // Gender
    if (colKey === "Sex") {
      const g = normalizeGender(sVal);
      return (
        <span
          style={{
            background: g === "M" ? "#eff6ff" : g === "F" ? "#fdf2f8" : "#f5f3ff",
            color: g === "M" ? "#1e40af" : g === "F" ? "#9d174d" : "#5b21b6",
            padding: "2px 6px",
            borderRadius: 4,
            fontWeight: 600,
            fontSize: 11,
          }}
        >
          {g}
        </span>
      );
    }

    // Basic Type codes (M/D/H/P)
    if (colKey === "Sal_BasicType" || colKey === "Sal_BasicTypeName" || colKey === "BasicType" || colKey === "BasicTypeName") {
      const b = normalizeBasicTypeCode(sVal);
      const bTitle = b === "M" ? "Monthly" : b === "D" ? "Daily / Per Day" : b === "H" ? "Hourly" : b === "P" ? "Piece Rate" : sVal;
      return (
        <span
          style={{
            background: "#ecfdf5",
            color: "#065f46",
            border: "1px solid #a7f3d0",
            padding: "2px 8px",
            borderRadius: 4,
            fontWeight: 600,
            fontSize: 11,
            whiteSpace: "nowrap",
          }}
          title={bTitle}
        >
          {b || sVal}
        </span>
      );
    }

    // Calculation codes (M/P/D/F)
    if (colKey === "PFCalculationType" || colKey.endsWith("_Calculation")) {
      const c = normalizeCalcCode(sVal);
      return (
        <span
          style={{
            background: "#fef3c7",
            color: "#92400e",
            padding: "2px 6px",
            borderRadius: 4,
            fontWeight: 600,
            fontSize: 11,
          }}
        >
          {c}
        </span>
      );
    }

    // Dates
    if (
      colKey.includes("Date") ||
      colKey.includes("From") ||
      colKey.includes("Till") ||
      colKey.includes("DOB")
    ) {
      return (
        <span style={{ color: "#374151", fontFamily: "monospace", whiteSpace: "nowrap" }}>
          {safeDate(sVal)}
        </span>
      );
    }

    // Allowances amounts styling
    if (colKey.startsWith("Allow_") && colKey.endsWith("_Amount")) {
      const num = parseFloat(sVal);
      if (isNaN(num) || num <= 0) {
        return <span style={{ color: "#9ca3af", fontStyle: "italic" }}>—</span>;
      }
      return (
        <span
          style={{
            background: "#ecfdf5",
            color: "#047857",
            border: "1px solid #a7f3d0",
            padding: "2px 8px",
            borderRadius: 6,
            fontWeight: 700,
            fontSize: 12,
            fontFamily: "monospace",
            whiteSpace: "nowrap",
          }}
        >
          ₹ {num.toLocaleString("en-IN")}
        </span>
      );
    }

    // Deductions amounts styling
    if (colKey.startsWith("Ded_") && colKey.endsWith("_Amount")) {
      const num = parseFloat(sVal);
      if (isNaN(num) || num <= 0) {
        return <span style={{ color: "#9ca3af", fontStyle: "italic" }}>—</span>;
      }
      return (
        <span
          style={{
            background: "#fff1f2",
            color: "#be123c",
            border: "1px solid #fecdd3",
            padding: "2px 8px",
            borderRadius: 6,
            fontWeight: 700,
            fontSize: 12,
            fontFamily: "monospace",
            whiteSpace: "nowrap",
          }}
        >
          ₹ {num.toLocaleString("en-IN")}
        </span>
      );
    }

    // Numbers / Amounts
    if (
      colKey.includes("Salary") ||
      colKey.includes("Amount") ||
      colKey.includes("Rate") ||
      colKey.includes("Pay")
    ) {
      return (
        <span style={{ fontFamily: "monospace", fontWeight: 600, color: "#111827", whiteSpace: "nowrap" }}>
          {sVal}
        </span>
      );
    }

    // Standard string
    return <span style={{ whiteSpace: "nowrap" }}>{sVal}</span>;
  };

  // ──────────────────────────────────────────
  // Reusable Pagination Bar Component
  // ──────────────────────────────────────────
  const PaginationBar = ({ currentPage, totalPages, pageSize, setPageSize, setPage, totalItems, label = "rows" }) => {
    const startItem = totalItems === 0 ? 0 : (currentPage - 1) * pageSize + 1;
    const endItem = Math.min(currentPage * pageSize, totalItems);

    const pages = [];
    const maxButtons = 5;
    let startPage = Math.max(1, currentPage - 2);
    let endPage = Math.min(totalPages, startPage + maxButtons - 1);
    if (endPage - startPage < maxButtons - 1) {
      startPage = Math.max(1, endPage - maxButtons + 1);
    }
    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    return (
      <div
        style={{
          padding: "12px 20px",
          borderTop: "1px solid #e2e8f0",
          background: "#fafafa",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 12,
          fontSize: 12,
          color: "#475569",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <span>
            Showing <strong>{startItem}</strong> to <strong>{endItem}</strong> of <strong>{totalItems}</strong> {label}
          </span>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span>Per page:</span>
            <select
              value={pageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value));
                setPage(1);
              }}
              style={{
                padding: "4px 8px",
                borderRadius: 6,
                border: "1px solid #cbd5e1",
                fontSize: 12,
                fontWeight: 600,
                background: "#fff",
                cursor: "pointer",
              }}
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
              <option value={250}>250</option>
            </select>
          </div>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
          <button
            type="button"
            disabled={currentPage === 1}
            onClick={() => setPage(1)}
            style={{
              padding: "4px 8px",
              borderRadius: 6,
              border: "1px solid #cbd5e1",
              background: currentPage === 1 ? "#f1f5f9" : "#fff",
              color: currentPage === 1 ? "#94a3b8" : "#334155",
              cursor: currentPage === 1 ? "not-allowed" : "pointer",
              fontWeight: 600,
            }}
            title="First Page"
          >
            «
          </button>
          <button
            type="button"
            disabled={currentPage === 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            style={{
              padding: "4px 10px",
              borderRadius: 6,
              border: "1px solid #cbd5e1",
              background: currentPage === 1 ? "#f1f5f9" : "#fff",
              color: currentPage === 1 ? "#94a3b8" : "#334155",
              cursor: currentPage === 1 ? "not-allowed" : "pointer",
              fontWeight: 600,
            }}
            title="Previous Page"
          >
            ‹ Prev
          </button>

          {startPage > 1 && <span style={{ padding: "0 4px", color: "#94a3b8" }}>...</span>}

          {pages.map((p) => (
            <button
              key={p}
              type="button"
              onClick={() => setPage(p)}
              style={{
                padding: "4px 10px",
                borderRadius: 6,
                border: p === currentPage ? "1px solid #4f46e5" : "1px solid #cbd5e1",
                background: p === currentPage ? "#4f46e5" : "#fff",
                color: p === currentPage ? "#fff" : "#334155",
                cursor: "pointer",
                fontWeight: p === currentPage ? 700 : 500,
                minWidth: 30,
              }}
            >
              {p}
            </button>
          ))}

          {endPage < totalPages && <span style={{ padding: "0 4px", color: "#94a3b8" }}>...</span>}

          <button
            type="button"
            disabled={currentPage === totalPages}
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            style={{
              padding: "4px 10px",
              borderRadius: 6,
              border: "1px solid #cbd5e1",
              background: currentPage === totalPages ? "#f1f5f9" : "#fff",
              color: currentPage === totalPages ? "#94a3b8" : "#334155",
              cursor: currentPage === totalPages ? "not-allowed" : "pointer",
              fontWeight: 600,
            }}
            title="Next Page"
          >
            Next ›
          </button>
          <button
            type="button"
            disabled={currentPage === totalPages}
            onClick={() => setPage(totalPages)}
            style={{
              padding: "4px 8px",
              borderRadius: 6,
              border: "1px solid #cbd5e1",
              background: currentPage === totalPages ? "#f1f5f9" : "#fff",
              color: currentPage === totalPages ? "#94a3b8" : "#334155",
              cursor: currentPage === totalPages ? "not-allowed" : "pointer",
              fontWeight: 600,
            }}
            title="Last Page"
          >
            »
          </button>
        </div>
      </div>
    );
  };

  // ──────────────────────────────────────────
  // Render
  // ──────────────────────────────────────────
  return (
    <div style={{ padding: "20px 24px", fontFamily: "'Poppins', sans-serif" }}>
      {/* Page Header */}
      <div
        style={{
          background: "linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)",
          borderRadius: 16,
          padding: "28px 32px",
          marginBottom: 28,
          color: "#fff",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          boxShadow: "0 8px 32px rgba(79,70,229,0.25)",
        }}
      >
        <div>
          <h2 style={{ margin: 0, fontWeight: 700, fontSize: 26, letterSpacing: -0.5 }}>
            🔄 Employee Import / Export
          </h2>
          <p style={{ margin: "6px 0 0", opacity: 0.9, fontSize: 14 }}>
            Direct 1-click Excel Export & Unified Auto Insert / Update Employee Import
          </p>
        </div>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          <button
            className="btn"
            style={{
              background: "#fff",
              color: "#059669",
              fontWeight: 700,
              borderRadius: 8,
              padding: "10px 18px",
              fontSize: 13,
              cursor: "pointer",
              border: "none",
              boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
              display: "flex",
              alignItems: "center",
              gap: 6,
            }}
            onClick={() => setShowColumnModal(true)}
          >
            ⚙️ Customize Columns ({selectedColumnKeys.length || exportColumns.length})
          </button>
          <button
            className="btn"
            style={{
              background: "#fff",
              color: "#4f46e5",
              fontWeight: 700,
              borderRadius: 8,
              padding: "10px 18px",
              fontSize: 13,
              cursor: "pointer",
              border: "none",
              boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
              display: "flex",
              alignItems: "center",
              gap: 6,
            }}
            onClick={() => setShowGuidelinesModal(true)}
          >
            📖 Format Guidelines & Codes
          </button>
          <button
            className="btn"
            style={{
              background: "rgba(255,255,255,0.15)",
              border: "1px solid rgba(255,255,255,0.4)",
              color: "#fff",
              borderRadius: 8,
              padding: "10px 18px",
              fontSize: 13,
              cursor: "pointer",
            }}
            onClick={() => navigate("/EmployeeMaster")}
          >
            ← Back to Employee List
          </button>
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24 }}>
        {/* ── EXPORT CARD ── */}
        <div
          style={{
            background: "#fff",
            borderRadius: 16,
            boxShadow: "0 4px 24px rgba(0,0,0,0.07)",
            overflow: "hidden",
            border: "1px solid #e8e8f0",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <div
            style={{
              background: "linear-gradient(135deg, #059669, #10b981)",
              padding: "20px 24px",
              color: "#fff",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <div>
              <h4 style={{ margin: 0, fontWeight: 700, fontSize: 18 }}>
                📤 Export Employees
              </h4>
              <p style={{ margin: "4px 0 0", opacity: 0.9, fontSize: 13 }}>
                {selectedFirm?.FirmName || selectedFirm?.Name || "Selected Firm"}
              </p>
            </div>
            {exportSummary.totalEmployees > 0 && (
              <span
                style={{
                  background: "rgba(255,255,255,0.22)",
                  color: "#fff",
                  borderRadius: 20,
                  padding: "4px 12px",
                  fontSize: 12,
                  fontWeight: 600,
                }}
              >
                {exportSummary.totalEmployees} Employees
              </span>
            )}
          </div>
          <div style={{ padding: 24, flex: 1, display: "flex", flexDirection: "column" }}>
            {/* Dynamic Summary Cards */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(2, 1fr)",
                gap: 10,
                marginBottom: 18,
              }}
            >
              <div
                style={{
                  background: "#f0fdf4",
                  border: "1px solid #bbf7d0",
                  borderRadius: 10,
                  padding: "10px 14px",
                }}
              >
                <div style={{ fontSize: 11, color: "#166534", fontWeight: 600, textTransform: "uppercase" }}>
                  Total Employees
                </div>
                <div style={{ fontSize: 20, fontWeight: 700, color: "#059669" }}>
                  {exportSummary.loading ? "..." : exportSummary.totalEmployees}
                </div>
              </div>
              <div
                style={{
                  background: "#eff6ff",
                  border: "1px solid #bfdbfe",
                  borderRadius: 10,
                  padding: "10px 14px",
                }}
              >
                <div style={{ fontSize: 11, color: "#1e40af", fontWeight: 600, textTransform: "uppercase" }}>
                  Exportable Fields
                </div>
                <div style={{ fontSize: 20, fontWeight: 700, color: "#2563eb" }}>
                  {exportSummary.loading ? "..." : exportSummary.totalColumns || "—"}
                </div>
              </div>
              <div
                style={{
                  background: "#fff7ed",
                  border: "1px solid #fed7aa",
                  borderRadius: 10,
                  padding: "10px 14px",
                }}
              >
                <div style={{ fontSize: 11, color: "#9a3412", fontWeight: 600, textTransform: "uppercase" }}>
                  Firm Allowances
                </div>
                <div style={{ fontSize: 18, fontWeight: 700, color: "#ea580c" }}>
                  {exportSummary.loading ? "..." : `${exportSummary.allowancesCount} Active`}
                </div>
              </div>
              <div
                style={{
                  background: "#fef2f2",
                  border: "1px solid #fecaca",
                  borderRadius: 10,
                  padding: "10px 14px",
                }}
              >
                <div style={{ fontSize: 11, color: "#991b1b", fontWeight: 600, textTransform: "uppercase" }}>
                  Firm Deductions
                </div>
                <div style={{ fontSize: 18, fontWeight: 700, color: "#dc2626" }}>
                  {exportSummary.loading ? "..." : `${exportSummary.deductionsCount} Active`}
                </div>
              </div>
            </div>

            <div
              style={{
                background: "#fafafa",
                borderRadius: 10,
                padding: "12px 16px",
                marginBottom: 20,
                border: "1px solid #f0f0f5",
                fontSize: 13,
                color: "#4b5563",
                lineHeight: 1.6,
              }}
            >
              ✨ Includes latest effective records for <strong>Department</strong>, <strong>Shift</strong>, <strong>Join</strong>, and <strong>Salary</strong>, with standardized codes (<strong>M/P/D/F</strong>, <strong>YES/NO</strong>, <strong>M/F</strong>, <strong>YYYY-MM-DD</strong>).
            </div>

            <div style={{ marginTop: "auto" }}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  marginBottom: 12,
                  padding: "10px 14px",
                  background: "#f0fdf4",
                  borderRadius: 10,
                  border: "1px solid #bbf7d0",
                  fontSize: 12,
                  color: "#166534",
                }}
              >
                <span>
                  <strong>Columns to Export:</strong> {selectedColumnKeys.length || exportColumns.length} of {exportColumns.length || 0} active
                </span>
                <button
                  type="button"
                  style={{
                    background: "none",
                    border: "none",
                    color: "#059669",
                    fontWeight: 700,
                    cursor: "pointer",
                    textDecoration: "underline",
                    padding: 0,
                    fontSize: 12,
                  }}
                  onClick={() => setShowColumnModal(true)}
                >
                  ⚙️ Select Columns
                </button>
              </div>
              <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                <button
                  type="button"
                  className="btn"
                  style={{
                    flex: 1,
                    background: showExportPreview ? "#047857" : "#f0fdf4",
                    color: showExportPreview ? "#fff" : "#059669",
                    border: "1px solid #059669",
                    borderRadius: 10,
                    padding: "12px 14px",
                    fontWeight: 600,
                    fontSize: 13,
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 6,
                  }}
                  onClick={() => setShowExportPreview((p) => !p)}
                >
                  👁️ {showExportPreview ? "Hide Preview" : `Preview Employees (${existingEmployees.length})`}
                </button>
                <button
                  className="btn"
                  style={{
                    flex: 2,
                    background: "linear-gradient(135deg, #059669, #10b981)",
                    color: "#fff",
                    borderRadius: 10,
                    padding: "12px 18px",
                    fontWeight: 700,
                    fontSize: 14,
                    border: "none",
                    cursor: exporting ? "not-allowed" : "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 8,
                    boxShadow: "0 4px 16px rgba(5,150,105,0.35)",
                    transition: "all 0.2s",
                  }}
                  onClick={handleExport}
                  disabled={exporting}
                >
                  {exporting ? (
                    <>
                      <span className="spinner-border spinner-border-sm" role="status" />
                      Generating Excel...
                    </>
                  ) : (
                    "📥 Export & Download Excel"
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* ── IMPORT CARD (UNIFIED AUTO INSERT / UPDATE) ── */}
        <div
          style={{
            background: "#fff",
            borderRadius: 16,
            boxShadow: "0 4px 24px rgba(0,0,0,0.07)",
            overflow: "hidden",
            border: "1px solid #e8e8f0",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <div
            style={{
              background: "linear-gradient(135deg, #1d4ed8, #3b82f6)",
              padding: "20px 24px",
              color: "#fff",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <div>
              <h4 style={{ margin: 0, fontWeight: 700, fontSize: 18 }}>
                📥 Import Employees (Auto Insert / Update)
              </h4>
              <p style={{ margin: "4px 0 0", opacity: 0.9, fontSize: 13 }}>
                Upload Excel file — matches by <strong>Employee Code (Card No)</strong>
              </p>
            </div>
            <button
              style={{
                background: "rgba(255,255,255,0.2)",
                border: "1px solid rgba(255,255,255,0.4)",
                color: "#fff",
                borderRadius: 6,
                padding: "6px 12px",
                fontSize: 12,
                cursor: "pointer",
                fontWeight: 600,
              }}
              onClick={() => setShowGuidelinesModal(true)}
            >
              ℹ️ Guidelines
            </button>
          </div>

          <div style={{ padding: 24, flex: 1, display: "flex", flexDirection: "column" }}>
            {/* Template download notice */}
            <div
              style={{
                background: "#f0fdf4",
                border: "1px solid #bbf7d0",
                borderRadius: 10,
                padding: "12px 16px",
                marginBottom: 16,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 12,
              }}
            >
              <div>
                <div style={{ fontWeight: 600, fontSize: 13, color: "#166534" }}>
                  💡 Need the template format?
                </div>
                <div style={{ fontSize: 12, color: "#15803d", marginTop: 2 }}>
                  Download the blank template with all firm column headings (no data rows).
                </div>
              </div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <button
                  type="button"
                  className="btn"
                  style={{
                    background: "#f0fdf4",
                    color: "#166534",
                    border: "1px solid #86efac",
                    borderRadius: 6,
                    padding: "6px 12px",
                    fontSize: 12,
                    fontWeight: 600,
                    cursor: "pointer",
                    whiteSpace: "nowrap",
                  }}
                  onClick={() => setShowColumnModal(true)}
                >
                  ⚙️ Select Columns ({selectedColumnKeys.length || exportColumns.length})
                </button>
                <button
                  className="btn"
                  style={{
                    background: "#16a34a",
                    color: "#fff",
                    border: "none",
                    borderRadius: 6,
                    padding: "6px 14px",
                    fontSize: 12,
                    fontWeight: 600,
                    cursor: downloadingTemplate ? "not-allowed" : "pointer",
                    whiteSpace: "nowrap",
                  }}
                  onClick={handleDownloadTemplate}
                  disabled={downloadingTemplate}
                >
                  {downloadingTemplate ? "⏳ Downloading..." : "📄 Download Template (Headings Only)"}
                </button>
              </div>
            </div>

            {/* Matching explanation */}
            <div
              style={{
                background: "#eff6ff",
                border: "1px solid #bfdbfe",
                borderRadius: 10,
                padding: "10px 14px",
                marginBottom: 16,
                fontSize: 12,
                color: "#1e40af",
                lineHeight: 1.5,
              }}
            >
              <strong>⚡ Auto-Matching Rule:</strong>
              <ul style={{ margin: "4px 0 0", paddingLeft: 18 }}>
                <li><strong>Existing Employee:</strong> If <code>Employee Code</code> exists in active firm → <strong>Updates</strong> master, history & allowances.</li>
                <li><strong>New Employee:</strong> If <code>Employee Code</code> is empty or not in active firm → <strong>Inserts</strong> a new employee record.</li>
              </ul>
            </div>

            {/* File Dropzone */}
            <div
              style={{
                border: "2px dashed #cbd5e1",
                borderRadius: 12,
                padding: "24px 20px",
                textAlign: "center",
                background: "#f8fafc",
                cursor: "pointer",
                marginBottom: 16,
                transition: "border-color 0.2s",
              }}
              onClick={() => fileInputRef.current?.click()}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => {
                e.preventDefault();
                const file = e.dataTransfer.files[0];
                if (file) {
                  const fakeEvent = { target: { files: [file], value: "" } };
                  handleFileChange(fakeEvent);
                }
              }}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xls"
                style={{ display: "none" }}
                onChange={handleFileChange}
              />
              <div style={{ fontSize: 32, marginBottom: 8 }}>📁</div>
              {importFile ? (
                <div>
                  <div style={{ fontWeight: 700, fontSize: 14, color: "#1e293b" }}>
                    {importFile.name}
                  </div>
                  <div style={{ fontSize: 12, color: "#64748b", marginTop: 4 }}>
                    {(importFile.size / 1024).toFixed(1)} KB — Click to choose a different file
                  </div>
                </div>
              ) : (
                <div>
                  <div style={{ fontWeight: 600, fontSize: 14, color: "#334155" }}>
                    Click to select or drag & drop an Excel file
                  </div>
                  <div style={{ fontSize: 12, color: "#94a3b8", marginTop: 4 }}>
                    Supports .xlsx and .xls (exported employee sheets or standard templates)
                  </div>
                </div>
              )}
            </div>

            {isParsing && (
              <div style={{ textAlign: "center", padding: "10px 0", color: "#4f46e5" }}>
                <span className="spinner-border spinner-border-sm me-2" role="status" />
                Parsing Excel file...
              </div>
            )}

            {/* Action buttons */}
            <div style={{ marginTop: "auto", display: "flex", gap: 10 }}>
              {importFile && (
                <button
                  className="btn"
                  style={{
                    flex: 1,
                    background: "#f1f5f9",
                    color: "#475569",
                    border: "1px solid #cbd5e1",
                    borderRadius: 10,
                    padding: "12px",
                    fontWeight: 600,
                    fontSize: 14,
                    cursor: "pointer",
                  }}
                  onClick={resetImport}
                  disabled={importing}
                >
                  ✕ Clear
                </button>
              )}
              <button
                className="btn"
                style={{
                  flex: 2,
                  background:
                    !importFile || previewData.length === 0 || importing
                      ? "#94a3b8"
                      : "linear-gradient(135deg, #1d4ed8, #3b82f6)",
                  color: "#fff",
                  borderRadius: 10,
                  padding: "12px 18px",
                  fontWeight: 700,
                  fontSize: 14,
                  border: "none",
                  cursor: !importFile || previewData.length === 0 || importing ? "not-allowed" : "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 8,
                  boxShadow:
                    importFile && previewData.length > 0 && !importing
                      ? "0 4px 14px rgba(29,78,216,0.35)"
                      : "none",
                  transition: "all 0.2s",
                }}
                onClick={handleImport}
                disabled={!importFile || previewData.length === 0 || importing}
              >
                {importing ? (
                  <>
                    <span className="spinner-border spinner-border-sm" role="status" />
                    Importing ({importProgress}%)...
                  </>
                ) : (
                  `🚀 Run Import (${previewData.length} rows)`
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* ── EXPORT EMPLOYEES PREVIEW TABLE (PAGINATED) ── */}
      {showExportPreview && (
        <div
          style={{
            marginTop: 24,
            background: "#fff",
            borderRadius: 16,
            boxShadow: "0 4px 24px rgba(0,0,0,0.07)",
            overflow: "hidden",
            border: "1px solid #e8e8f0",
          }}
        >
          <div
            style={{
              padding: "16px 24px",
              borderBottom: "1px solid #f0f0f5",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              background: "#fafafa",
              flexWrap: "wrap",
              gap: 12,
            }}
          >
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <h5 style={{ margin: 0, fontWeight: 700, fontSize: 16, color: "#1f2937" }}>
                  📤 Existing Employees Preview (Export Data)
                </h5>
                <span
                  style={{
                    background: "#f0fdf4",
                    color: "#059669",
                    border: "1px solid #bbf7d0",
                    borderRadius: 12,
                    padding: "2px 10px",
                    fontSize: 11,
                    fontWeight: 700,
                  }}
                >
                  {filteredExportEmployees.length} Matching of {existingEmployees.length} Total
                </span>
              </div>
              <div style={{ fontSize: 12, color: "#6b7280", marginTop: 4 }}>
                Displaying columns configured for export. Use pagination to browse without browser lag.
              </div>
            </div>

            <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
              <input
                type="text"
                placeholder="🔍 Search employees..."
                value={exportSearch}
                onChange={(e) => setExportSearch(e.target.value)}
                style={{
                  padding: "6px 12px",
                  borderRadius: 8,
                  border: "1px solid #d1d5db",
                  fontSize: 12,
                  outline: "none",
                  width: 200,
                }}
              />
              <button
                className="btn btn-sm"
                style={{
                  background: "linear-gradient(135deg, #059669, #10b981)",
                  color: "#fff",
                  borderRadius: 6,
                  fontWeight: 600,
                  padding: "6px 16px",
                  whiteSpace: "nowrap",
                }}
                onClick={handleExport}
                disabled={exporting}
              >
                {exporting ? "Downloading..." : "📥 Download Excel"}
              </button>
            </div>
          </div>

          <div style={{ overflowX: "auto", maxHeight: 440 }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
              <thead>
                <tr>
                  <th style={stickyTh(0, 4, { width: 45, textAlign: "center" })}>#</th>
                  <th style={stickyTh(45, 4, { width: 120 })}>Employee Code</th>
                  <th style={stickyTh(165, 4, { width: 160, boxShadow: "3px 0 6px -2px rgba(0,0,0,0.12)" })}>
                    Employee Name
                  </th>
                  {(exportColumns || []).filter((c) => !selectedColumnKeys.length || selectedColumnKeys.includes(c.key)).map((col) => (
                    <th key={col.key} style={thStyle} title={col.key}>
                      {col.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {paginatedExportEmployees.map((emp, idx) => {
                  const rowBg = idx % 2 === 0 ? "#fff" : "#fafafa";
                  const globalSNo = (currentExportPage - 1) * exportPageSize + idx + 1;
                  const activeCols = (exportColumns || []).filter((c) => !selectedColumnKeys.length || selectedColumnKeys.includes(c.key));

                  return (
                    <tr key={emp.Id || idx} style={{ background: rowBg, borderBottom: "1px solid #f0f0f5" }}>
                      <td style={stickyTd(0, 3, rowBg, { width: 45, textAlign: "center" })}>{globalSNo}</td>
                      <td style={stickyTd(45, 3, rowBg, { width: 120, fontWeight: 600, color: "#1d4ed8" })}>
                        {emp.EmployeeCode || emp.CardNo || "-"}
                      </td>
                      <td style={stickyTd(165, 3, rowBg, { width: 160, fontWeight: 600, boxShadow: "3px 0 6px -2px rgba(0,0,0,0.12)" })}>
                        {emp.Name || "-"}
                      </td>
                      {activeCols.map((col) => (
                        <td key={col.key} style={tdStyle} title={`${col.label}: ${emp[col.key] ?? ""}`}>
                          {renderPreviewCell(col.key, emp[col.key], emp)}
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <PaginationBar
            currentPage={currentExportPage}
            totalPages={exportTotalPages}
            pageSize={exportPageSize}
            setPageSize={setExportPageSize}
            setPage={setExportPage}
            totalItems={filteredExportEmployees.length}
            label="employees"
          />
        </div>
      )}

      {/* ── PREVIEW TABLE ── */}
      {showPreview && previewData.length > 0 && (
        <div
          style={{
            marginTop: 28,
            background: "#fff",
            borderRadius: 16,
            boxShadow: "0 4px 24px rgba(0,0,0,0.07)",
            overflow: "hidden",
            border: "1px solid #e8e8f0",
          }}
        >
          {/* Header Bar */}
          <div
            style={{
              padding: "16px 24px",
              borderBottom: "1px solid #f0f0f5",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              background: "#fafafa",
              flexWrap: "wrap",
              gap: 12,
            }}
          >
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <h5 style={{ margin: 0, fontWeight: 700, fontSize: 16, color: "#1f2937" }}>
                  📋 Import Data Preview
                </h5>
                <span
                  style={{
                    background: "#ecfdf5",
                    color: "#059669",
                    border: "1px solid #a7f3d0",
                    borderRadius: 12,
                    padding: "2px 10px",
                    fontSize: 11,
                    fontWeight: 700,
                  }}
                >
                  ⚡ {previewColumns.length + 2} Columns Active
                </span>
              </div>
              <div style={{ display: "flex", gap: 8, marginTop: 6, flexWrap: "wrap" }}>
                <span style={{ background: "#f1f5f9", color: "#475569", borderRadius: 12, padding: "2px 10px", fontSize: 12, fontWeight: 600 }}>
                  Total Rows: {previewData.length}
                </span>
                <span style={{ background: "#05966915", color: "#059669", borderRadius: 12, padding: "2px 10px", fontSize: 12, fontWeight: 600 }}>
                  Valid: {validRows}
                </span>
                <span style={{ background: "#e0e7ff", color: "#3730a3", borderRadius: 12, padding: "2px 10px", fontSize: 12, fontWeight: 600 }}>
                  + Insert: {insertCount}
                </span>
                <span style={{ background: "#fef3c7", color: "#92400e", borderRadius: 12, padding: "2px 10px", fontSize: 12, fontWeight: 600 }}>
                  ✏️ Update: {updateCount}
                </span>
                {existingCount > 0 && (
                  <span style={{ background: "#f1f5f9", color: "#64748b", border: "1px solid #e2e8f0", borderRadius: 12, padding: "2px 10px", fontSize: 12, fontWeight: 600 }}>
                    — Existing: {existingCount}
                  </span>
                )}
                {invalidRows > 0 && (
                  <span style={{ background: "#fee2e2", color: "#991b1b", borderRadius: 12, padding: "2px 10px", fontSize: 12, fontWeight: 600 }}>
                    ✗ Errors: {invalidRows}
                  </span>
                )}
                {newDeptsDetected.length > 0 && (
                  <span
                    style={{
                      background: "#ecfdf5",
                      color: "#065f46",
                      border: "1px solid #a7f3d0",
                      borderRadius: 12,
                      padding: "2px 10px",
                      fontSize: 12,
                      fontWeight: 700,
                    }}
                    title={`New Departments: ${newDeptsDetected.join(", ")}`}
                  >
                    ✨ New Depts: {newDeptsDetected.length}
                  </span>
                )}
                {newDesigsDetected.length > 0 && (
                  <span
                    style={{
                      background: "#eff6ff",
                      color: "#1e40af",
                      border: "1px solid #bfdbfe",
                      borderRadius: 12,
                      padding: "2px 10px",
                      fontSize: 12,
                      fontWeight: 700,
                    }}
                    title={`New Designations: ${newDesigsDetected.join(", ")}`}
                  >
                    ✨ New Desigs: {newDesigsDetected.length}
                  </span>
                )}
              </div>
            </div>

            <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
              <input
                type="text"
                placeholder="🔍 Search rows..."
                value={previewSearch}
                onChange={(e) => setPreviewSearch(e.target.value)}
                style={{
                  padding: "6px 12px",
                  borderRadius: 8,
                  border: "1px solid #d1d5db",
                  fontSize: 12,
                  outline: "none",
                  width: 180,
                }}
              />
              <button
                className="btn btn-sm"
                style={{
                  background: "#4f46e5",
                  color: "#fff",
                  borderRadius: 6,
                  fontWeight: 600,
                  padding: "6px 16px",
                  whiteSpace: "nowrap",
                }}
                onClick={handleImport}
                disabled={importing || validRows === 0}
              >
                {importing ? "Importing..." : `Import ${validRows} Valid Rows`}
              </button>
            </div>
          </div>

          {/* Informative banner if new departments or designations are detected */}
          {(newDeptsDetected.length > 0 || newDesigsDetected.length > 0) && (
            <div
              style={{
                background: "#f0fdf4",
                borderBottom: "1px solid #bbf7d0",
                padding: "10px 24px",
                display: "flex",
                alignItems: "center",
                gap: 10,
                fontSize: 12,
                color: "#166534",
                flexWrap: "wrap",
              }}
            >
              <span style={{ fontSize: 16 }}>ℹ️</span>
              <div>
                <strong>Auto-Create Notice: </strong>
                {newDeptsDetected.length > 0 && (
                  <span>
                    New Department(s) detected: <strong>{newDeptsDetected.join(", ")}</strong>.{" "}
                  </span>
                )}
                {newDesigsDetected.length > 0 && (
                  <span>
                    New Designation(s) detected: <strong>{newDesigsDetected.join(", ")}</strong>.{" "}
                  </span>
                )}
                <span>These will be automatically created & saved into Masters upon import.</span>
              </div>
            </div>
          )}

          {/* Table with Sticky Left Columns and Dynamic Headers */}
          <div style={{ overflowX: "auto", maxHeight: 480 }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
              <thead>
                <tr>
                  <th style={stickyTh(0, 4, { width: 45, textAlign: "center" })}>#</th>
                  <th style={stickyTh(45, 4, { width: 95 })}>Status</th>
                  <th style={stickyTh(140, 4, { width: 90 })}>Type</th>
                  <th style={stickyTh(230, 4, { width: 170 })}>Validation / Errors</th>
                  <th style={stickyTh(400, 4, { width: 120 })}>Employee Code</th>
                  <th style={stickyTh(520, 4, { width: 160, boxShadow: "3px 0 6px -2px rgba(0,0,0,0.12)" })}>
                    Employee Name
                  </th>
                  <th style={{ ...thStyle, minWidth: 220, background: "#f8fafc", color: "#0f172a" }}>
                    Applied Allowances &amp; Deductions
                  </th>
                  {/* Dynamic Imported Columns */}
                  {previewColumns.map((col) => (
                    <th key={col.key} style={thStyle} title={col.key}>
                      {col.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {paginatedPreviewData.map((row, idx) => {
                  const rowBg =
                    row.status === "success"
                      ? "#f0fdf4"
                      : row.status === "failed"
                      ? "#fff1f2"
                      : row.status === "skipped"
                      ? "#fff7ed"
                      : row.status === "error"
                      ? "#fff1f2"
                      : idx % 2 === 0
                      ? "#fff"
                      : "#fafafa";

                  const globalIdx = (currentImportPage - 1) * importPageSize + idx;

                  return (
                    <tr
                      key={idx}
                      style={{
                        background: rowBg,
                        borderBottom: "1px solid #f0f0f5",
                      }}
                    >
                      {/* Sticky Identification Columns */}
                      <td style={stickyTd(0, 3, rowBg, { width: 45, textAlign: "center" })} title={`Excel Row: ${row.rowIndex}`}>
                        {row.sNo || globalIdx + 1}
                      </td>
                      <td style={stickyTd(45, 3, rowBg, { width: 95 })}>
                        <StatusBadge status={row.status} action={row.apiAction} />
                      </td>
                      <td style={stickyTd(140, 3, rowBg, { width: 90 })}>
                        <TypeBadge type={row.type} />
                      </td>
                      <td
                        style={stickyTd(230, 3, rowBg, {
                          width: 170,
                          color:
                            row.errors?.length > 0
                              ? "#dc3545"
                              : row.apiError
                              ? "#dc3545"
                              : row.status === "success"
                              ? "#059669"
                              : "#6b7280",
                          fontWeight: row.errors?.length > 0 ? 600 : 500,
                        })}
                      >
                        {row.errors?.length > 0
                          ? row.errors.join(", ")
                          : row.apiError
                          ? row.apiError
                          : row.status === "success"
                          ? row.apiAction === "INSERTED"
                            ? "✓ Inserted"
                            : row.apiAction === "UNCHANGED"
                            ? "— Unchanged"
                            : "✓ Updated"
                          : row.type === "EXISTING"
                          ? "No changes (Existing)"
                          : row.type === "UPDATE"
                          ? (row.matchedBy === "NAME_FATHER" ? "Update (Name & Father Match)" : "Update")
                          : "Ready"}
                      </td>
                      <td style={stickyTd(400, 3, rowBg, { width: 120, fontWeight: 600, color: "#1d4ed8" })}>
                        {row.data.EmployeeCode || row.data.CardNo || <em style={{ color: "#9ca3af" }}>Auto-Gen</em>}
                        {row.matchedBy === "NAME_FATHER" && (
                          <div style={{ fontSize: 10, color: "#16a34a", fontWeight: 500, lineHeight: 1.2 }}>
                            Matched (Name &amp; Father)
                          </div>
                        )}
                      </td>
                      <td style={stickyTd(520, 3, rowBg, { width: 160, fontWeight: 600, boxShadow: "3px 0 6px -2px rgba(0,0,0,0.12)" })}>
                        {row.data.Name}
                      </td>
                      <td style={{ ...tdStyle, minWidth: 220, verticalAlign: "middle" }}>
                        {((row.data.Allowances && row.data.Allowances.length > 0) || (row.data.Deductions && row.data.Deductions.length > 0)) ? (
                          <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                            {row.data.Allowances && row.data.Allowances.length > 0 && (
                              <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                                {row.data.Allowances.map((a, i) => {
                                  const foundA = (firmAllowancesList || []).find((fa) => Number(fa.Id || fa.id) === Number(a.F_AllowanceMaster));
                                  const aName = foundA?.Name || foundA?.name || `Allow ${a.F_AllowanceMaster}`;
                                  return (
                                    <span key={i} style={{ background: "#ecfdf5", color: "#047857", border: "1px solid #a7f3d0", padding: "1px 6px", borderRadius: 4, fontSize: 11, fontWeight: 700, whiteSpace: "nowrap" }}>
                                      {aName}: ₹{Number(a.Amount).toLocaleString("en-IN")}
                                    </span>
                                  );
                                })}
                              </div>
                            )}
                            {row.data.Deductions && row.data.Deductions.length > 0 && (
                              <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                                {row.data.Deductions.map((d, i) => {
                                  const foundD = (firmDeductionsList || []).find((fd) => Number(fd.Id || fd.id) === Number(d.F_DeductionMaster));
                                  const dName = foundD?.Name || foundD?.name || `Ded ${d.F_DeductionMaster}`;
                                  return (
                                    <span key={i} style={{ background: "#fff1f2", color: "#be123c", border: "1px solid #fecdd3", padding: "1px 6px", borderRadius: 4, fontSize: 11, fontWeight: 700, whiteSpace: "nowrap" }}>
                                      {dName}: ₹{Number(d.Amount).toLocaleString("en-IN")}
                                    </span>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        ) : (
                          <span style={{ color: "#9ca3af", fontStyle: "italic" }}>—</span>
                        )}
                      </td>

                      {/* Dynamic Columns Data */}
                      {previewColumns.map((col) => (
                        <td key={col.key} style={tdStyle} title={`${col.label}: ${row.data[col.key] ?? ""}`}>
                          {renderPreviewCell(col.key, row.data[col.key], row.data)}
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Import Pagination Footer */}
          <PaginationBar
            currentPage={currentImportPage}
            totalPages={importTotalPages}
            pageSize={importPageSize}
            setPageSize={setImportPageSize}
            setPage={setImportPage}
            totalItems={filteredPreviewData.length}
            label="rows"
          />
        </div>
      )}

      {/* ── FORMAT GUIDELINES & CODES MODAL ── */}
      {showGuidelinesModal && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.55)",
            zIndex: 9999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 20,
            backdropFilter: "blur(4px)",
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) setShowGuidelinesModal(false);
          }}
        >
          <div
            style={{
              background: "#fff",
              borderRadius: 20,
              width: "100%",
              maxWidth: 860,
              maxHeight: "90vh",
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
              boxShadow: "0 24px 64px rgba(0,0,0,0.25)",
            }}
          >
            {/* Modal Header */}
            <div
              style={{
                background: "linear-gradient(135deg, #4f46e5, #7c3aed)",
                padding: "20px 24px",
                color: "#fff",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <div>
                <h4 style={{ margin: 0, fontWeight: 700, fontSize: 18 }}>
                  📖 Format Guidelines & Code Definitions
                </h4>
                <p style={{ margin: "4px 0 0", opacity: 0.9, fontSize: 13 }}>
                  Cheat sheet of standard codes, acceptable formats, and matching rules
                </p>
              </div>
              <button
                style={smallBtnStyle("#fff", "#666")}
                onClick={() => setShowGuidelinesModal(false)}
              >
                ✕ Close
              </button>
            </div>

            {/* Search Bar */}
            <div
              style={{
                padding: "12px 24px",
                background: "#f9fafb",
                borderBottom: "1px solid #e5e7eb",
                display: "flex",
                gap: 10,
              }}
            >
              <input
                type="text"
                placeholder="🔍 Search guidelines (e.g. Calculation, Gender, Date, PF, Allowance, CardNo)..."
                value={guidelineSearch}
                onChange={(e) => setGuidelineSearch(e.target.value)}
                style={{
                  flex: 1,
                  padding: "8px 14px",
                  borderRadius: 8,
                  border: "1px solid #d1d5db",
                  fontSize: 13,
                  outline: "none",
                }}
              />
              {guidelineSearch && (
                <button
                  onClick={() => setGuidelineSearch("")}
                  style={{
                    background: "#e5e7eb",
                    border: "none",
                    borderRadius: 6,
                    padding: "6px 12px",
                    fontSize: 12,
                    cursor: "pointer",
                  }}
                >
                  Clear
                </button>
              )}
            </div>

            {/* Modal Body */}
            <div style={{ overflowY: "auto", padding: 24, flex: 1 }}>
              {filteredGuidelines.map((cat, idx) => (
                <div key={idx} style={{ marginBottom: 24 }}>
                  <h6
                    style={{
                      fontWeight: 700,
                      fontSize: 14,
                      color: "#4338ca",
                      borderBottom: "2px solid #e0e7ff",
                      paddingBottom: 6,
                      marginBottom: 12,
                    }}
                  >
                    {cat.category}
                  </h6>
                  <div style={{ display: "grid", gap: 12 }}>
                    {cat.fields.map((f, fIdx) => (
                      <div
                        key={fIdx}
                        style={{
                          background: "#f8fafc",
                          border: "1px solid #e2e8f0",
                          borderRadius: 10,
                          padding: "12px 16px",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            marginBottom: 6,
                          }}
                        >
                          <span style={{ fontWeight: 700, fontSize: 13, color: "#1e293b" }}>
                            {f.field}
                          </span>
                          <span
                            style={{
                              background: "#e0e7ff",
                              color: "#4338ca",
                              borderRadius: 6,
                              padding: "2px 8px",
                              fontSize: 11,
                              fontWeight: 600,
                            }}
                          >
                            Format: {f.format}
                          </span>
                        </div>
                        <div style={{ fontSize: 12, color: "#334155", lineHeight: 1.5 }}>
                          <div><strong>Allowed Values / Codes:</strong> <code style={{ background: "#e2e8f0", padding: "1px 6px", borderRadius: 4 }}>{f.allowed}</code></div>
                          <div style={{ marginTop: 4, whiteSpace: "pre-line" }}><strong>Rule:</strong> {f.rule}</div>
                          <div style={{ marginTop: 4, color: "#059669" }}>
                            <strong>Example Value:</strong> <code>{f.example}</code>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {/* Modal Footer */}
            <div
              style={{
                padding: "14px 24px",
                borderTop: "1px solid #f0f0f5",
                display: "flex",
                justifyContent: "flex-end",
                background: "#fafafa",
              }}
            >
              <button
                className="btn"
                style={{
                  background: "linear-gradient(135deg, #4f46e5, #7c3aed)",
                  color: "#fff",
                  borderRadius: 8,
                  padding: "8px 24px",
                  fontWeight: 600,
                  fontSize: 13,
                  border: "none",
                  cursor: "pointer",
                }}
                onClick={() => setShowGuidelinesModal(false)}
              >
                Got It!
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── CUSTOMIZE COLUMNS MODAL (Firm-Wise Persistent) ── */}
      {showColumnModal && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.55)",
            zIndex: 9999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 20,
            backdropFilter: "blur(4px)",
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) setShowColumnModal(false);
          }}
        >
          <div
            style={{
              background: "#fff",
              borderRadius: 20,
              width: "100%",
              maxWidth: 900,
              maxHeight: "90vh",
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
              boxShadow: "0 24px 64px rgba(0,0,0,0.25)",
            }}
          >
            {/* Header */}
            <div
              style={{
                background: "linear-gradient(135deg, #059669 0%, #10b981 100%)",
                padding: "20px 24px",
                color: "#fff",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <div>
                <h4 style={{ margin: 0, fontWeight: 700, fontSize: 18 }}>
                  ⚙️ Select Export & Template Columns
                </h4>
                <p style={{ margin: "4px 0 0", opacity: 0.9, fontSize: 13 }}>
                  Firm: <strong>{selectedFirm?.FirmName || selectedFirm?.Name || "Active Firm"}</strong> &bull; Selected: <strong>{selectedColumnKeys.length}</strong> of {exportColumns.length} columns
                </p>
              </div>
              <button
                style={smallBtnStyle("#fff", "#666")}
                onClick={() => setShowColumnModal(false)}
              >
                ✕ Close
              </button>
            </div>

            {/* Quick Actions & Search Toolbar */}
            <div
              style={{
                padding: "14px 24px",
                background: "#f8fafc",
                borderBottom: "1px solid #e2e8f0",
                display: "flex",
                flexWrap: "wrap",
                gap: 12,
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <div style={{ flex: "1 1 260px", minWidth: 220 }}>
                <input
                  type="text"
                  placeholder="🔍 Search columns or categories..."
                  value={columnSearch}
                  onChange={(e) => setColumnSearch(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "8px 14px",
                    borderRadius: 8,
                    border: "1px solid #cbd5e1",
                    fontSize: 13,
                    outline: "none",
                  }}
                />
              </div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <button
                  type="button"
                  onClick={handleSelectAllColumns}
                  style={{
                    background: "#e0e7ff",
                    color: "#4338ca",
                    border: "none",
                    borderRadius: 6,
                    padding: "6px 12px",
                    fontSize: 12,
                    fontWeight: 600,
                    cursor: "pointer",
                  }}
                >
                  ✓ Select All
                </button>
                <button
                  type="button"
                  onClick={handleSelectCoreColumns}
                  style={{
                    background: "#fef3c7",
                    color: "#92400e",
                    border: "none",
                    borderRadius: 6,
                    padding: "6px 12px",
                    fontSize: 12,
                    fontWeight: 600,
                    cursor: "pointer",
                  }}
                >
                  ★ Core Only
                </button>
                <button
                  type="button"
                  onClick={handleDeselectAllColumns}
                  style={{
                    background: "#f1f5f9",
                    color: "#64748b",
                    border: "none",
                    borderRadius: 6,
                    padding: "6px 12px",
                    fontSize: 12,
                    fontWeight: 600,
                    cursor: "pointer",
                  }}
                >
                  ✗ Clear (Min)
                </button>
              </div>
            </div>

            {/* Modal Body - Category Accordions / Groups */}
            <div style={{ overflowY: "auto", padding: "20px 24px", flex: 1, background: "#fcfdfd" }}>
              {Object.keys(filteredGroupedColumns).length === 0 ? (
                <div style={{ textAlign: "center", padding: "40px 0", color: "#94a3b8" }}>
                  No columns match &quot;{columnSearch}&quot;
                </div>
              ) : (
                Object.entries(filteredGroupedColumns).map(([category, cols]) => {
                  const catKeys = cols.map((c) => c.key);
                  const selectedCount = catKeys.filter((k) => selectedColumnKeys.includes(k)).length;
                  const allSelected = catKeys.length > 0 && selectedCount === catKeys.length;

                  return (
                    <div
                      key={category}
                      style={{
                        marginBottom: 20,
                        background: "#fff",
                        border: "1px solid #e2e8f0",
                        borderRadius: 12,
                        overflow: "hidden",
                        boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
                      }}
                    >
                      {/* Category Header */}
                      <div
                        style={{
                          padding: "10px 16px",
                          background: "#f8fafc",
                          borderBottom: "1px solid #e2e8f0",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                        }}
                      >
                        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                          <span style={{ fontWeight: 700, fontSize: 13, color: "#1e293b" }}>
                            {category}
                          </span>
                          <span
                            style={{
                              background: selectedCount > 0 ? "#ecfdf5" : "#f1f5f9",
                              color: selectedCount > 0 ? "#059669" : "#64748b",
                              border: `1px solid ${selectedCount > 0 ? "#a7f3d0" : "#e2e8f0"}`,
                              borderRadius: 12,
                              padding: "2px 8px",
                              fontSize: 11,
                              fontWeight: 600,
                            }}
                          >
                            {selectedCount} / {cols.length} active
                          </span>
                        </div>
                        <button
                          type="button"
                          onClick={() => handleToggleCategory(cols)}
                          style={{
                            background: "transparent",
                            border: "none",
                            color: allSelected ? "#dc2626" : "#2563eb",
                            fontSize: 12,
                            fontWeight: 600,
                            cursor: "pointer",
                            padding: "4px 8px",
                          }}
                        >
                          {allSelected ? "Deselect Group" : "Select Group"}
                        </button>
                      </div>

                      {/* Columns Grid */}
                      <div
                        style={{
                          padding: 14,
                          display: "grid",
                          gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))",
                          gap: 8,
                        }}
                      >
                        {cols.map((col) => {
                          const isSelected = selectedColumnKeys.includes(col.key);
                          const isRequired = col.key === "Name";

                          return (
                            <label
                              key={col.key}
                              style={{
                                display: "flex",
                                alignItems: "flex-start",
                                gap: 10,
                                padding: "8px 12px",
                                borderRadius: 8,
                                background: isSelected ? "#f0fdf4" : "#ffffff",
                                border: isSelected ? "1px solid #86efac" : "1px solid #f1f5f9",
                                cursor: isRequired ? "not-allowed" : "pointer",
                                transition: "all 0.15s ease",
                                userSelect: "none",
                              }}
                              onClick={(e) => {
                                if (isRequired) e.preventDefault();
                              }}
                            >
                              <input
                                type="checkbox"
                                checked={isSelected}
                                disabled={isRequired}
                                onChange={() => handleToggleColumn(col.key)}
                                style={{ marginTop: 3, accentColor: "#059669", cursor: isRequired ? "not-allowed" : "pointer" }}
                              />
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div
                                  style={{
                                    fontSize: 12,
                                    fontWeight: isSelected ? 600 : 500,
                                    color: isSelected ? "#15803d" : "#334155",
                                    lineHeight: 1.3,
                                    wordBreak: "break-word",
                                  }}
                                >
                                  {col.label}
                                  {isRequired && (
                                    <span
                                      style={{
                                        marginLeft: 6,
                                        fontSize: 10,
                                        color: "#dc2626",
                                        fontWeight: 700,
                                      }}
                                    >
                                      *Required
                                    </span>
                                  )}
                                </div>
                                <div
                                  style={{
                                    fontSize: 10,
                                    color: "#94a3b8",
                                    marginTop: 2,
                                    fontFamily: "monospace",
                                  }}
                                >
                                  {col.key}
                                </div>
                              </div>
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            {/* Modal Footer with Persistent Save & Apply */}
            <div
              style={{
                padding: "16px 24px",
                borderTop: "1px solid #e2e8f0",
                background: "#f8fafc",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 16,
                flexWrap: "wrap",
              }}
            >
              <div style={{ fontSize: 12, color: "#64748b", maxWidth: 440 }}>
                💾 <strong>Save as Firm Default</strong> will store this selection in the database so you won&apos;t have to re-select columns for this firm again.
              </div>

              <div style={{ display: "flex", gap: 10 }}>
                <button
                  type="button"
                  style={{
                    background: "#fff",
                    border: "1px solid #cbd5e1",
                    color: "#475569",
                    borderRadius: 8,
                    padding: "8px 18px",
                    fontWeight: 600,
                    fontSize: 13,
                    cursor: "pointer",
                  }}
                  onClick={() => setShowColumnModal(false)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  style={{
                    background: "#f1f5f9",
                    border: "1px solid #cbd5e1",
                    color: "#1e293b",
                    borderRadius: 8,
                    padding: "8px 18px",
                    fontWeight: 600,
                    fontSize: 13,
                    cursor: "pointer",
                  }}
                  onClick={() => {
                    setShowColumnModal(false);
                    toast.info(`Selected ${selectedColumnKeys.length} columns for this session.`);
                  }}
                >
                  Apply (This Session Only)
                </button>
                <button
                  type="button"
                  style={{
                    background: "linear-gradient(135deg, #059669, #10b981)",
                    color: "#fff",
                    border: "none",
                    borderRadius: 8,
                    padding: "8px 20px",
                    fontWeight: 700,
                    fontSize: 13,
                    cursor: savingPreferences ? "not-allowed" : "pointer",
                    boxShadow: "0 2px 8px rgba(5,150,105,0.3)",
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                  }}
                  onClick={handleSavePreferences}
                  disabled={savingPreferences}
                >
                  {savingPreferences ? "Saving..." : "💾 Save as Firm Default"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── RESULT MODAL ── */}
      {showResultModal && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.55)",
            zIndex: 9999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 20,
            backdropFilter: "blur(4px)",
          }}
        >
          <div
            style={{
              background: "#fff",
              borderRadius: 20,
              width: "100%",
              maxWidth: 600,
              boxShadow: "0 24px 64px rgba(0,0,0,0.2)",
              overflow: "hidden",
            }}
          >
            <div
              style={{
                background:
                  importResults.failed > 0
                    ? "linear-gradient(135deg, #d97706, #f59e0b)"
                    : "linear-gradient(135deg, #059669, #10b981)",
                padding: "24px",
                color: "#fff",
                textAlign: "center",
              }}
            >
              <div style={{ fontSize: 48, marginBottom: 8 }}>
                {importResults.failed === 0 ? "🎉" : "⚠️"}
              </div>
              <h4 style={{ margin: 0, fontWeight: 700 }}>Import Finished!</h4>
              <p style={{ margin: "6px 0 0", opacity: 0.95, fontSize: 14 }}>
                Batch processed across {importResults.total} total rows
              </p>
            </div>
            <div style={{ padding: 24 }}>
              {/* Stats grid */}
              <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 10, marginBottom: 20 }}>
                {[
                  { label: "Total Rows", value: importResults.total, color: "#6b7280", bg: "#f4f4f8" },
                  { label: "Inserted", value: importResults.inserted, color: "#059669", bg: "#f0fdf4" },
                  { label: "Updated", value: importResults.updated, color: "#d97706", bg: "#fffbeb" },
                  { label: "Unchanged", value: importResults.unchanged ?? 0, color: "#64748b", bg: "#f1f5f9" },
                  { label: "Failed", value: importResults.failed, color: "#dc3545", bg: "#fff1f2" },
                ].map((s) => (
                  <div
                    key={s.label}
                    style={{
                      background: s.bg,
                      borderRadius: 10,
                      padding: "12px 8px",
                      textAlign: "center",
                    }}
                  >
                    <div style={{ fontSize: 24, fontWeight: 700, color: s.color }}>{s.value}</div>
                    <div style={{ fontSize: 11, color: "#6b7280", marginTop: 2, fontWeight: 600 }}>
                      {s.label}
                    </div>
                  </div>
                ))}
              </div>

              {/* Auto-created Masters Information */}
              {(importResults.createdDepartments?.length > 0 || importResults.createdDesignations?.length > 0) && (
                <div
                  style={{
                    background: "#f0fdf4",
                    border: "1px solid #86efac",
                    borderRadius: 12,
                    padding: "14px 16px",
                    marginBottom: 18,
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 8, color: "#166534", fontWeight: 700, fontSize: 13, marginBottom: 8 }}>
                    <span style={{ fontSize: 16 }}>✨</span>
                    <span>New Masters Auto-Created & Saved to Database:</span>
                  </div>
                  <p style={{ margin: "0 0 10px", fontSize: 12, color: "#15803d" }}>
                    New records were automatically created in Masters and assigned to imported employees:
                  </p>
                  {importResults.createdDepartments?.length > 0 && (
                    <div style={{ marginBottom: importResults.createdDesignations?.length > 0 ? 8 : 0 }}>
                      <div style={{ fontSize: 12, color: "#166534", fontWeight: 700, marginBottom: 4 }}>
                        📁 Departments ({importResults.createdDepartments.length}):
                      </div>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                        {importResults.createdDepartments.map((dept, i) => (
                          <span
                            key={i}
                            style={{
                              background: "#dcfce7",
                              color: "#166534",
                              border: "1px solid #bbf7d0",
                              borderRadius: 6,
                              padding: "2px 8px",
                              fontSize: 12,
                              fontWeight: 600,
                            }}
                          >
                            ✓ {dept}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  {importResults.createdDesignations?.length > 0 && (
                    <div>
                      <div style={{ fontSize: 12, color: "#166534", fontWeight: 700, marginBottom: 4 }}>
                        💼 Designations ({importResults.createdDesignations.length}):
                      </div>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                        {importResults.createdDesignations.map((desig, i) => (
                          <span
                            key={i}
                            style={{
                              background: "#e0e7ff",
                              color: "#3730a3",
                              border: "1px solid #c7d2fe",
                              borderRadius: 6,
                              padding: "2px 8px",
                              fontSize: 12,
                              fontWeight: 600,
                            }}
                          >
                            ✓ {desig}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Error details */}
              {importResults.errors.length > 0 && (
                <div
                  style={{
                    background: "#fff8f8",
                    border: "1px solid #fecaca",
                    borderRadius: 10,
                    padding: 14,
                    marginBottom: 16,
                    maxHeight: 160,
                    overflowY: "auto",
                  }}
                >
                  <div style={{ fontWeight: 600, fontSize: 13, color: "#dc3545", marginBottom: 8 }}>
                    Error Details:
                  </div>
                  {importResults.errors.map((e, i) => (
                    <div
                      key={i}
                      style={{ fontSize: 12, color: "#6b7280", marginBottom: 4, fontFamily: "monospace" }}
                    >
                      • {e}
                    </div>
                  ))}
                </div>
              )}

              <div style={{ display: "flex", gap: 10 }}>
                <button
                  className="btn"
                  style={{
                    flex: 1,
                    background: "linear-gradient(135deg, #4f46e5, #7c3aed)",
                    color: "#fff",
                    border: "none",
                    borderRadius: 8,
                    padding: "12px",
                    fontWeight: 600,
                    cursor: "pointer",
                  }}
                  onClick={() => {
                    setShowResultModal(false);
                    resetImport();
                  }}
                >
                  Done
                </button>
                {importResults.success > 0 && (
                  <button
                    className="btn"
                    style={{
                      flex: 1,
                      background: "#f4f4f8",
                      border: "1px solid #e0e0eb",
                      color: "#374151",
                      borderRadius: 8,
                      padding: "12px",
                      fontWeight: 500,
                      cursor: "pointer",
                    }}
                    onClick={() => {
                      setShowResultModal(false);
                      navigate("/EmployeeMaster");
                    }}
                  >
                    View Employee List →
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────
// Style helpers
// ─────────────────────────────────────────────
const thStyle = {
  padding: "10px 12px",
  textAlign: "left",
  fontWeight: 700,
  fontSize: 11,
  color: "#374151",
  whiteSpace: "nowrap",
  borderBottom: "2px solid #e8e8f0",
  background: "#f4f4f8",
};

const tdStyle = {
  padding: "8px 12px",
  fontSize: 12,
  color: "#374151",
  whiteSpace: "nowrap",
  maxWidth: 200,
  overflow: "hidden",
  textOverflow: "ellipsis",
};

const stickyTh = (left, zIndex = 3, extra = {}) => ({
  ...thStyle,
  position: "sticky",
  left,
  zIndex,
  background: "#f4f4f8",
  ...extra,
});

const stickyTd = (left, zIndex = 2, bg = "#fff", extra = {}) => ({
  ...tdStyle,
  position: "sticky",
  left,
  zIndex,
  background: bg,
  ...extra,
});

const smallBtnStyle = (color, bg) => ({
  background: "rgba(255,255,255,0.15)",
  border: "1px solid rgba(255,255,255,0.35)",
  color: "#fff",
  borderRadius: 6,
  padding: "5px 12px",
  fontSize: 12,
  cursor: "pointer",
  fontWeight: 500,
  whiteSpace: "nowrap",
});

export default EmployeeImportExport;
